import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import ScrollToTop from './components/ScrollToTop';
import { AmeratPage, QurumPage, KhuwairPage } from './components/LocationPages';

// Import shadcn components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Alert, AlertDescription } from './components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';
import { Sparkles } from "lucide-react";
import { Zap } from "lucide-react";
import { canSpinToday, recordSpin, addUserCoupon, canPlayGame, recordGameAttempt, recordCorrectGameAttempt, getGameAttemptsRemaining, getTimeUntilGameReset } from './utils/couponManager';
import { getDeviceInfo } from './utils/deviceFingerprint';
import { isOrderingAllowed, getCurrentOmanTime, getOrderingHoursMessage, isTuesday, isComboDay, isHappyHour, getHappyHourTimeLeftLabel, isWomensDayOffer, isRegularPizza } from './utils/orderingTime';
import spinCoupons from './data/spinCoupons';
import gameCoupons from './data/gameCoupons';

// Import Google Maps Distance Calculator
import GoogleMapsDistance from './components/GoogleMapsDistance';
import SimpleDistanceCalculator from './components/SimpleDistanceCalculator';
import SecureDistanceCalculator from './components/SecureDistanceCalculator';
import CheckoutFlow from './components/CheckoutFlow';
import CartModalWithCheckout from './components/CartModalWithCheckout';
import UserCoupons from './components/UserCoupons';
import KhanjarBadge from './components/KhanjarBadge';
import FoodSafetyCertificate from './components/FoodSafetyCertificate';
import LanguageToggle from './components/LanguageToggle';

// Icons
import {
    ShoppingCart,
    User,
    Phone,
    MapPin,
    CreditCard,
    Truck,
    Star,
    CheckCircle,
    XCircle,
    Clock,
    Users,
    Pizza,
    Salad,
    Trophy,
    BarChart3,
    Edit,
    Package,
    Trash2,
    Plus,
    Check,
    DollarSign,
    Minus,
    Eye,
    Gift,
    Download,
    Copy,
    Settings,
    Utensils,
    Coffee,
    Mail,
    X,
    Bell,
    Search,
    Menu as MenuIcon,
    FileText,
    Calendar
} from 'lucide-react';

// Add this import at the top of the file
import jsPDF from 'jspdf';

// Import API configuration
import API_BASE_URL from './config/api';

// WOW Pizza shop location and contact info
const WOW_PIZZA_ADDRESS = "Al Amarat - Phase 5, between Omani Shiwa Restaurant & Red Roaster";

// Debug logging
console.log('Environment variables:', {
    REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL,
    API_BASE_URL: API_BASE_URL,
    NODE_ENV: process.env.NODE_ENV
});

// Use the same API URL for all requests
console.log('Using API URL:', API_BASE_URL);


// Navigation Header Component
// Navigation Header Component
const Header = ({ cartItems, setShowCart, setSelectedCategory }) => {
    const cartItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const lang = i18n.language;

    const handleMenuClick = () => {
        // Navigate to home page first, then scroll to menu filter
        navigate('/');
        // Use setTimeout to ensure navigation completes before scrolling
        setTimeout(() => {
            const filterSection = document.getElementById('filter-section');
            if (filterSection) {
                filterSection.scrollIntoView({ behavior: 'smooth' });
            } else {
                const menuSection = document.getElementById('menu-section');
                if (menuSection) menuSection.scrollIntoView({ behavior: 'smooth' });
            }
        }, 200);
    };

    return (
        <header className="fixed top-0 left-0 right-0 z-50 bg-black text-white shadow-2xl border-b-4 border-red-500 backdrop-blur-sm bg-black/95">
            <div className="container mx-auto px-3 py-1 sm:px-2 sm:py-2">
                <div className="flex items-center justify-between">
                    <Link to="/" className="flex items-center space-x-2 sm:space-x-4 group">
                        <div>
                            <img
                                src={lang === 'ar' ? '/assets/logo/mylogo_arabic.png' : '/assets/logo/mylogo.png'}
                                alt="WOW! PIZZA Logo"
                                className="h-12 sm:h-16 md:h-20 w-auto object-contain drop-shadow-lg group-hover:scale-105 transition-all duration-300"
                            />
                        </div>
                        <div className="relative">
                            <img src="/assets/logo/pizza.png" alt="WOW! PIZZA Logo" className="h-12 sm:h-16 md:h-20 w-auto object-contain drop-shadow-lg group-hover:scale-105 transition-all duration-300" />
                        </div>

                    </Link>

                    <nav className="hidden md:flex items-center gap-12">
                        <Link to="/" className="text-xl font-bold hover:text-red-400 transition-colors hover:scale-105">{t('common.home')}</Link>
                        <button
                            onClick={handleMenuClick}
                            className="text-xl font-bold hover:text-red-400 transition-colors cursor-pointer hover:scale-105"
                        >
                            {t('common.menu')}
                        </button>
                        <Link to="/game" className="hover:text-yellow-400 transition-all duration-500 flex items-center group relative hover:scale-110">
                            <div className="relative">
                                <Trophy className="h-8 w-8 mr-3 group-hover:scale-125 transition-transform duration-500 text-yellow-400 group-hover:text-yellow-300" />
                                <div className="absolute inset-0 h-8 w-8 bg-yellow-400 rounded-full blur-md opacity-30 animate-pulse"></div>
                                <div className="absolute inset-0 h-8 w-8 bg-yellow-300 rounded-full blur-sm opacity-20 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                            </div>
                            <span className="text-xl font-bold group-hover:text-yellow-300 transition-colors duration-500 group-hover:font-bold">{t('common.game')}</span>

                            {/* Always glowing border effect */}
                            <div className="absolute inset-0 rounded-lg border border-yellow-400/30 blur-sm animate-pulse"></div>

                            {/* Always visible sparkle effects */}
                            <span className="absolute -top-1 -right-1 text-yellow-400 text-sm animate-ping">✨</span>
                            <span className="absolute -bottom-1 -left-1 text-yellow-400 text-sm animate-ping" style={{ animationDelay: '0.3s' }}>⭐</span>
                        </Link>
                        <Link to="/about" className="text-xl font-bold hover:text-red-400 transition-colors hover:scale-105">{t('common.aboutUs')}</Link>
                    </nav>

                    <div className="flex items-center gap-2 sm:gap-4">
                        <LanguageToggle />
                        <div className="relative">
                            <div
                                className="text-white hover:text-red-400 hover:bg-gray-800 transition-all duration-300 p-2 rounded-lg cursor-pointer"
                                onClick={() => setShowCart(true)}
                            >
                                <ShoppingCart className={`h-7 w-7 sm:h-8 sm:w-8 transition-transform duration-300 ${cartItemCount > 0 ? 'scale-110' : ''}`} />
                                {cartItemCount > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold animate-bounce">
                                        {cartItemCount}
                                    </span>
                                )}
                            </div>
                        </div>
                        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-1">
                            {mobileMenuOpen ? (
                                <X className="h-8 w-8 text-white hover:text-red-400 transition-colors" />
                            ) : (
                                <MenuIcon className="h-8 w-8 text-white hover:text-red-400 transition-colors" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <div className="md:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-md border-b-2 border-red-500 shadow-xl p-4 flex flex-col space-y-4 animate-[fadeInScale_0.2s_ease-out]">
                    <div className="pb-2 border-b border-gray-800">
                        <LanguageToggle />
                    </div>
                    <Link
                        to="/"
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-lg font-bold text-white hover:text-red-400 py-2 border-b border-gray-800"
                    >
                        {t('common.home')}
                    </Link>
                    <button
                        onClick={() => {
                            handleMenuClick();
                            setMobileMenuOpen(false);
                        }}
                        className="text-lg font-bold text-white hover:text-red-400 py-2 border-b border-gray-800 text-left"
                    >
                        {t('common.menu')}
                    </button>
                    <Link
                        to="/game"
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-lg font-bold text-white hover:text-yellow-400 py-2 border-b border-gray-800 flex items-center"
                    >
                        <span className="mr-2">🏆</span> {t('common.game')}
                    </Link>
                    <Link
                        to="/about"
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-lg font-bold text-white hover:text-red-400 py-2 text-left"
                    >
                        {t('common.aboutUs')}
                    </Link>
                </div>
            )}
        </header>
    );
};

// Wow Element Component for Homepage
const WowElement = () => {
    const [isSpinning, setIsSpinning] = useState(false);
    const [surprise, setSurprise] = useState(null);
    const [showSurprise, setShowSurprise] = useState(false);
    const [canSpin, setCanSpin] = useState(true);
    const { t, i18n } = useTranslation();
    const lang = i18n.language;

    // Use fixed spin coupons from data file
    const [surprises, setSurprises] = useState(spinCoupons);

    // Spin coupons are now fixed arrays - no need to fetch from server

    // Check if user can spin today
    const checkSpinStatus = async () => {
        try {
            const canSpinTodayResult = await canSpinToday();
            setCanSpin(canSpinTodayResult);
        } catch (error) {
            console.error('Error checking spin status:', error);
            // If there's an error, assume user can't spin
            setCanSpin(false);
        }
    };

    useEffect(() => {
        checkSpinStatus();
    }, []);



    // Confetti function
    const triggerConfetti = () => {
        const colors = ['#ff0000', '#ff6b6b', '#ff8e8e', '#ffb3b3', '#ffd6d6'];
        const confettiCount = 100;

        for (let i = 0; i < confettiCount; i++) {
            const confetti = document.createElement('div');
            confetti.style.position = 'fixed';
            confetti.style.left = Math.random() * window.innerWidth + 'px';
            confetti.style.top = '-10px';
            confetti.style.width = '10px';
            confetti.style.height = '10px';
            confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
            confetti.style.borderRadius = '50%';
            confetti.style.pointerEvents = 'none';
            confetti.style.zIndex = '9999';
            confetti.style.animation = `fall ${Math.random() * 3 + 2}s linear forwards`;

            document.body.appendChild(confetti);

            setTimeout(() => {
                if (confetti.parentNode) {
                    confetti.parentNode.removeChild(confetti);
                }
            }, 5000);
        }
    };

    const spinWheel = async () => {
        if (isSpinning) return;

        try {
            // Check if user can spin today
            const canSpin = await canSpinToday();
            if (!canSpin) {
                toast.error(t('toast.spinOncePerDay'));
                return;
            }

            setIsSpinning(true);
            setShowSurprise(false);

            setTimeout(async () => {
                const randomSurprise = surprises[Math.floor(Math.random() * surprises.length)];
                setSurprise(randomSurprise);
                setIsSpinning(false);
                setShowSurprise(true);

                try {
                    // Record the spin
                    await recordSpin();

                    // Add coupon to user's collection if it has a code
                    if (randomSurprise.code) {
                        const couponData = {
                            code: randomSurprise.code,
                            text: randomSurprise.text,
                            type: randomSurprise.type || 'percentage',
                            discount: randomSurprise.discount || 10,
                            minAmount: randomSurprise.minAmount || 5
                        };

                        // Use the new spin-specific endpoint
                        const response = await axios.post(`${API_BASE_URL}/api/user/spin/add-coupon`, {
                            ...couponData,
                            device_info: getDeviceInfo()
                        });

                        if (response.data.message) {
                            triggerConfetti();
                            toast.success(t('toast.wonCoupon', { text: lang === 'ar' ? (randomSurprise.text_ar || randomSurprise.text) : randomSurprise.text }));

                            // Refresh the coupons display
                            if (window.refreshUserCoupons) {
                                window.refreshUserCoupons();
                            }
                        }
                    } else if (randomSurprise.action === 'game') {
                        triggerConfetti();
                        toast.success(t('toast.wonGameAttempt'));
                    }
                } catch (error) {
                    console.error('Error processing spin result:', error);
                    toast.error(t('toast.spinRewardFailed'));
                }
            }, 2000);
        } catch (error) {
            console.error('Error checking spin status:', error);
            toast.error(t('toast.spinStatusFailed'));
        }
    };

    return (
        <div className="relative p-4 md:p-8 mb-8">
            <div className="relative text-center max-w-lg mx-auto">
                {/* Red card background - rounded with bright pulsing glow */}
                <div className="absolute inset-0 bg-red-600 rounded-lg shadow-lg transform scale-100 -top-1 -bottom-1 -left-1 -right-1 md:-top-2 md:-bottom-2 md:-left-2 md:-right-2 ring-2 md:ring-4 ring-red-300/60 ring-offset-8 md:ring-offset-12 ring-offset-black animate-pulse"></div>

                <div className="relative z-10">
                    <div className="flex items-center justify-center mb-4 md:mb-6">
                        <Sparkles className="h-6 w-6 md:h-10 md:w-10 text-red-300 mr-2 md:mr-3" />
                        <h3 className="text-xl md:text-3xl font-bold text-white">{t('spin.title')}</h3>
                        <Sparkles className="h-6 w-6 md:h-10 md:w-10 text-red-300 ml-2 md:ml-3" />
                    </div>

                    <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:space-x-8">
                        <div className="relative">
                            {/* Pizza wheel - responsive sizing */}
                            <div
                                className={`relative w-32 h-32 md:w-48 md:h-48 transform transition-all duration-2000 ${isSpinning ? 'rotate-[1440deg] scale-110' : canSpin ? 'cursor-pointer hover:rotate-12 hover:scale-105' : 'opacity-50'
                                    }`}
                                onClick={canSpin ? spinWheel : undefined}
                            >
                                <img
                                    src="/assets/logo/pepperonipizza.png"
                                    alt="Pizza Wheel"
                                    className={`w-full h-full object-contain ${isSpinning ? 'animate-spin' : ''}`}
                                />

                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="text-center px-1">
                                        <p className="text-[10px] md:text-lg font-bold text-white drop-shadow-lg bg-black/50 px-2 md:px-3 py-0.5 md:py-1 rounded-full whitespace-nowrap">
                                            {isSpinning ? t('spin.spinning') : canSpin ? t('spin.spinMe') : t('spin.comeBack')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {showSurprise && surprise && (
                            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-4 md:p-6 shadow-2xl animate-fade-in w-full md:max-w-xs border border-red-300/30">
                                <div className="text-center">
                                    <div className="mb-2 md:mb-4">
                                        <div className="w-12 h-12 md:w-16 md:h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3">
                                            <Gift className="h-6 w-6 md:h-8 md:w-8 text-white" />
                                        </div>
                                    </div>
                                    <p className="text-lg md:text-xl font-bold text-white mb-2 md:mb-3 drop-shadow-lg">{lang === 'ar' ? (surprise.text_ar || surprise.text) : surprise.text}</p>
                                    {surprise.code && (
                                        <div className="bg-white/20 backdrop-blur-sm rounded-xl p-2 md:p-3 border border-white/30">
                                            <p className="text-xs md:text-sm font-mono text-white font-bold">{surprise.code}</p>
                                        </div>
                                    )}
                                    {surprise.action === 'game' && (
                                        <Button
                                            onClick={() => window.location.href = '/game'}
                                            className="mt-3 bg-white text-red-600 hover:bg-red-50 font-bold w-full md:w-auto"
                                            size="sm"
                                        >
                                            {t('spin.playGameNow')}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="text-red-100 mt-4 md:mt-6 text-sm md:text-lg font-semibold drop-shadow-lg relative z-10 px-4">
                    {canSpin ? (
                        t('spin.tagline')
                    ) : (
                        <div className="text-center">
                            <div className="text-yellow-300 font-bold mb-1">
                                {t('spin.alreadySpun')}
                            </div>
                            <div className="text-gray-300 text-xs md:text-sm">
                                {t('spin.comeBackTomorrow')}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Distance Calculator Component (using Google Maps)
const DistanceCalculator = ({ onDistanceChange }) => {
    // Use full Google Maps integration
    return <GoogleMapsDistance onDistanceChange={onDistanceChange} />;
};
// Menu Component
// Combo Customization Modal
// Combo Customization Modal
const ComboCustomizationModal = ({ isOpen, onClose, item, onAdd, menuItems, selectedSize }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const [selections, setSelections] = useState({});
    const [currentStep, setCurrentStep] = useState(0);

    // Reset state when modal opens/closes or item changes
    useEffect(() => {
        setSelections({});
        setCurrentStep(0);
    }, [isOpen, item]);

    if (!isOpen || !item) return null;

    // Parse combo config from description
    let comboConfig = null;
    try {
        const parts = item.description.split('|||COMBO_CONFIG|||');
        if (parts.length > 1) {
            comboConfig = JSON.parse(parts[1]);
        }
    } catch (e) {
        console.error('Failed to parse combo config', e);
    }

    if (!comboConfig || !comboConfig.groups || comboConfig.groups.length === 0) return null;

    const hasFixedItems = comboConfig.fixed_items && Array.isArray(comboConfig.fixed_items) && comboConfig.fixed_items.length > 0;

    // Total steps is groups length + 1 if there are fixed items to review
    const totalSteps = comboConfig.groups.length + (hasFixedItems ? 1 : 0);
    const isFixedItemsStep = hasFixedItems && currentStep === comboConfig.groups.length;
    const currentGroupConfig = isFixedItemsStep ? null : comboConfig.groups[currentStep];

    const handleQuantityChange = (groupIndex, itemId, delta) => {
        if (isFixedItemsStep) return;

        setSelections(prev => {
            const groupConfig = comboConfig.groups[groupIndex];
            const maxQuantity = groupConfig.quantity;
            const currentGroup = prev[groupIndex] || { group_id: groupConfig.id, quantities: {} };
            const currentQuantities = { ...(currentGroup.quantities || {}) };
            const currentCount = currentQuantities[itemId] || 0;
            const totalSelected = Object.values(currentQuantities).reduce((a, b) => a + b, 0);

            const newCount = currentCount + delta;
            if (newCount < 0) return prev;
            if (delta > 0 && totalSelected >= maxQuantity) return prev;

            const newQuantities = { ...currentQuantities, [itemId]: newCount };
            if (newCount === 0) delete newQuantities[itemId];

            return {
                ...prev,
                [groupIndex]: { group_id: groupConfig.id, quantities: newQuantities }
            };
        });
    };

    const getTotalSelected = (groupIndex) => {
        const group = selections[groupIndex];
        if (!group || !group.quantities) return 0;
        return Object.values(group.quantities).reduce((a, b) => a + b, 0);
    };

    const isCurrentStepComplete = () => {
        if (isFixedItemsStep) return true;
        return getTotalSelected(currentStep) === currentGroupConfig?.quantity;
    };

    const handleNext = () => {
        if (isCurrentStepComplete()) {
            if (currentStep < totalSteps - 1) {
                setCurrentStep(prev => prev + 1);
            } else {
                handleConfirm();
            }
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const handleConfirm = () => {
        const formattedSelections = Object.keys(selections).map(groupIndex => {
            const groupSelection = selections[groupIndex];
            const groupConfig = comboConfig.groups[groupIndex];

            const itemNames = [];
            Object.entries(groupSelection.quantities || {}).forEach(([itemId, qty]) => {
                const mItem = menuItems.find(m => m.id === itemId);
                const name = mItem ? mItem.name : itemId;
                for (let i = 0; i < qty; i++) itemNames.push(name);
            });

            return {
                group_id: groupConfig.id || groupIndex,
                items: itemNames
            };
        });

        // Merge fixed items into the final payload if they exist
        if (hasFixedItems) {
            formattedSelections.push({
                group_id: "fixed_items",
                items: comboConfig.fixed_items
            });
        }

        // Use the passed selectedSize or fallback to medium
        const finalSize = selectedSize || item.sizes?.[0]?.size || 'medium';
        onAdd(item, finalSize, formattedSelections);
        onClose();
    };

    // Filter items for current group or get fixed items objects
    let groupAvailableItems = [];
    if (isFixedItemsStep && hasFixedItems) {
        // Attempt to map strings to real menu items to get images. If not found, use a fallback object.
        groupAvailableItems = comboConfig.fixed_items.map(fixedName => {
            const foundItem = menuItems.find(m => m.name.toLowerCase() === fixedName.toLowerCase());
            return foundItem || { id: fixedName, name: fixedName, image: null, isCustom: true };
        });
    } else if (currentGroupConfig) {
        if (currentGroupConfig.type === 'category') {
            groupAvailableItems = menuItems.filter(m => m.category?.id === currentGroupConfig.category_id || m.category_id === currentGroupConfig.category_id);
        } else {
            groupAvailableItems = menuItems.filter(m => (currentGroupConfig.allowed_items || []).includes(m.id));
            if (currentGroupConfig.custom_items && currentGroupConfig.custom_items.length > 0) {
                const customObjects = currentGroupConfig.custom_items.map(str => ({
                    id: str,
                    name: str,
                    isCustom: true
                }));
                groupAvailableItems = [...groupAvailableItems, ...customObjects];
            }
        }
    }

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex flex-col md:items-center md:justify-center animate-fade-in">
            <div className="bg-white md:rounded-3xl w-full h-full md:h-[90vh] md:max-w-5xl flex flex-col shadow-2xl overflow-hidden relative transition-all duration-300">

                {/* Header - Fixed */}
                <div className="bg-white z-10 p-4 md:p-6 border-b border-gray-100 shadow-sm flex-shrink-0 relative">
                    <div className="flex items-start justify-between relative">
                        <div className="flex-1 pr-12">
                            <h2 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight leading-tight mb-1 flex flex-col sm:flex-row sm:items-center gap-2">
                                {t('combo.customize', { name: lang === 'ar' && item.name_ar ? item.name_ar : item.name })}
                            </h2>
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between mt-1">
                                {item.sizes && item.sizes.length > 0 && (
                                    <p className="text-xl font-bold text-red-600 mb-2 sm:mb-0">
                                        OMR {(item.sizes.find(s => s.size === selectedSize)?.price || item.sizes[0].price).toFixed(3)}
                                    </p>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="absolute right-0 top-0 h-10 w-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition-colors"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Progress Indicator */}
                    <div className="mt-6 flex items-center justify-between">
                        <div className="flex space-x-2 flex-1 mr-4">
                            {Array.from({ length: totalSteps }).map((_, idx) => (
                                <div
                                    key={idx}
                                    className={`h-2 flex-1 rounded-full transition-all duration-500 ${idx === currentStep ? 'bg-red-600' : idx < currentStep ? 'bg-red-300' : 'bg-gray-200'
                                        }`}
                                />
                            ))}
                        </div>
                        <div className="text-sm font-bold text-gray-500 whitespace-nowrap">
                            {t('combo.step', { current: currentStep + 1, total: totalSteps })}
                        </div>
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto bg-gray-50 p-4 md:p-6 pb-40 md:pb-32 hide-scrollbar">
                    <div className="max-w-4xl mx-auto animate-fade-up">
                        <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <h3 className="text-2xl md:text-3xl font-black text-gray-900 flex items-center gap-3">
                                    <span className="bg-red-100 text-red-600 p-2 px-4 rounded-xl shadow-inner">
                                        {isFixedItemsStep ? t('combo.includedInMeal') : (currentGroupConfig?.title ? t(`comboGroups.${currentGroupConfig.title}`, { defaultValue: currentGroupConfig.title }) : t('combo.group', { num: currentStep + 1 }))}
                                    </span>
                                </h3>
                            </div>
                            {!isFixedItemsStep && (
                                <div className="bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100 flex items-center gap-2">
                                    <span className="text-gray-500 font-medium">{t('combo.selectedLabel')}</span>
                                    <span className={`font-bold text-lg ${getTotalSelected(currentStep) === currentGroupConfig?.quantity ? 'text-green-600' : 'text-red-600'}`}>
                                        {getTotalSelected(currentStep)} / {currentGroupConfig?.quantity}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
                            {groupAvailableItems.map((mItem, index) => {
                                const itemImage = mItem.image;
                                const itemQty = isFixedItemsStep ? 1 : (selections[currentStep]?.quantities?.[mItem.id] || 0);
                                const totalSelectedInGroup = getTotalSelected(currentStep);
                                const isAtMax = totalSelectedInGroup >= (currentGroupConfig?.quantity || 0);

                                return (
                                    <div
                                        key={mItem.id || index}
                                        className={`rounded-2xl md:rounded-3xl overflow-hidden bg-white flex flex-col transition-all duration-300 ${itemQty > 0 ? 'ring-4 ring-offset-2 ring-offset-white ring-red-500 shadow-xl' : 'shadow-md border border-gray-100'}`}
                                    >
                                        {/* Image Container */}
                                        <div className="relative aspect-square md:aspect-[4/3] bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-2 overflow-hidden">
                                            {itemImage ? (
                                                <img
                                                    src={itemImage}
                                                    alt={mItem.name}
                                                    className="w-full h-full object-contain scale-[1.5] drop-shadow-lg"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            ) : (
                                                <span className="text-6xl text-gray-300 drop-shadow-md">🍕</span>
                                            )}
                                            {itemQty > 0 && (
                                                <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white text-sm font-black flex items-center justify-center shadow-lg">
                                                    {itemQty}
                                                </div>
                                            )}
                                        </div>

                                        {/* Content Section */}
                                        <div className="p-2 md:p-3 text-center border-t border-gray-50 flex-1">
                                            <h4 className={`text-sm md:text-base font-extrabold leading-tight line-clamp-2 transition-colors duration-300 ${itemQty > 0 ? 'text-red-600' : 'text-gray-800'}`}>
                                                {lang === 'ar' && mItem.name_ar ? mItem.name_ar : mItem.name}
                                            </h4>
                                        </div>

                                        {/* +/- Controls */}
                                        {!isFixedItemsStep && (
                                            <div className="flex items-center justify-center gap-3 p-3 pt-1">
                                                <button
                                                    onClick={() => handleQuantityChange(currentStep, mItem.id, -1)}
                                                    disabled={itemQty === 0}
                                                    className="w-8 h-8 rounded-full bg-red-100 text-red-600 font-black text-lg flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-red-200 transition-colors"
                                                >
                                                    −
                                                </button>
                                                <span className="text-lg font-black text-gray-900 w-6 text-center">{itemQty}</span>
                                                <button
                                                    onClick={() => handleQuantityChange(currentStep, mItem.id, 1)}
                                                    disabled={isAtMax}
                                                    className="w-8 h-8 rounded-full bg-red-500 text-white font-black text-lg flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-red-600 transition-colors"
                                                >
                                                    +
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Sticky Action Bar */}
                <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200 p-4 md:p-6 shadow-[0_-10px_40px_rgba(0,0,0,0.1)] z-20 transition-transform duration-300 rounded-b-none md:rounded-b-3xl">
                    <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
                        {currentStep > 0 ? (
                            <Button
                                variant="outline"
                                onClick={handleBack}
                                className="rounded-2xl h-14 md:h-16 px-6 md:px-8 border-2 border-gray-200 text-gray-700 font-bold hover:bg-gray-50 flex-1 md:flex-none shadow-sm"
                            >
                                Back
                            </Button>
                        ) : (
                            <div className="flex-1 md:flex-none hidden md:block w-32" /> // Spacer
                        )}

                        <Button
                            onClick={handleNext}
                            disabled={!isCurrentStepComplete()}
                            className={`flex-1 h-14 md:h-16 rounded-2xl text-lg md:text-xl font-black transition-all duration-300 shadow-xl ${isCurrentStepComplete()
                                ? currentStep < totalSteps - 1
                                    ? 'bg-gray-900 hover:bg-black text-white hover:scale-[1.02]'
                                    : 'bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white hover:scale-[1.02] shadow-green-500/30'
                                : 'bg-gray-100 text-gray-400 border-2 border-gray-200 shadow-none'
                                }`}
                        >
                            {isCurrentStepComplete()
                                ? (currentStep < totalSteps - 1 ? `${t('combo.nextStep')} ➔` : `✨ ${t('combo.addComboToCart')}`)
                                : (!isFixedItemsStep && currentGroupConfig ? t(currentGroupConfig.quantity === 1 ? 'combo.choose' : 'combo.choose_plural', { count: currentGroupConfig.quantity }) : '')}
                        </Button>
                    </div>
                    {/* Safe area padding for mobile */}
                    <div className="h-safe-area-bottom w-full bg-transparent" />
                </div>
            </div>
        </div>
    );
};

// Live "ends in Xh Ym" chip for the Happy Hour window, ticks every 30s
const HappyHourCountdown = ({ className = '' }) => {
    const { t } = useTranslation();
    const [label, setLabel] = useState(getHappyHourTimeLeftLabel());

    useEffect(() => {
        const timer = setInterval(() => setLabel(getHappyHourTimeLeftLabel()), 30000);
        return () => clearInterval(timer);
    }, []);

    if (!label) return null;

    return (
        <span className={`inline-flex items-center gap-1.5 ${className}`}>
            <span className="hh-blink h-1.5 w-1.5 rounded-full bg-red-500" />
            {t('happyHour.endsIn', { time: label })}
        </span>
    );
};

const Menu = ({ onAddToCart, updateQuantity, recentlyAddedItems = new Set(), cartItems = [], selectedCategory, onSelectCategory }) => {
    const [menuItems, setMenuItems] = useState([]);
    const [categories, setCategories] = useState([]);
    // selectedCategory lifted to App
    const [loading, setLoading] = useState(true);
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const pickLocalized = (obj, field) => (lang === 'ar' && obj?.[`${field}_ar`]) || obj?.[field] || '';
    const [selectedSizes, setSelectedSizes] = useState(() => {
        const sizes = {};
        cartItems.forEach(item => {
            if (item.selectedSize) sizes[item.id] = item.selectedSize;
        });
        return sizes;
    });
    const [comboModalOpen, setComboModalOpen] = useState(false);
    const [selectedComboItem, setSelectedComboItem] = useState(null);
    const [selectedComboSize, setSelectedComboSize] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showHappyHourPoster, setShowHappyHourPoster] = useState(false);
    const [happyHourLive, setHappyHourLive] = useState(isHappyHour());
    useEffect(() => {
        const timer = setInterval(() => setHappyHourLive(isHappyHour()), 30000);
        return () => clearInterval(timer);
    }, []);
    const happyHourPoster = lang === 'ar' ? '/assets/logo/happy_hour_arabic.jpeg' : '/assets/logo/happy_hour_eng.jpeg';
    const [showWomensDayPoster, setShowWomensDayPoster] = useState(false);
    // Omani Women's Day: jump to the pizza category (banner button, poster lightbox, and the app-level popup)
    const goToPizzaMenu = () => {
        const pizzaCategory = categories
            .map(c => (typeof c === 'object' ? c.name : c) || '')
            .find(name => name.trim().toLowerCase() === 'pizza');
        onSelectCategory(pizzaCategory || 'all');
        setTimeout(() => {
            const filterSection = document.getElementById('filter-section');
            if (filterSection) filterSection.scrollIntoView({ behavior: 'smooth' });
        }, 150);
    };
    useEffect(() => {
        const handler = () => goToPizzaMenu();
        window.addEventListener('womensday:order', handler);
        // Popup was tapped on another page: finish the jump once categories have loaded here
        let pending = false;
        try { pending = sessionStorage.getItem('womensDayGoToPizza') === '1'; } catch (e) {}
        if (pending && categories.length > 0) {
            try { sessionStorage.removeItem('womensDayGoToPizza'); } catch (e) {}
            setTimeout(goToPizzaMenu, 300);
        }
        return () => window.removeEventListener('womensday:order', handler);
    }, [categories]);
    const womensDayLive = isWomensDayOffer();

    // Fuzzy match — tolerates typos
    const fuzzyMatch = (text, query) => {
        if (!query) return true;
        const t = text.toLowerCase();
        const q = query.toLowerCase().trim();
        if (!q) return true;

        // Exact substring
        if (t.includes(q)) return true;

        // Characters appear in order (handles missing letters: "mrghrta" → "margherita")
        let qi = 0;
        for (let i = 0; i < t.length && qi < q.length; i++) {
            if (t[i] === q[qi]) qi++;
        }
        if (qi >= q.length) return true;

        // Allow up to 2 wrong characters (handles typos: "margarita" → "margherita")
        const words = t.split(/\s+/);
        for (const word of words) {
            const len = Math.max(word.length, q.length);
            let mismatches = Math.abs(word.length - q.length);
            for (let i = 0; i < Math.min(word.length, q.length); i++) {
                if (word[i] !== q[i]) mismatches++;
            }
            if (mismatches <= (q.length <= 3 ? 1 : 2)) return true;
        }

        return false;
    };

    // Helper to check if item is combo
    const isComboItem = (item) => {
        return item.description && item.description.includes('|||COMBO_CONFIG|||');
    };

    // Omani Women's Day: medium/large of a regular pizza comes with a free heart-shaped pizza
    const earnsHeartPizza = (item, size) => womensDayLive && isRegularPizza(item) && ['medium', 'large'].includes(size);

    // Helper to check if item is a Happy Hour lunch treat (11 AM - 4 PM daily)
    const isHappyHourItem = (item) => {
        const name = (item?.name || '').toLowerCase();
        const nameAr = item?.name_ar || '';
        return name.includes('happy hour') || nameAr.includes('ساعة السعادة');
    };

    const getDisplayDescription = (description) => {
        if (!description) return '';
        return description.split('|||COMBO_CONFIG|||')[0];
    };

    const handleItemClick = (item, size) => {
        if (isComboItem(item)) {
            setSelectedComboItem(item);
            // Default to medium if not yet explicitly selected on the card
            setSelectedComboSize(size || item.sizes?.[0]?.size || 'medium');
            setComboModalOpen(true);
        } else {
            onAddToCart(item, size);
        }
    };

    console.log('Menu component rendered with:', { menuItems, categories, selectedCategory, loading });

    useEffect(() => {
        console.log('Menu component useEffect triggered');
        fetchMenu();
        fetchCategories();
    }, []);

    const fetchMenu = async () => {
        try {
            console.log('Main Menu - Fetching from:', `${API_BASE_URL}/api/menu`);
            const response = await axios.get(`${API_BASE_URL}/api/menu`);
            console.log('Main Menu - Response:', response.data);

            // Handle both old and new data structures
            const menuItems = response.data.menu_items.map(item => {
                // If item has the old structure (price and category as string), convert it
                if (item.price && typeof item.category === 'string') {
                    return {
                        ...item,
                        category: {
                            id: 'legacy',
                            name: item.category,
                            icon: '🍽️'
                        },
                        sizes: [
                            {
                                id: 'legacy-size',
                                size: 'medium',
                                price: item.price,
                                available: item.available
                            }
                        ]
                    };
                }
                return item;
            });

            setMenuItems(menuItems);
            setLoading(false);
        } catch (error) {
            console.error('Main Menu - Error fetching menu:', error);
            console.error('Main Menu - Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                url: error.config?.url
            });
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/menu/categories`);
            // API now returns [{name, name_ar}] objects
            const apiCategories = response.data.categories.filter(c => {
                const name = typeof c === 'object' ? c.name : c;
                return name.toLowerCase() !== 'offers';
            });
            setCategories(['all', 'offers', ...apiCategories]);
        } catch (error) {
            console.error('Main Menu - Error fetching categories:', error);
            setCategories(['all', 'offers', 'pizza', 'sides', 'drinks', 'desserts']);
        }
    };

    const categoryFiltered = selectedCategory === 'all'
        ? menuItems
        : selectedCategory === 'offers'
            ? menuItems.filter(item => isComboItem(item) || isHappyHourItem(item))
            : menuItems.filter(item => {
                const itemCategory = item.category?.name || item.category || '';
                return itemCategory === selectedCategory;
            });

    const offerSortOrder = (item) => {
        if (isHappyHourItem(item)) return 0;
        if (item.name.toLowerCase().includes('limited time offer')) return 1;
        if (item.name.toLowerCase().includes('tuesday offer')) return 2;
        if (isComboItem(item)) return 3;
        return 4;
    };

    const filteredItems = (searchQuery.trim()
        ? categoryFiltered.filter(item =>
            fuzzyMatch(item.name, searchQuery) ||
            fuzzyMatch(getDisplayDescription(item.description) || '', searchQuery)
        )
        : categoryFiltered
    ).slice().sort((a, b) => offerSortOrder(a) - offerSortOrder(b));

    const getCategoryIcon = (category) => {
        // If category is an object and has an icon (emoji), use it
        if (category && typeof category === 'object' && category.icon) {
            return <span className="text-lg">{category.icon}</span>;
        }

        const categoryName = (typeof category === 'string' ? category : category.name || '').toLowerCase();

        switch (categoryName) {
            case 'offers': return <Sparkles className="h-4 w-4" />;
            case 'pizza': return <Pizza className="h-4 w-4" />;
            case 'sides': return <Utensils className="h-4 w-4" />;
            case 'drinks': return <Coffee className="h-4 w-4" />;
            case 'salads': return <Salad className="h-4 w-4" />;
            case 'fresh juice': return <Coffee className="h-4 w-4" />;

            default: return <Utensils className="h-4 w-4" />;
        }
    };

    if (loading) {
        console.log('Menu component showing loading state');
        return (
            <div className="flex flex-col justify-center items-center min-h-96 gap-6">
                <img
                    src="/assets/logo/pepperonipizza.png"
                    alt="Loading..."
                    className="w-36 h-36 animate-spin drop-shadow-2xl"
                    style={{ animationDuration: '2.4s' }}
                />
                <p className="text-red-500 text-sm font-semibold tracking-widest uppercase animate-pulse">Loading Menu...</p>
            </div>
        );
    }

    console.log('Menu component rendering with items:', filteredItems.length, 'items');

    return (
        <div id="menu-section" className="min-h-screen bg-black">
            {/* Hero Section - Split Layout */}
            <div className="container mx-auto px-4 py-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
                    {/* Left Side - Food Photography */}
                    <div className="relative">
                        <div className="relative z-10">
                            {/* Pizza Slices */}
                            <div className="absolute top-0 left-0 transform rotate-12">
                                <div className="w-64 h-64 bg-gradient-to-br from-red-500 to-orange-500 rounded-full opacity-20 blur-xl"></div>
                            </div>
                            <div className="absolute top-8 left-8 transform -rotate-6">
                                <div className="w-48 h-48 bg-gradient-to-br from-red-600 to-red-800 rounded-full opacity-20 blur-xl"></div>
                            </div>

                            {/* Main Logo Visual */}
                            <div className="relative z-20 mx-auto flex items-center justify-center">
                                {/* Firework burst effects around logo */}
                                <div className="absolute inset-0 w-96 h-96">
                                    {/* Outer firework ring */}
                                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-red-500 rounded-full animate-ping"></div>
                                    <div className="absolute top-8 right-8 w-2 h-2 bg-yellow-400 rounded-full animate-ping" style={{ animationDelay: '0.2s' }}></div>
                                    <div className="absolute top-1/2 right-0 transform -translate-y-1/2 w-2 h-2 bg-red-400 rounded-full animate-ping" style={{ animationDelay: '0.4s' }}></div>
                                    <div className="absolute bottom-8 right-8 w-2 h-2 bg-yellow-500 rounded-full animate-ping" style={{ animationDelay: '0.6s' }}></div>
                                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-red-500 rounded-full animate-ping" style={{ animationDelay: '0.8s' }}></div>
                                    <div className="absolute bottom-8 left-8 w-2 h-2 bg-yellow-400 rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
                                    <div className="absolute top-1/2 left-0 transform -translate-y-1/2 w-2 h-2 bg-red-400 rounded-full animate-ping" style={{ animationDelay: '1.2s' }}></div>
                                    <div className="absolute top-8 left-8 w-2 h-2 bg-yellow-500 rounded-full animate-ping" style={{ animationDelay: '1.4s' }}></div>
                                </div>

                                {/* Red glow effect behind logo */}
                                <div className="absolute inset-0 w-48 h-48 lg:w-96 lg:h-96 bg-red-500 rounded-full blur-2xl lg:blur-3xl opacity-25 lg:opacity-30 animate-pulse"></div>
                                <div className="absolute inset-0 w-40 h-40 lg:w-80 lg:h-80 bg-red-600 rounded-full blur-xl lg:blur-2xl opacity-30 lg:opacity-40 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                                <div className="absolute inset-0 w-32 h-32 lg:w-64 lg:h-64 bg-red-700 rounded-full blur-lg lg:blur-xl opacity-35 lg:opacity-50 animate-pulse" style={{ animationDelay: '1s' }}></div>

                                {/* Main logo */}
                                <div className="relative w-48 h-48 sm:w-64 sm:h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 flex items-center justify-center">
                                    <img src="/assets/logo/pizza.png" alt="WOW! PIZZA Logo" className="w-full h-full object-contain drop-shadow-lg transition-all duration-500 ease-out hover:scale-125 hover:rotate-12 hover:shadow-2xl hover:brightness-110 group-hover:scale-150 group-hover:rotate-12 group-hover:shadow-2xl cursor-pointer" />
                                </div>

                                {/* Inner firework bursts */}
                                <div className="absolute inset-0 w-64 h-64">
                                    <div className="absolute top-4 left-4 w-1 h-1 bg-yellow-400 rounded-full animate-ping" style={{ animationDelay: '0.3s' }}></div>
                                    <div className="absolute top-4 right-4 w-1 h-1 bg-red-400 rounded-full animate-ping" style={{ animationDelay: '0.7s' }}></div>
                                    <div className="absolute bottom-4 left-4 w-1 h-1 bg-yellow-500 rounded-full animate-ping" style={{ animationDelay: '1.1s' }}></div>
                                    <div className="absolute bottom-4 right-4 w-1 h-1 bg-red-500 rounded-full animate-ping" style={{ animationDelay: '1.5s' }}></div>
                                </div>
                            </div>

                            {/* Firework Effects */}
                            <div className="absolute top-4 right-4 w-4 h-4 bg-red-500 rounded-full opacity-60 animate-ping"></div>
                            <div className="absolute top-16 right-8 w-3 h-3 bg-yellow-400 rounded-full opacity-60 animate-ping" style={{ animationDelay: '0.5s' }}></div>
                            <div className="absolute top-32 right-4 w-2 h-2 bg-red-400 rounded-full opacity-60 animate-ping" style={{ animationDelay: '1s' }}></div>
                            <div className="absolute bottom-8 right-8 w-3 h-3 bg-yellow-500 rounded-full opacity-60 animate-ping" style={{ animationDelay: '1.5s' }}></div>
                            <div className="absolute bottom-16 right-4 w-2 h-2 bg-red-400 rounded-full opacity-60 animate-ping" style={{ animationDelay: '2s' }}></div>

                            {/* Firework Bursts */}
                            <div className="absolute top-8 left-8">
                                <div className="w-2 h-2 bg-red-500 rounded-full animate-ping"></div>
                                <div className="w-1 h-1 bg-yellow-400 rounded-full animate-ping absolute top-1 left-1" style={{ animationDelay: '0.3s' }}></div>
                                <div className="w-1 h-1 bg-red-400 rounded-full animate-ping absolute top-2 left-0" style={{ animationDelay: '0.6s' }}></div>
                            </div>

                            <div className="absolute bottom-12 left-12">
                                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-ping" style={{ animationDelay: '0.8s' }}></div>
                                <div className="w-1 h-1 bg-red-400 rounded-full animate-ping absolute top-1 left-1" style={{ animationDelay: '1.1s' }}></div>
                                <div className="w-1 h-1 bg-yellow-400 rounded-full animate-ping absolute top-2 left-0" style={{ animationDelay: '1.4s' }}></div>
                            </div>

                            <div className="absolute top-24 left-24">
                                <div className="w-2 h-2 bg-red-400 rounded-full animate-ping" style={{ animationDelay: '1.2s' }}></div>
                                <div className="w-1 h-1 bg-yellow-500 rounded-full animate-ping absolute top-1 left-1" style={{ animationDelay: '1.5s' }}></div>
                                <div className="w-1 h-1 bg-red-500 rounded-full animate-ping absolute top-2 left-0" style={{ animationDelay: '1.8s' }}></div>
                            </div>
                        </div>
                    </div>

                    {/* Right Side - Text and CTA */}
                    <div className="text-white space-y-8 flex flex-col items-center lg:items-start text-center lg:text-left">
                        <div>
                            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6">
                                <span className="bg-gradient-to-r from-red-500 via-red-600 to-red-700 bg-clip-text text-transparent drop-shadow-2xl relative">
                                    {t('home.brandName')}
                                </span>
                                <br />
                                <span className="text-gray-300 text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light tracking-wider">{t('home.tagline')}</span><br />
                            </h1>
                        </div>

                        <Button
                            onClick={() => {
                                const filterSection = document.getElementById('filter-section');
                                if (filterSection) {
                                    filterSection.scrollIntoView({ behavior: 'smooth' });
                                } else {
                                    const menuSection = document.getElementById('menu-section');
                                    if (menuSection) {
                                        menuSection.scrollIntoView({ behavior: 'smooth' });
                                    }
                                }
                            }}
                            className="bg-gradient-to-r from-red-500 via-red-600 to-red-700 hover:from-red-400 hover:via-red-500 hover:to-red-600 text-white px-10 py-5 text-xl font-bold rounded-2xl transition-all duration-500 transform hover:scale-110 hover:shadow-2xl shadow-xl border border-red-400/30"
                        >
                            <span className="drop-shadow-lg">{lang === 'ar' ? '✨ استكشف قائمتنا ✨' : '✨ Explore our Menu ✨'}</span>
                        </Button>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-gray-300 w-full max-w-2xl">
                            {[
                                { name: t('home.ameratName'), phone: '+968 9923 6352 / 24870085' },
                                { name: t('home.qurumName'), phone: '+968 92111965' },
                                { name: t('home.khuwairName'), phone: '+968 92111946' },
                            ].map((branch) => (
                                <div key={branch.name} className="flex flex-col items-center text-center bg-white/5 border border-white/10 rounded-xl px-4 py-3 hover:bg-white/10 transition-colors duration-300">
                                    <span className="text-red-400 font-semibold text-sm mb-1">{branch.name}</span>
                                    <div className="flex items-center gap-1 text-gray-300 text-xs">
                                        <Phone className="h-3 w-3 text-red-500 flex-shrink-0" />
                                        <span>{branch.phone}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Menu Section */}
            <div id="menu-section" className="bg-black py-16">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-white mb-4">{t('common.menu')}</h2>
                        <p className="text-xl text-gray-300">{lang === 'ar' ? 'نقبل الطلبات حتى 20 كم فقط' : 'Currently accepting orders within 20 kms only'}</p>
                    </div>

                    {/* Omani Women's Day - free heart-shaped pizza, 17 to 19 October only */}
                    {womensDayLive && (
                        <div className="mb-6">
                            <div className="relative overflow-hidden rounded-2xl border-2 border-red-500/70 bg-gradient-to-br from-red-950 via-black to-green-950 shadow-[0_0_30px_rgba(220,38,38,0.25)]">
                                <div className="flex flex-col sm:flex-row items-center gap-5 p-5 sm:p-6">
                                    <button
                                        type="button"
                                        onClick={() => setShowWomensDayPoster(true)}
                                        className="flex-shrink-0 rounded-xl overflow-hidden border border-red-500/30 hover:border-red-400 transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-400"
                                    >
                                        <img
                                            src="/assets/logo/womens_day_offer.jpeg"
                                            alt={t('womensDay.posterAlt')}
                                            loading="lazy"
                                            className="w-32 sm:w-36 h-auto object-contain"
                                        />
                                    </button>

                                    <div className="flex-1 text-center sm:text-start">
                                        <div className="flex items-center justify-center sm:justify-start gap-2 mb-2 flex-wrap">
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-gradient-to-r from-red-600 to-red-500 text-white animate-pulse">
                                                <Gift className="h-3.5 w-3.5" />
                                                {t('womensDay.badge')}
                                            </span>
                                            <span className="text-green-400 text-xs font-semibold">{t('womensDay.dates')}</span>
                                        </div>

                                        <h3 className="text-2xl sm:text-3xl font-black text-white mb-1">
                                            {t('womensDay.title')}
                                        </h3>
                                        <p className="text-gray-200 text-base sm:text-lg leading-snug mb-4">
                                            <Trans
                                                i18nKey="womensDay.deal"
                                                components={{
                                                    free: <span className="text-red-400 font-black text-xl sm:text-2xl uppercase tracking-wide" />,
                                                    size: <span className="text-yellow-300 font-black text-lg sm:text-xl uppercase tracking-wide" />
                                                }}
                                            />
                                        </p>

                                        <Button
                                            onClick={goToPizzaMenu}
                                            className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-black rounded-full px-6 py-2.5 transition-all duration-300 transform hover:scale-105 shadow-lg"
                                        >
                                            {t('womensDay.cta')}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Omani Women's Day poster lightbox */}
                    <Dialog open={showWomensDayPoster} onOpenChange={setShowWomensDayPoster}>
                        <DialogContent className="max-w-[95vw] w-full p-0 bg-transparent border-none sm:max-w-[80vw] md:max-w-[70vw] lg:max-w-[55vw] xl:max-w-[45vw]">
                            <DialogTitle className="sr-only">{t('womensDay.title')}</DialogTitle>
                            <button
                                type="button"
                                onClick={() => { setShowWomensDayPoster(false); goToPizzaMenu(); }}
                                aria-label={t('womensDay.cta')}
                                className="relative bg-white rounded-xl overflow-hidden shadow-2xl flex flex-col justify-center items-center p-2 sm:p-3 cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-red-400"
                            >
                                <img
                                    src="/assets/logo/womens_day_offer.jpeg"
                                    alt={t('womensDay.posterAlt')}
                                    className="w-auto max-w-full h-auto max-h-[75vh] object-contain"
                                />
                                <span className="pointer-events-none mt-2 sm:mt-3 mx-auto flex w-fit items-center gap-1.5 whitespace-nowrap rounded-full bg-red-600 px-5 py-2.5 text-sm sm:text-base font-black text-white shadow-[0_6px_20px_rgba(220,38,38,0.5)] animate-pulse">
                                    {t('womensDay.tapToOrder')}
                                </span>
                            </button>
                        </DialogContent>
                    </Dialog>

                    {/* Happy Hour Lunch Treat - every day 11 AM to 4 PM (Oman time) */}
                    <div className="mb-10">
                        <div className={`relative overflow-hidden rounded-2xl border-2 bg-gradient-to-br from-black via-gray-950 to-black transition-all duration-300 ${happyHourLive
                            ? 'border-yellow-400/70 shadow-[0_0_30px_rgba(251,191,36,0.25)]'
                            : 'border-gray-800'}`}>
                            <div className="flex flex-col sm:flex-row items-center gap-5 p-5 sm:p-6">
                                <button
                                    type="button"
                                    onClick={() => setShowHappyHourPoster(true)}
                                    className="flex-shrink-0 rounded-xl overflow-hidden border border-yellow-500/30 hover:border-yellow-400 transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                >
                                    <img
                                        src={happyHourPoster}
                                        alt={t('happyHour.posterAlt')}
                                        loading="lazy"
                                        className={`w-32 sm:w-36 h-auto object-contain ${happyHourLive ? '' : 'opacity-70 grayscale'}`}
                                    />
                                </button>

                                <div className="flex-1 text-center sm:text-start">
                                    <div className="flex items-center justify-center sm:justify-start gap-2 mb-2 flex-wrap">
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider ${happyHourLive
                                            ? 'bg-gradient-to-r from-amber-400 to-yellow-500 text-black animate-pulse'
                                            : 'bg-gray-800 text-gray-400'}`}>
                                            <Clock className="h-3.5 w-3.5" />
                                            {happyHourLive ? t('happyHour.liveNow') : t('happyHour.startsAt')}
                                        </span>
                                        <span className="text-yellow-400/90 text-xs font-semibold">{t('happyHour.window')}</span>
                                    </div>

                                    <h3 className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-yellow-300 via-amber-400 to-yellow-500 bg-clip-text text-transparent mb-1">
                                        {t('happyHour.title')}
                                    </h3>
                                    <p className="text-gray-300 text-sm mb-4">{t('happyHour.deals')}</p>

                                    <Button
                                        onClick={() => {
                                            onSelectCategory('offers');
                                            const filterSection = document.getElementById('filter-section');
                                            if (filterSection) filterSection.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                        className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-black rounded-full px-6 py-2.5 transition-all duration-300 transform hover:scale-105 shadow-lg"
                                    >
                                        {t('happyHour.cta')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Happy Hour poster lightbox */}
                    <Dialog open={showHappyHourPoster} onOpenChange={setShowHappyHourPoster}>
                        <DialogContent className="max-w-[95vw] w-full p-0 bg-transparent border-none sm:max-w-[80vw] md:max-w-[70vw] lg:max-w-[55vw] xl:max-w-[45vw]">
                            <DialogTitle className="sr-only">{t('happyHour.title')}</DialogTitle>
                            <div className="relative bg-white rounded-xl overflow-hidden shadow-2xl flex justify-center items-center p-2 sm:p-3">
                                <img
                                    src={happyHourPoster}
                                    alt={t('happyHour.posterAlt')}
                                    className="w-auto max-w-full h-auto max-h-[85vh] object-contain"
                                />
                            </div>
                        </DialogContent>
                    </Dialog>

                    {/* Wow Element */}
                    <WowElement />

                    {/* User Coupons */}
                    <UserCoupons />

                    {/* Category Filter - Mobile Horizontal Scroll */}
                    <div id="filter-section" className="relative mb-8 pt-4">
                        <div className="flex flex-nowrap overflow-x-auto gap-2 md:gap-3 py-2 scroll-smooth no-scrollbar px-4 md:px-0">
                            {categories.map(category => (
                                <Button
                                    key={typeof category === 'object' ? category.name : category}
                                    onClick={() => onSelectCategory(typeof category === 'object' ? category.name : category)}
                                    variant={selectedCategory === (typeof category === 'object' ? category.name : category) ? "default" : "outline"}
                                    className={`capitalize flex items-center transition-all duration-300 transform hover:scale-105 hover:shadow-xl rounded-full px-4 md:px-6 py-2 md:py-2.5 border-2 flex-shrink-0
                    ${category === 'offers'
                                            ? (selectedCategory === 'offers'
                                                ? "bg-gradient-to-br from-amber-400 via-yellow-300 to-amber-600 text-black font-black border-none shadow-[0_0_25px_rgba(251,191,36,0.6)] ring-2 ring-yellow-100 scale-105"
                                                : "bg-gray-950/60 text-yellow-500 border-yellow-600/50 hover:bg-yellow-500/10 hover:border-yellow-400 hover:text-yellow-400 hover:shadow-[0_0_15px_rgba(251,191,36,0.2)]")
                                            : (selectedCategory === (typeof category === 'object' ? category.name : category)
                                                ? "bg-red-600 hover:bg-red-700 text-white shadow-lg ring-2 ring-red-300 border-none scale-105"
                                                : "border-gray-800 text-gray-500 hover:bg-red-600/10 hover:text-red-500 bg-gray-950/40 backdrop-blur-md hover:border-red-500/50")
                                        }`}
                                >
                                    {category !== 'all' && (
                                        <span className={`mr-2 transition-transform duration-300 ${category === 'offers' ? 'text-yellow-500' : 'text-current'}`}>
                                            {getCategoryIcon(category)}
                                        </span>
                                    )}
                                    <span className="font-bold tracking-wide text-sm md:text-base">
                                        {category === 'all'
                                            ? t('common.all')
                                            : category === 'offers'
                                                ? (lang === 'ar' ? 'عروض' : 'Offers')
                                                : typeof category === 'object'
                                                    ? (lang === 'ar' && category.name_ar ? category.name_ar : category.name)
                                                    : (category.charAt(0).toUpperCase() + category.slice(1))}
                                    </span>
                                </Button>
                            ))}
                        </div>
                    </div>

                    {/* Search Bar */}
                    <div className="relative mb-6 max-w-md mx-auto">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={t('menu.searchPlaceholder')}
                            className="w-full pl-12 pr-10 py-3 text-base bg-gray-900 border border-gray-700 rounded-full text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        )}
                    </div>
                    {searchQuery.trim() && filteredItems.length === 0 && (
                        <div className="text-center py-8 text-gray-400">
                            <p className="text-lg">{t('menu.noResults')}</p>
                        </div>
                    )}

                    {/* Menu Items Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredItems.map(item => {
                            // Special logic for Tuesday Offer (BOGO)
                            const isTuesdayOfferItem = item.name.toLowerCase().includes('tuesday offer');
                            const canOrderTuesdayOffer = isTuesdayOfferItem ? isTuesday() : true;
                            // Special logic for Limited Time Offer combo deal (Sat, Sun, Mon, Wed, Thu only)
                            const isLimitedComboItem = item.name.toLowerCase().includes('limited time offer');
                            const canOrderCombo = isLimitedComboItem ? isComboDay() : true;
                            // Special logic for Happy Hour lunch treat (every day, 11 AM - 4 PM Oman time)
                            const isHappyHourDeal = isHappyHourItem(item);
                            const canOrderHappyHour = isHappyHourDeal ? happyHourLive : true;
                            const isItemAvailable = item.available && canOrderTuesdayOffer && canOrderCombo && canOrderHappyHour;

                            return (
                                <Card key={item.id} className={`group hover:shadow-xl transition-all duration-300 shadow-lg overflow-hidden backdrop-blur-sm text-white relative ${isHappyHourDeal
                                    ? 'hh-card hh-shimmer bg-gradient-to-b from-amber-950/50 via-black/70 to-black/70 border-2 border-amber-400/60 rounded-2xl'
                                    : 'bg-black/40 border border-red-500/20'}`}>
                                    {recentlyAddedItems.has(item.id) && (
                                        <div className={`absolute right-2 z-40 ${isHappyHourDeal ? 'top-11' : 'top-2'}`}>
                                            <div className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-bounce">
                                                ✓ Added!
                                            </div>
                                        </div>
                                    )}
                                    {isHappyHourDeal && (
                                        <div className="relative z-30 flex items-center justify-between gap-2 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 px-3 py-1.5">
                                            <span className="flex items-center gap-1.5 text-black font-black text-[11px] sm:text-xs uppercase tracking-widest">
                                                <Clock className="h-3.5 w-3.5" />
                                                {t('happyHour.title')}
                                            </span>
                                            {canOrderHappyHour ? (
                                                <HappyHourCountdown className="text-black/80 font-bold text-[10px] sm:text-[11px] uppercase tracking-wide" />
                                            ) : (
                                                <span className="text-black/80 font-bold text-[10px] sm:text-[11px] uppercase tracking-wide">
                                                    {t('happyHour.window')}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    <div className="relative overflow-hidden">
                                        <img
                                            src={item.image}
                                            alt={item.name}
                                            className={`w-full transition-transform duration-500 ${isHappyHourDeal
                                                ? 'h-52 object-cover bg-white scale-105 group-hover:scale-110'
                                                : 'h-48 object-contain bg-white p-2 scale-[1.7] group-hover:scale-[1.8]'} ${!isItemAvailable ? 'opacity-60 grayscale filter' : ''}`}
                                        />
                                        <Badge className={`absolute top-3 left-3 z-10 ${isHappyHourDeal
                                            ? 'bg-gradient-to-r from-amber-500 to-yellow-400 text-black font-black shadow-[0_2px_10px_rgba(251,191,36,0.5)] border-none'
                                            : 'bg-red-600'}`}>
                                            {item.category && item.category.name ? item.category.name : (item.category || 'Unknown')}
                                        </Badge>

                                        {isHappyHourDeal && isItemAvailable && (
                                            <div className="absolute bottom-3 right-3 z-10 flex items-center gap-1.5 rounded-full bg-black/85 backdrop-blur-sm px-3 py-1.5 border border-amber-400/50 shadow-lg">
                                                <Sparkles className="h-3.5 w-3.5 text-amber-400" />
                                                <span className="text-amber-300 text-xs font-black tracking-wide">
                                                    {t('happyHour.fromPrice', { price: Math.min(...item.sizes.map(sz => sz.price)).toFixed(3) })}
                                                </span>
                                            </div>
                                        )}

                                        {/* Status Overlays */}
                                        {!item.available ? (
                                            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-20">
                                                <div className="bg-red-600/90 text-white font-black px-6 py-2 border-2 border-red-400 rounded-lg shadow-[0_0_15px_rgba(220,38,38,0.5)] transform -rotate-12">
                                                    {t('home.soldOut')}
                                                </div>
                                            </div>
                                        ) : isTuesdayOfferItem && !canOrderTuesdayOffer ? (
                                            <div className="absolute inset-0 bg-black/80 backdrop-blur-md flex flex-col items-center justify-center z-20 transition-all duration-300">
                                                <div className="bg-amber-500/10 p-4 rounded-full mb-3 backdrop-blur-sm border border-amber-500/30">
                                                    <Calendar className="h-10 w-10 text-amber-400" />
                                                </div>
                                                <div className="bg-gradient-to-r from-amber-600 to-yellow-500 text-black font-black px-5 py-2.5 rounded-xl shadow-[0_0_20px_rgba(245,158,11,0.4)] text-center max-w-[80%] uppercase tracking-wider text-sm border-2 border-amber-300/50">
                                                    {t('home.onlyTuesdays')}
                                                </div>
                                            </div>
                                        ) : isLimitedComboItem && !canOrderCombo ? (
                                            <div className="absolute inset-0 bg-black/80 backdrop-blur-md flex flex-col items-center justify-center z-20 transition-all duration-300">
                                                <div className="bg-amber-500/10 p-4 rounded-full mb-3 backdrop-blur-sm border border-amber-500/30">
                                                    <Calendar className="h-10 w-10 text-amber-400" />
                                                </div>
                                                <div className="bg-gradient-to-r from-amber-600 to-yellow-500 text-black font-black px-5 py-2.5 rounded-xl shadow-[0_0_20px_rgba(245,158,11,0.4)] text-center max-w-[80%] uppercase tracking-wider text-sm border-2 border-amber-300/50">
                                                    {t('home.onlyOtherDays')}
                                                </div>
                                            </div>
                                        ) : isHappyHourDeal && !canOrderHappyHour ? (
                                            <div className="absolute inset-0 bg-black/80 backdrop-blur-md flex flex-col items-center justify-center z-20 transition-all duration-300">
                                                <div className="bg-amber-500/10 p-4 rounded-full mb-3 backdrop-blur-sm border border-amber-500/30">
                                                    <Clock className="h-10 w-10 text-amber-400" />
                                                </div>
                                                <div className="bg-gradient-to-r from-amber-600 to-yellow-500 text-black font-black px-5 py-2.5 rounded-xl shadow-[0_0_20px_rgba(245,158,11,0.4)] text-center max-w-[80%] uppercase tracking-wider text-sm border-2 border-amber-300/50">
                                                    {t('home.onlyHappyHour')}
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                    <CardHeader className={`transition-colors duration-300 ${isItemAvailable ? 'group-hover:bg-red-500/10' : ''}`}>
                                        <CardTitle className={`text-lg sm:text-xl break-words leading-tight transition-colors duration-300 ${isHappyHourDeal
                                            ? (isItemAvailable ? 'font-black bg-gradient-to-r from-yellow-200 via-amber-300 to-yellow-400 bg-clip-text text-transparent' : 'font-black text-gray-400')
                                            : (isItemAvailable ? 'text-white group-hover:text-red-400' : 'text-gray-400')}`}>
                                            {pickLocalized(item, 'name')}
                                        </CardTitle>
                                        <CardDescription className={`break-words transition-colors duration-300 ${isItemAvailable ? 'text-gray-300 group-hover:text-gray-200' : 'text-gray-500'}`}>
                                            {getDisplayDescription(pickLocalized(item, 'description') || item.description)}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className={isItemAvailable ? '' : 'opacity-75'}>
                                        {/* Size Selection - Only show if multiple sizes */}
                                        {item.sizes && item.sizes.length > 1 ? (
                                            <div className="mb-4">
                                                <h4 className={`text-sm font-semibold mb-2 ${isItemAvailable ? 'text-gray-300' : 'text-gray-500'}`}>{t('menu.selectSize')}:</h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {item.sizes.map(size => (
                                                        <button
                                                            key={size.id}
                                                            onClick={() => {
                                                                if (isItemAvailable) setSelectedSizes(prev => ({ ...prev, [item.id]: size.size }));
                                                            }}
                                                            className={`px-3 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 transform flex-shrink-0
                              ${!isItemAvailable
                                                                    ? 'bg-gray-800 text-gray-500 border border-gray-700 cursor-not-allowed hidden' // Hide entirely or make very muted
                                                                    : !size.available
                                                                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed border border-gray-500'
                                                                        : selectedSizes[item.id] === size.size
                                                                            ? (isHappyHourDeal
                                                                                ? 'bg-gradient-to-r from-amber-400 to-yellow-500 text-black ring-2 ring-yellow-200 shadow-[0_0_18px_rgba(251,191,36,0.55)] scale-105'
                                                                                : 'bg-red-700 text-white ring-2 ring-red-300 shadow-lg scale-105')
                                                                            : (isHappyHourDeal
                                                                                ? 'bg-amber-500/15 text-amber-200 border border-amber-400/50 hover:bg-gradient-to-r hover:from-amber-400 hover:to-yellow-500 hover:text-black hover:scale-105 hover:shadow-[0_0_18px_rgba(251,191,36,0.45)]'
                                                                                : 'bg-red-600 hover:bg-red-700 hover:ring-2 hover:ring-red-300 text-white hover:scale-105 hover:shadow-lg')
                                                                }`}
                                                            disabled={!size.available || !isItemAvailable}
                                                            style={{ display: (!isItemAvailable && selectedSizes[item.id] !== size.size) ? 'none' : 'block' }} // Simplify view when disabled
                                                        >
                                                            <span dir="ltr">{size.size.charAt(0).toUpperCase() + size.size.slice(1)} - {t('common.omr')} {size.price.toFixed(3)}</span>
                                                            {earnsHeartPizza(item, size.size) && (
                                                                <span className="block mt-1 text-[11px] sm:text-xs font-black uppercase tracking-wide text-yellow-200">
                                                                    {t('womensDay.sizeBonus')}
                                                                </span>
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : null}

                                        {/* Price Display - Only show range if single size, otherwise size buttons handle it */}
                                        <div className={`mb-4 p-2 rounded-lg transition-all duration-300 ${isHappyHourDeal
                                            ? 'bg-amber-500/10 border border-amber-400/30'
                                            : (isItemAvailable ? 'group-hover:bg-red-500/5' : '')}`}>
                                            <span className={`text-lg font-bold transition-colors duration-300 ${isHappyHourDeal
                                                ? (isItemAvailable ? 'text-amber-300 font-black' : 'text-gray-500')
                                                : (isItemAvailable ? 'text-red-500 group-hover:text-red-400' : 'text-gray-500')}`}>
                                                {item.sizes && item.sizes.length === 1 ? (
                                                    <span dir="ltr">{t('common.omr')} {item.sizes[0].price.toFixed(3)}</span>
                                                ) : (
                                                    item.sizes && item.sizes.length > 1 ? (
                                                        <span className="hidden md:inline" dir="ltr">
                                                            {t('common.omr')} {Math.min(...item.sizes.map(s => s.price)).toFixed(3)} - {Math.max(...item.sizes.map(s => s.price)).toFixed(3)}
                                                        </span>
                                                    ) : t('menu.priceNotAvailable')
                                                )}
                                            </span>
                                        </div>

                                        {/* Add to Cart */}
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-2">
                                                {cartItems.find(cartItem => cartItem.id === item.id && cartItem.selectedSize === (item.sizes.length === 1 ? item.sizes[0].size : selectedSizes[item.id])) ? (
                                                    <>
                                                        <Button
                                                            onClick={() => {
                                                                const cartItem = cartItems.find(cartItem => cartItem.id === item.id && cartItem.selectedSize === (item.sizes.length === 1 ? item.sizes[0].size : selectedSizes[item.id]));
                                                                if (cartItem) updateQuantity(cartItem.cartId, (cartItem.quantity || 1) - 1);
                                                            }}
                                                            className={`transition-all duration-300 w-8 h-8 p-0 transform ${isItemAvailable ? 'bg-red-600 hover:bg-red-700 text-white hover:scale-110 hover:shadow-xl hover:rotate-12' : 'bg-gray-800 text-gray-500 opacity-50 cursor-not-allowed'}`}
                                                            disabled={!isItemAvailable}
                                                        >
                                                            -
                                                        </Button>
                                                        <span className={`font-semibold min-w-[2rem] text-center transition-colors duration-300 ${isItemAvailable ? 'text-white group-hover:text-red-400' : 'text-gray-500'}`}>
                                                            {cartItems.find(cartItem => cartItem.id === item.id && cartItem.selectedSize === (item.sizes.length === 1 ? item.sizes[0].size : selectedSizes[item.id])).quantity}
                                                        </span>
                                                        <Button
                                                            onClick={() => handleItemClick(item, item.sizes.length === 1 ? item.sizes[0].size : selectedSizes[item.id])}
                                                            className={`transition-all duration-300 w-8 h-8 p-0 transform ${isItemAvailable ? 'bg-red-600 hover:bg-red-700 text-white hover:scale-110 hover:shadow-xl hover:-rotate-12' : 'bg-gray-800 text-gray-500 opacity-50 cursor-not-allowed'}`}
                                                            disabled={!isItemAvailable}
                                                        >
                                                            +
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <Button
                                                        onClick={() => {
                                                            const selectedSize = item.sizes.length === 1
                                                                ? item.sizes[0].size
                                                                : (selectedSizes[item.id] || item.sizes?.[0]?.size || 'medium');
                                                            handleItemClick(item, selectedSize);
                                                        }}
                                                        className={`flex-1 transition-all duration-300 font-bold tracking-wide transform ${!isItemAvailable
                                                            ? 'bg-gray-800/80 text-gray-500 border border-gray-700 cursor-not-allowed w-full shadow-inner'
                                                            : isHappyHourDeal
                                                                ? 'bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 hover:from-amber-300 hover:via-yellow-300 hover:to-amber-400 text-black font-black shadow-[0_6px_20px_rgba(251,191,36,0.35)] hover:shadow-[0_8px_28px_rgba(251,191,36,0.55)] hover:-translate-y-1 hover:scale-[1.03]'
                                                                : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white shadow-lg hover:shadow-red-500/25 hover:-translate-y-1 hover:scale-[1.02]'
                                                            }`}
                                                        disabled={!isItemAvailable || (item.sizes.length > 1 && !selectedSizes[item.id])}
                                                    >
                                                        {isItemAvailable ? (
                                                            isComboItem(item) ? (lang === 'ar' ? 'تخصيص العرض' : 'Customize Deal') : (
                                                                item.sizes.length === 1 ? t('menu.addToCart') : (selectedSizes[item.id]
                                                                    ? (earnsHeartPizza(item, selectedSizes[item.id]) ? t('womensDay.addToCartWithFree') : t('menu.addToCart'))
                                                                    : t('menu.selectSize'))
                                                            )
                                                        ) : t('common.unavailable')}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </div>
            {/* Combo Modal */}
            <ComboCustomizationModal
                isOpen={comboModalOpen}
                onClose={() => setComboModalOpen(false)}
                item={selectedComboItem}
                onAdd={onAddToCart}
                menuItems={menuItems}
                selectedSize={selectedComboSize}
            />
        </div>
    );
};


// Simple confetti function
const triggerConfetti = () => {
    // Create confetti effect using CSS animations
    const colors = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'];
    for (let i = 0; i < 50; i++) {
        setTimeout(() => {
            const confetti = document.createElement('div');
            confetti.style.position = 'fixed';
            confetti.style.left = Math.random() * 100 + 'vw';
            confetti.style.top = '-10px';
            confetti.style.width = '10px';
            confetti.style.height = '10px';
            confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
            confetti.style.borderRadius = '50%';
            confetti.style.pointerEvents = 'none';
            confetti.style.zIndex = '9999';
            confetti.style.animation = 'confetti-fall 3s linear forwards';
            document.body.appendChild(confetti);

            setTimeout(() => {
                document.body.removeChild(confetti);
            }, 3000);
        }, i * 50);
    }
};

// Add confetti CSS animation
const confettiStyle = document.createElement('style');
confettiStyle.textContent = `
  @keyframes confetti-fall {
    0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
    100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
  }
`;
document.head.appendChild(confettiStyle);

// Game Component
const Game = () => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [gameResult, setGameResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [gameAttemptsRemaining, setGameAttemptsRemaining] = useState(3);
    const [timeUntilReset, setTimeUntilReset] = useState(0);
    const [countdownSeconds, setCountdownSeconds] = useState(0);
    const [hasWon, setHasWon] = useState(() => {
        // Check localStorage for existing win state
        const savedWinState = localStorage.getItem('gameWinState');
        if (savedWinState) {
            const winData = JSON.parse(savedWinState);
            // Check if the win was today
            const today = new Date().toDateString();
            if (winData.date === today) {
                return true;
            } else {
                // Clear old win state
                localStorage.removeItem('gameWinState');
                return false;
            }
        }
        return false;
    });
    const [winData, setWinData] = useState(() => {
        // Check localStorage for existing win data
        const savedWinState = localStorage.getItem('gameWinState');
        if (savedWinState) {
            const data = JSON.parse(savedWinState);
            const today = new Date().toDateString();
            if (data.date === today) {
                return data;
            } else {
                localStorage.removeItem('gameWinState');
                return null;
            }
        }
        return null;
    });

    const fetchQuestion = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/game/question`);
            setCurrentQuestion(response.data);
            setSelectedAnswer(null);
            setShowResult(false);
            setGameResult(null);
            setHasWon(false);
        } catch (error) {
            console.error('Error fetching question:', error);
        }
        setLoading(false);
    };

    const submitAnswer = async () => {
        if (selectedAnswer === null) return;

        try {
            console.log('Submitting answer:', selectedAnswer);

            // Check if user can still play
            const canPlay = await canPlayGame();
            console.log('Can play:', canPlay);

            if (!canPlay) {
                toast.error('You have used all your game attempts for today. Come back tomorrow!');
                return;
            }

            const deviceInfo = getDeviceInfo();
            const response = await axios.post(`${API_BASE_URL}/api/game/answer`, {
                question_id: currentQuestion.id,
                answer: selectedAnswer,
                device_info: deviceInfo
            });

            console.log('Game response:', response.data);

            if (response.data.correct) {
                // Correct answer - end game session
                await recordCorrectGameAttempt();

                // Pick a random coupon from gameCoupons array
                const randomCoupon = gameCoupons[Math.floor(Math.random() * gameCoupons.length)];

                // Add coupon to user's collection using game-specific endpoint
                try {
                    const response = await axios.post(`${API_BASE_URL}/api/user/game/add-coupon`, {
                        code: randomCoupon.code,
                        text: randomCoupon.text,
                        type: randomCoupon.type,
                        discount: randomCoupon.discount,
                        minAmount: randomCoupon.minAmount,
                        device_info: getDeviceInfo()
                    });

                    if (response.data.message) {
                        // Trigger confetti animation
                        triggerConfetti();

                        // Show success message and coupon
                        toast.success(`🎉 Congratulations! You got it right! You won: ${randomCoupon.text}`);

                        // Refresh the coupons display
                        setTimeout(() => {
                            if (window.refreshUserCoupons) {
                                window.refreshUserCoupons();
                            }
                        }, 1000);
                    }

                    // Show success result briefly then end game
                    setGameResult({
                        correct: true,
                        correct_answer: response.data.correct_answer,
                        coupon_code: randomCoupon.code,
                        coupon_discount: randomCoupon.discount,
                        coupon_type: randomCoupon.type
                    });
                    setShowResult(true);
                    setHasWon(true);
                    setGameAttemptsRemaining(0);
                    setTimeUntilReset(24);

                    // Save win state to localStorage
                    const winStateData = {
                        date: new Date().toDateString(),
                        coupon_code: randomCoupon.code,
                        coupon_discount: randomCoupon.discount,
                        coupon_type: randomCoupon.type,
                        correct: true,
                        created_at: new Date().toISOString()
                    };
                    setWinData(winStateData);
                    localStorage.setItem('gameWinState', JSON.stringify(winStateData));
                } catch (error) {
                    console.error('Error adding coupon:', error);
                    toast.error('Failed to add coupon. Please try again.');
                }

                // Set countdown based on actual creation time
                const remainingTime = calculateRemainingTime(new Date().toISOString());
                setCountdownSeconds(remainingTime);

                // After 8 seconds, hide the result and show game over
                setTimeout(() => {
                    setShowResult(false);
                    setCurrentQuestion(null);
                }, 8000);

            } else {
                // Wrong answer - continue game
                await recordGameAttempt();
                const attemptsRemaining = await getGameAttemptsRemaining();
                setGameAttemptsRemaining(attemptsRemaining);

                // Show wrong answer message
                toast.error('❌ Wrong answer! Try again.');

                // Show result screen briefly to display wrong answer feedback
                setGameResult(response.data);
                setShowResult(true);

                // After 3 seconds, reset for next attempt
                setTimeout(() => {
                    setShowResult(false);
                    setGameResult(null);
                    setSelectedAnswer(null);
                    // Fetch a new question for the next attempt
                    if (attemptsRemaining > 0) {
                        fetchQuestion();
                    }
                }, 3000);
            }
        } catch (error) {
            console.error('Error submitting answer:', error);
            toast.error('Failed to submit answer. Please try again.');
        }
    };

    useEffect(() => {
        const loadGameStatus = async () => {
            try {
                // Check if win state is from today and still valid
                if (winData) {
                    const today = new Date().toDateString();
                    const remainingTime = calculateRemainingTime(winData.created_at);

                    if (winData.date !== today || remainingTime <= 0) {
                        // Clear expired win state
                        setHasWon(false);
                        setWinData(null);
                        localStorage.removeItem('gameWinState');
                    }
                }

                // Update game status
                const [attemptsRemaining, resetTime] = await Promise.all([
                    getGameAttemptsRemaining(),
                    getTimeUntilGameReset()
                ]);

                setGameAttemptsRemaining(attemptsRemaining);
                setTimeUntilReset(resetTime);

                // If user has won today, calculate remaining time based on creation timestamp
                if (hasWon && winData && winData.created_at) {
                    const remainingTime = calculateRemainingTime(winData.created_at);
                    setCountdownSeconds(remainingTime);
                } else {
                    setCountdownSeconds(resetTime);
                }

                // Only fetch question if user can play and hasn't won
                const canPlay = await canPlayGame();
                if (canPlay && !hasWon) {
                    fetchQuestion();
                }
            } catch (error) {
                console.error('Error loading game status:', error);
            }
        };

        loadGameStatus();
    }, [hasWon, winData]);

    // Calculate remaining time based on creation timestamp
    const calculateRemainingTime = (createdAt) => {
        if (!createdAt) return 0;

        const created = new Date(createdAt);
        const now = new Date();
        const endTime = new Date(created.getTime() + 24 * 60 * 60 * 1000); // 24 hours after creation

        const remainingMs = endTime.getTime() - now.getTime();
        return Math.max(0, Math.floor(remainingMs / 1000)); // Convert to seconds
    };

    // Format countdown time
    const formatCountdown = (seconds) => {
        if (seconds <= 0) return '00:00:00';

        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Countdown timer effect
    useEffect(() => {
        let interval;
        if (countdownSeconds > 0) {
            interval = setInterval(() => {
                setCountdownSeconds(prev => {
                    if (prev <= 1) {
                        // Clear win state when countdown reaches 0
                        if (hasWon && winData) {
                            setHasWon(false);
                            setWinData(null);
                            localStorage.removeItem('gameWinState');
                        }
                        // Reload game status when countdown reaches 0
                        setTimeout(() => {
                            window.location.reload();
                        }, 1000);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [countdownSeconds, hasWon, winData]);

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                    <h2 className="text-2xl md:text-4xl font-bold text-yellow-500 mb-4 flex items-center justify-center">
                        <Trophy className="h-8 w-8 md:h-10 md:w-10 me-3 text-yellow-500" />
                        {t('game.guessTitle')}
                    </h2>
                    <p className="text-sm md:text-xl text-white mb-4">{t('game.guessSubtitle')}</p>

                    {/* Game Status */}
                    <div className="flex items-center justify-center space-x-6 mb-6">
                        <div className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded-lg border border-yellow-300">
                            <span className="font-semibold">{t('game.attemptsRemaining', { current: gameAttemptsRemaining, total: 3 })}</span>
                        </div>
                        {countdownSeconds > 0 && (
                            <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-lg border border-blue-300">
                                <span className="font-semibold">{t('game.nextReset', { time: formatCountdown(countdownSeconds) })}</span>
                            </div>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center min-h-96">
                        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-600"></div>
                    </div>
                ) : gameAttemptsRemaining === 0 && !hasWon ? (
                    <div className="text-center py-12">
                        <Trophy className="h-16 w-16 text-yellow-400 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-yellow-500 mb-2">{t('game.usedUp')}</h3>
                        <p className="text-white mb-4">{t('game.usedUpSubtitle')}</p>
                        <p className="text-gray-300">{t('game.comeBackTomorrow')}</p>
                        {countdownSeconds > 0 && (
                            <p className="text-sm text-gray-400 mt-2">{t('game.nextResetIn', { time: formatCountdown(countdownSeconds) })}</p>
                        )}
                    </div>
                ) : hasWon && winData?.correct ? (
                    <div className="text-center py-12">
                        <div className="mb-8">
                            <Alert className="border-green-200 bg-green-50 max-w-2xl mx-auto">
                                <Gift className="h-4 w-4 text-green-600" />
                                <AlertDescription className="text-green-800">
                                    <strong className="text-2xl">{t('game.congratulations')}</strong>
                                    <p className="text-lg mt-2">{t('game.gotItRight')}</p>
                                    {winData.coupon_code && (
                                        <div className="mt-4 p-4 bg-green-100 rounded-lg border-2 border-green-300">
                                            <div className="text-center">
                                                <p className="font-bold text-lg text-green-800 mb-2">{t('game.wonCoupon')}</p>
                                                <p className="font-semibold text-green-700 mb-3">
                                                    {winData.coupon_type === 'percentage' && t('game.percentOff', { discount: winData.coupon_discount })}
                                                    {winData.coupon_type === 'fixed' && t('game.fixedOff', { discount: winData.coupon_discount })}
                                                    {winData.coupon_type === 'delivery' && t('game.freeDelivery')}
                                                </p>
                                                <div className="bg-white p-3 rounded-lg border border-green-400 mb-3">
                                                    <p className="text-sm text-green-600 mb-1">{t('game.couponCode')}</p>
                                                    <p className="text-xl font-mono font-bold text-green-800">{winData.coupon_code}</p>
                                                </div>
                                                <p className="text-sm text-green-700">{t('game.useAtCheckout')}</p>
                                            </div>
                                        </div>
                                    )}
                                </AlertDescription>
                            </Alert>
                        </div>
                        <Button
                            onClick={() => window.location.href = '/'}
                            className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg"
                        >
                            {t('game.backToMenu')}
                        </Button>
                    </div>
                ) : currentQuestion && (
                    <Card className="shadow-xl">
                        <CardHeader className="text-center">
                            <CardTitle className="text-2xl mb-4">
                                {lang === 'ar' && currentQuestion.question_ar ? currentQuestion.question_ar : currentQuestion.question}
                            </CardTitle>
                            <div className="relative rounded-xl overflow-hidden">
                                <img
                                    src={currentQuestion.image_url}
                                    alt="Question"
                                    className="w-full h-64 md:h-96 object-cover"
                                />
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {(lang === 'ar' && currentQuestion.options_ar ? currentQuestion.options_ar : currentQuestion.options).map((option, index) => (
                                    <Button
                                        key={index}
                                        onClick={() => setSelectedAnswer(index)}
                                        variant={selectedAnswer === index ? "default" : "outline"}
                                        className={`w-full p-4 text-right justify-end text-lg ${selectedAnswer === index
                                            ? "bg-red-600 hover:bg-red-700 text-white"
                                            : "border-gray-300 hover:bg-gray-50"
                                            }`}
                                        disabled={showResult}
                                    >
                                        {String.fromCharCode(65 + index)}. {option}
                                    </Button>
                                ))}
                            </div>

                            {!showResult ? (
                                <div className="mt-6 text-center">
                                    <Button
                                        onClick={submitAnswer}
                                        disabled={selectedAnswer === null}
                                        className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 text-lg"
                                    >
                                        {t('game.submitAnswer')}
                                    </Button>
                                </div>
                            ) : (
                                <div className="mt-6 space-y-4">
                                    {gameResult && gameResult.correct && (
                                        <Alert className="border-green-200 bg-green-50">
                                            <Gift className="h-4 w-4 text-green-600" />
                                            <AlertDescription className="text-green-800">
                                                <strong>{t('game.congratulations')}</strong> {t('game.gotItRight')}
                                                {gameResult.coupon_code && (
                                                    <div className="mt-4 p-4 bg-green-100 rounded-lg border-2 border-green-300">
                                                        <div className="text-center">
                                                            <p className="font-bold text-lg text-green-800 mb-2">{t('game.wonCoupon')}</p>
                                                            <p className="font-semibold text-green-700 mb-3">
                                                                {gameResult.coupon_type === 'percentage' && t('game.percentOff', { discount: gameResult.coupon_discount })}
                                                                {gameResult.coupon_type === 'fixed' && t('game.fixedOff', { discount: gameResult.coupon_discount })}
                                                                {gameResult.coupon_type === 'delivery' && t('game.freeDelivery')}
                                                            </p>
                                                            <div className="bg-white p-3 rounded-lg border border-green-400 mb-3">
                                                                <p className="text-sm text-green-600 mb-1">{t('game.couponCode')}</p>
                                                                <p className="text-xl font-mono font-bold text-green-800">{gameResult.coupon_code}</p>
                                                            </div>
                                                            <p className="text-sm text-green-700">{t('game.useAtCheckout')}</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    {gameResult && !gameResult.correct && (
                                        <Alert className="border-red-200 bg-red-50">
                                            <AlertDescription className="text-red-800">
                                                <strong>❌ Wrong Answer!</strong>
                                                <p className="mt-2">Don't worry! You still have {gameAttemptsRemaining} attempts remaining.</p>
                                                <p className="text-sm mt-1">Loading next question...</p>
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="flex justify-center space-x-4 mt-6">
                                        {gameResult && gameResult.correct && (
                                            <Button
                                                onClick={() => window.location.href = '/'}
                                                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3"
                                            >
                                                {t('game.backToMenu')}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
};

// Admin Login Component
const AdminLogin = ({ onLogin }) => {
    const [credentials, setCredentials] = useState({ username: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await axios.post(`${API_BASE_URL}/api/admin/login`, credentials);
            localStorage.setItem('admin_token', response.data.access_token);
            onLogin(response.data.access_token);
            toast.success('Login successful!');
        } catch (error) {
            setError('Invalid username or password');
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <CardTitle className="text-2xl text-red-600">Admin Login</CardTitle>
                    <CardDescription>Access the Wow Pizza admin panel</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <Label htmlFor="username">Username</Label>
                            <Input
                                id="username"
                                value={credentials.username}
                                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                value={credentials.password}
                                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                                required
                            />
                        </div>
                        {error && (
                            <Alert className="border-red-200 bg-red-50">
                                <AlertDescription className="text-red-800">{error}</AlertDescription>
                            </Alert>
                        )}
                        <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={loading}>
                            {loading ? 'Logging in...' : 'Login'}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

// Add New Item Modal Component
const AddMenuItemModal = ({ isOpen, onClose, onAdd, categories, menuItems = [] }) => {
    const [newItem, setNewItem] = useState({
        name: '',
        description: '',
        image: '',
        category_id: '',
        available: true,
        singlePrice: false,
        sizes: [
            { size: 'small', price: '', available: true },
            { size: 'medium', price: '', available: true },
            { size: 'large', price: '', available: true }
        ]
    });

    // Combo Deal State
    const [isCombo, setIsCombo] = useState(false);
    const [comboGroups, setComboGroups] = useState([]);
    const [fixedComboItems, setFixedComboItems] = useState([]); // array of item names

    const addComboGroup = () => {
        setComboGroups([...comboGroups, {
            id: Date.now().toString(),
            title: 'Select Items',
            type: 'category', // 'category' or 'manual'
            category_id: '',
            quantity: 1,
            allowed_items: [], // array of item IDs from menu
            custom_items: [] // array of custom strings
        }]);
    };

    const updateComboGroup = (index, field, value) => {
        const updatedGroups = [...comboGroups];
        updatedGroups[index] = { ...updatedGroups[index], [field]: value };
        setComboGroups(updatedGroups);
    };

    const removeComboGroup = (index) => {
        setComboGroups(comboGroups.filter((_, i) => i !== index));
    };

    const toggleAllowedItem = (groupIndex, itemId) => {
        const group = comboGroups[groupIndex];
        if (!group) return;

        const currentAllowed = group.allowed_items || [];
        const newAllowed = currentAllowed.includes(itemId)
            ? currentAllowed.filter(id => id !== itemId)
            : [...currentAllowed, itemId];

        updateComboGroup(groupIndex, 'allowed_items', newAllowed);
    };

    const addCustomItem = (groupIndex, itemName) => {
        if (!itemName || !itemName.trim()) return;
        const group = comboGroups[groupIndex];
        if (!group) return;

        const currentCustom = group.custom_items || [];
        if (!currentCustom.includes(itemName.trim())) {
            updateComboGroup(groupIndex, 'custom_items', [...currentCustom, itemName.trim()]);
        }
    };

    const removeCustomItem = (groupIndex, itemName) => {
        const group = comboGroups[groupIndex];
        if (!group) return;

        const currentCustom = group.custom_items || [];
        updateComboGroup(groupIndex, 'custom_items', currentCustom.filter(i => i !== itemName));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!newItem.name || !newItem.description || !newItem.image || !newItem.category_id) {
            toast.error('Please fill in all required fields');
            return;
        }

        // Validate sizes
        const validSizes = newItem.sizes.filter(size => size.price && !isNaN(size.price) && parseFloat(size.price) > 0);
        if (validSizes.length === 0) {
            toast.error('Please enter at least one valid price for sizes');
            return;
        }

        // Handle Combo Config Serialization
        let finalDescription = newItem.description;
        if (isCombo) {
            if (comboGroups.length === 0) {
                toast.error('Please add at least one item group for the combo');
                return;
            }

            const config = {
                groups: comboGroups,
                ...(fixedComboItems.length > 0 && { fixed_items: fixedComboItems })
            };
            // Append config to description with delimiter
            finalDescription = `${newItem.description}|||COMBO_CONFIG|||${JSON.stringify(config)}`;
        }

        // Convert sizes to the format expected by the API
        const itemData = {
            name: newItem.name,
            name_ar: newItem.name_ar || null,
            description: finalDescription,
            description_ar: newItem.description_ar || null,
            image: newItem.image,
            category_id: newItem.category_id,
            available: newItem.available,
            sizes: validSizes.map(size => ({
                size: size.size,
                price: parseFloat(size.price),
                available: size.available
            }))
        };

        onAdd(itemData);

        // Reset form
        setNewItem({
            name: '',
            description: '',
            image: '',
            category_id: '',
            available: true,
            singlePrice: false,
            sizes: [
                { size: 'small', price: '', available: true },
                { size: 'medium', price: '', available: true },
                { size: 'large', price: '', available: true }
            ]
        });
        setIsCombo(false);
        setComboGroups([]);
        setFixedComboItems([]);
    };

    const updateSize = (index, field, value) => {
        const updatedSizes = [...newItem.sizes];
        updatedSizes[index] = { ...updatedSizes[index], [field]: value };
        setNewItem({ ...newItem, sizes: updatedSizes });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-800">Add New Menu Item</h2>
                        <Button onClick={onClose} variant="outline" size="sm">×</Button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="name">Item Name *</Label>
                                <Input
                                    id="name"
                                    value={newItem.name}
                                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                                    placeholder="e.g., Family Feast"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="category">Category *</Label>
                                <select
                                    id="category"
                                    value={newItem.category_id}
                                    onChange={(e) => setNewItem({ ...newItem, category_id: e.target.value })}
                                    className="w-full p-2 border rounded-md"
                                    required
                                >
                                    <option value="">Select Category</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="description">Description *</Label>
                            <Textarea
                                id="description"
                                value={newItem.description}
                                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                                placeholder="Describe the item..."
                                rows={3}
                                required
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="name_ar">Arabic Name (اسم عربي)</Label>
                                <Input
                                    id="name_ar"
                                    dir="rtl"
                                    value={newItem.name_ar}
                                    onChange={(e) => setNewItem({ ...newItem, name_ar: e.target.value })}
                                    placeholder="مثال: بيتزا مارجريتا"
                                />
                            </div>
                            <div>
                                <Label htmlFor="description_ar">Arabic Description (وصف عربي)</Label>
                                <Textarea
                                    id="description_ar"
                                    dir="rtl"
                                    value={newItem.description_ar}
                                    onChange={(e) => setNewItem({ ...newItem, description_ar: e.target.value })}
                                    placeholder="وصف العنصر بالعربية..."
                                    rows={3}
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="image">Image URL *</Label>
                            <Input
                                id="image"
                                value={newItem.image}
                                onChange={(e) => setNewItem({ ...newItem, image: e.target.value })}
                                placeholder="https://example.com/image.jpg"
                                required
                            />
                        </div>

                        {/* Combo Toggle */}
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="is-combo"
                                    checked={isCombo}
                                    onChange={(e) => {
                                        setIsCombo(e.target.checked);
                                    }}
                                    className="rounded w-4 h-4 text-yellow-600"
                                />
                                <Label htmlFor="is-combo" className="text-lg font-semibold text-gray-800">Is this a Combo Deal?</Label>
                            </div>

                            {isCombo && (
                                <div className="mt-4 space-y-4">
                                    <div className="flex justify-between items-center">
                                        <h3 className="font-semibold text-gray-700">Combo Groups</h3>
                                        <Button type="button" onClick={addComboGroup} size="sm" variant="outline" className="text-sm">
                                            + Add Group
                                        </Button>
                                    </div>

                                    {comboGroups.map((group, index) => (
                                        <div key={group.id} className="bg-white p-3 border rounded-md shadow-sm space-y-3">
                                            <div className="flex justify-between">
                                                <h4 className="text-sm font-bold text-gray-500">Group {index + 1}</h4>
                                                <button type="button" onClick={() => removeComboGroup(index)} className="text-red-500 text-xs">Remove</button>
                                            </div>

                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <Label className="text-xs">Group Title</Label>
                                                    <Input
                                                        value={group.title}
                                                        onChange={(e) => updateComboGroup(index, 'title', e.target.value)}
                                                        placeholder="e.g. Select 2 Pizzas"
                                                        className="h-8 text-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="text-xs">Quantity</Label>
                                                    <Input
                                                        type="number"
                                                        value={group.quantity}
                                                        onChange={(e) => updateComboGroup(index, 'quantity', parseInt(e.target.value) || 1)}
                                                        className="h-8 text-sm"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <Label className="text-xs">Selection Type</Label>
                                                <select
                                                    value={group.type}
                                                    onChange={(e) => updateComboGroup(index, 'type', e.target.value)}
                                                    className="w-full p-1 border rounded text-sm"
                                                >
                                                    <option value="category">From Category</option>
                                                    <option value="manual">Manual Selection</option>
                                                </select>
                                            </div>

                                            {group.type === 'category' ? (
                                                <div>
                                                    <Label className="text-xs">Allowed Category</Label>
                                                    <select
                                                        value={group.category_id}
                                                        onChange={(e) => updateComboGroup(index, 'category_id', e.target.value)}
                                                        className="w-full p-1 border rounded text-sm"
                                                    >
                                                        <option value="">Select Category</option>
                                                        {categories.map(cat => (
                                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    <div>
                                                        <Label className="text-xs">Select Allowed Menu Items</Label>
                                                        <div className="max-h-32 overflow-y-auto border rounded p-2 text-xs">
                                                            {menuItems.map(item => (
                                                                <div key={item.id} className="flex items-center space-x-2 mb-1">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={(group.allowed_items || []).includes(item.id)}
                                                                        onChange={() => toggleAllowedItem(index, item.id)}
                                                                    />
                                                                    <span className="truncate">{item.name}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <Label className="text-xs">Add Custom Options (Manual Entry)</Label>
                                                        <div className="flex space-x-1 mb-2">
                                                            <Input
                                                                id={`custom-item-input-${index}`}
                                                                placeholder="e.g. Garlic Dip"
                                                                className="h-7 text-xs"
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        e.preventDefault();
                                                                        addCustomItem(index, e.target.value);
                                                                        e.target.value = '';
                                                                    }
                                                                }}
                                                            />
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant="outline"
                                                                className="h-7 px-2 text-xs"
                                                                onClick={() => {
                                                                    const input = document.getElementById(`custom-item-input-${index}`);
                                                                    if (input) {
                                                                        addCustomItem(index, input.value);
                                                                        input.value = '';
                                                                    }
                                                                }}
                                                            >
                                                                Add
                                                            </Button>
                                                        </div>
                                                        <div className="flex flex-wrap gap-1">
                                                            {(group.custom_items || []).map((item, idx) => (
                                                                <div key={idx} className="bg-gray-100 px-2 py-1 rounded text-xs flex items-center">
                                                                    <span>{item}</span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => removeCustomItem(index, item)}
                                                                        className="ml-1 text-red-500 font-bold hover:text-red-700"
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    {/* FIXED COMBO ITEMS SECTION */}
                                    <div className="mt-6 border-t pt-4">
                                        <div className="flex justify-between items-center mb-3">
                                            <h3 className="font-semibold text-gray-700">Fixed Items (Included Automatically)</h3>
                                        </div>
                                        <div className="bg-white p-4 border rounded-md shadow-sm">
                                            <Label className="text-sm font-medium mb-2 block">Select Automatically Included Menu Items</Label>
                                            <p className="text-xs text-gray-500 mb-3">These items will be displayed to the user at the final step of the combo builder, including their image.</p>
                                            <div className="max-h-40 overflow-y-auto border rounded p-2 text-sm bg-gray-50">
                                                {menuItems.map(item => (
                                                    <div key={`fixed-${item.id}`} className="flex items-center space-x-3 mb-2 hover:bg-gray-100 p-1.5 rounded transition-colors cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={(fixedComboItems || []).includes(item.name)}
                                                            onChange={() => {
                                                                setFixedComboItems(prev => prev.includes(item.name)
                                                                    ? prev.filter(name => name !== item.name)
                                                                    : [...prev, item.name]
                                                                )
                                                            }}
                                                            className="w-4 h-4 rounded text-red-600 focus:ring-red-500 cursor-pointer"
                                                        />
                                                        <span className="truncate font-medium text-gray-700 cursor-pointer" onClick={() => {
                                                            setFixedComboItems(prev => prev.includes(item.name)
                                                                ? prev.filter(name => name !== item.name)
                                                                : [...prev, item.name]
                                                            )
                                                        }}>{item.name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            {fixedComboItems.length > 0 && (
                                                <div className="mt-3 pt-3 border-t">
                                                    <Label className="text-xs text-gray-500 mb-2 block font-bold uppercase tracking-wider">Selected Fixed Items:</Label>
                                                    <div className="flex flex-wrap gap-2">
                                                        {fixedComboItems.map((name, idx) => (
                                                            <div key={idx} className="bg-green-100 text-green-800 px-2 py-1 rounded-md text-sm flex items-center shadow-sm font-semibold border border-green-200">
                                                                <span>{name}</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setFixedComboItems(prev => prev.filter(n => n !== name))}
                                                                    className="ml-2 bg-green-200 text-green-700 hover:bg-red-500 hover:text-white rounded-full w-5 h-5 flex items-center justify-center transition-colors focus:outline-none"
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                </div>
                            )}
                        </div>

                        {/* Size Management */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <Label className="text-base font-medium">Price Settings *</Label>
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="single-price"
                                        checked={newItem.singlePrice}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setNewItem({
                                                    ...newItem,
                                                    singlePrice: true,
                                                    sizes: [{ size: 'medium', price: '', available: true }]
                                                });
                                            } else {
                                                setNewItem({
                                                    ...newItem,
                                                    singlePrice: false,
                                                    sizes: [
                                                        { size: 'small', price: '', available: true },
                                                        { size: 'medium', price: '', available: true },
                                                        { size: 'large', price: '', available: true }
                                                    ]
                                                });
                                            }
                                        }}
                                        className="rounded"
                                    />
                                    <Label htmlFor="single-price" className="text-sm">Single Price</Label>
                                </div>
                            </div>

                            <div className="space-y-3">
                                {newItem.sizes.map((size, index) => (
                                    <div key={index} className="flex items-center space-x-3 p-3 border rounded-lg">
                                        {!newItem.singlePrice && (
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    checked={size.available}
                                                    onChange={(e) => updateSize(index, 'available', e.target.checked)}
                                                    className="rounded"
                                                />
                                                <Label className="font-medium capitalize">{size.size}</Label>
                                            </div>
                                        )}
                                        <div className="flex-1">
                                            <Input
                                                type="number"
                                                step="0.001"
                                                min="0"
                                                value={size.price}
                                                onChange={(e) => updateSize(index, 'price', e.target.value)}
                                                placeholder="0.000"
                                                className="w-full"
                                            />
                                        </div>
                                        <span className="text-sm text-gray-500">OMR</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="available"
                                checked={newItem.available}
                                onChange={(e) => setNewItem({ ...newItem, available: e.target.checked })}
                                className="rounded"
                            />
                            <Label htmlFor="available">Available for ordering</Label>
                        </div>

                        <div className="flex space-x-2 pt-4">
                            <Button type="submit" className="bg-green-600 hover:bg-green-700 flex-1">
                                <Plus className="h-4 w-4 mr-2" />
                                Add Menu Item
                            </Button>
                            <Button onClick={onClose} variant="outline" className="flex-1">
                                Cancel
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// Admin Dashboard Component
const AdminDashboard = ({ token }) => {
    const [activeTab, setActiveTab] = useState('orders');
    const [orders, setOrders] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [gameStats, setGameStats] = useState({});
    const [orderStats, setOrderStats] = useState({});
    const [loading, setLoading] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [showAddItem, setShowAddItem] = useState(false);
    const [newItem, setNewItem] = useState({
        name: '',
        name_ar: '',
        description: '',
        description_ar: '',
        image: '',
        category_id: '',
        available: true,
        sizes: [
            { size: 'small', price: '', available: true },
            { size: 'medium', price: '', available: true },
            { size: 'large', price: '', available: true }
        ]
    });
    const [adminCategories, setAdminCategories] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [bulkAction, setBulkAction] = useState('');
    const [categoryStats, setCategoryStats] = useState([]);
    const [orderFilter, setOrderFilter] = useState('all');
    const [orderSearch, setOrderSearch] = useState('');
    const [menuSearch, setMenuSearch] = useState('');
    const [menuFilter, setMenuFilter] = useState('all');
    const [showExportModal, setShowExportModal] = useState(false);
    const [duplicatingItem, setDuplicatingItem] = useState(null);
    const [showAddQuestion, setShowAddQuestion] = useState(false);
    const [showQuestionsList, setShowQuestionsList] = useState(false);
    const [showCouponsList, setShowCouponsList] = useState(false);
    const [gameQuestions, setGameQuestions] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [editingCoupon, setEditingCoupon] = useState(null);
    const [newQuestion, setNewQuestion] = useState({
        question: '',
        question_ar: '',
        image_url: '',
        options: ['', '', '', ''],
        options_ar: ['', '', '', ''],
        correct_answer: 0,
        difficulty: 'easy'
    });
    const [newCoupon, setNewCoupon] = useState({
        code: '',
        discount_percentage: 10,
        max_uses: 100,
        active: true,
        expiry_days: 30
    });
    const [showAddCoupon, setShowAddCoupon] = useState(false);

    // Spin Coupon Management - REMOVED (using fixed arrays)

    // Notification Management
    const [showNotificationRecipients, setShowNotificationRecipients] = useState(false);
    const [showAddRecipient, setShowAddRecipient] = useState(false);
    const [editingRecipient, setEditingRecipient] = useState(null);
    const [notificationRecipients, setNotificationRecipients] = useState([]);
    const [newRecipient, setNewRecipient] = useState({
        name: '',
        email: '',
        phone: '',
        notification_type: 'admin',
        active: true
    });
    const [notificationSettings, setNotificationSettings] = useState({});
    const [showNotificationSettings, setShowNotificationSettings] = useState(false);

    // Category Management
    const [showCategoryManagement, setShowCategoryManagement] = useState(false);
    const [showAddCategory, setShowAddCategory] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [newCategory, setNewCategory] = useState({
        name: '',
        name_ar: '',
        icon: '🍽️'
    });

    // Add these state variables in the AdminDashboard component
    const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
    const [showDatePicker, setShowDatePicker] = useState(false);

    const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };

    useEffect(() => {
        if (activeTab === 'orders') fetchOrders();
        if (activeTab === 'menu') {
            fetchMenuItems();
            fetchCategories();
            fetchCategoryStats();
        }
        if (activeTab === 'game') {
            fetchGameStats();
            fetchGameQuestions();
            fetchCoupons();
            // Spin coupons are now fixed arrays - no need to fetch
        }
        if (activeTab === 'notifications') {
            fetchNotificationRecipients();
            fetchNotificationSettings();
        }
        if (activeTab === 'stats') calculateOrderStats();
    }, [activeTab]);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/admin/orders`, { headers });
            setOrders(response.data.orders);
        } catch (error) {
            toast.error('Failed to fetch orders');
        }
        setLoading(false);
    };

    const fetchMenuItems = async () => {
        setLoading(true);
        try {
            console.log('Fetching admin menu from:', `${API_BASE_URL}/api/admin/menu`);
            const response = await axios.get(`${API_BASE_URL}/api/admin/menu`, { headers });
            console.log('Admin menu response:', response.data);
            setMenuItems(response.data.menu_items);
        } catch (error) {
            console.error('Error fetching admin menu:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                url: error.config?.url
            });
            toast.error('Failed to fetch menu items');
        }
        setLoading(false);
    };

    const fetchCategories = async () => {
        try {
            console.log('Admin - Fetching categories from:', `${API_BASE_URL}/api/categories`);
            const response = await axios.get(`${API_BASE_URL}/api/categories`, { headers });
            console.log('Admin - Categories response:', response.data);
            // Store categories with both id and name for proper handling
            setAdminCategories(response.data);
        } catch (error) {
            console.error('Admin - Error fetching categories:', error);
            // Set default categories if API fails
            setAdminCategories([]);
        }
    };

    const fetchCategoryStats = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/admin/menu/categories/manage`, { headers });
            setCategoryStats(response.data.categories);
        } catch (error) {
            console.error('Error fetching category stats:', error);
        }
    };

    const fetchGameStats = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/admin/game/stats`, { headers });
            setGameStats(response.data);
        } catch (error) {
            toast.error('Failed to fetch game stats');
        }
        setLoading(false);
    };

    const fetchGameQuestions = async () => {
        try {
            console.log('Fetching game questions from:', `${API_BASE_URL}/api/admin/game/questions`);
            const response = await axios.get(`${API_BASE_URL}/api/admin/game/questions`, { headers });
            console.log('Game questions response:', response.data);
            setGameQuestions(response.data.questions);
            console.log('Set game questions:', response.data.questions);
        } catch (error) {
            console.error('Error fetching game questions:', error);
            toast.error('Failed to fetch game questions');
        }
    };

    const fetchCoupons = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/admin/coupons`, { headers });
            setCoupons(response.data.coupons);
        } catch (error) {
            toast.error('Failed to fetch coupons');
        }
    };

    const createGameQuestion = async (questionData) => {
        try {
            await axios.post(`${API_BASE_URL}/api/admin/game/questions`, questionData, { headers });
            toast.success('Game question created successfully');
            setShowAddQuestion(false);
            setNewQuestion({
                question: '',
                question_ar: '',
                image_url: '',
                options: ['', '', '', ''],
                options_ar: ['', '', '', ''],
                correct_answer: 0,
                difficulty: 'easy'
            });
            fetchGameQuestions();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create game question');
        }
    };

    const updateGameQuestion = async (questionId, questionData) => {
        try {
            await axios.put(`${API_BASE_URL}/api/admin/game/questions/${questionId}`, questionData, { headers });
            toast.success('Game question updated successfully');
            setEditingQuestion(null);
            fetchGameQuestions();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update game question');
        }
    };

    const deleteGameQuestion = async (questionId) => {
        if (!window.confirm('Are you sure you want to delete this question?')) return;

        try {
            await axios.delete(`${API_BASE_URL}/api/admin/game/questions/${questionId}`, { headers });
            toast.success('Game question deleted successfully');
            fetchGameQuestions();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete game question');
        }
    };

    const createCoupon = async (couponData) => {
        try {
            await axios.post(`${API_BASE_URL}/api/admin/coupons`, couponData, { headers });
            toast.success('Coupon created successfully');
            setShowAddCoupon(false);
            setNewCoupon({
                code: '',
                discount_percentage: 10,
                max_uses: 100,
                active: true,
                expiry_days: 30
            });
            fetchCoupons();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create coupon');
        }
    };

    const updateCoupon = async (couponId, couponData) => {
        try {
            await axios.put(`${API_BASE_URL}/api/admin/coupons/${couponId}`, couponData, { headers });
            toast.success('Coupon updated successfully');
            setEditingCoupon(null);
            fetchCoupons();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update coupon');
        }
    };

    const deleteCoupon = async (couponId) => {
        if (!window.confirm('Are you sure you want to delete this coupon?')) return;

        try {
            await axios.delete(`${API_BASE_URL}/api/admin/coupons/${couponId}`, { headers });
            toast.success('Coupon deleted successfully');
            fetchCoupons();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete coupon');
        }
    };

    // Spin Coupon Functions - REMOVED (using fixed arrays)

    // Notification Management Functions
    const fetchNotificationRecipients = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/admin/notification-recipients`, { headers });
            setNotificationRecipients(response.data.recipients);
        } catch (error) {
            toast.error('Failed to fetch notification recipients');
        }
    };

    const fetchNotificationSettings = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/admin/notification-settings`, { headers });
            setNotificationSettings(response.data.settings);
        } catch (error) {
            toast.error('Failed to fetch notification settings');
            // Set default settings if fetch fails
            setNotificationSettings({
                email_enabled: 'true',
                sms_enabled: 'true'
            });
        }
    };

    const createNotificationRecipient = async (recipientData) => {
        try {
            await axios.post(`${API_BASE_URL}/api/admin/notification-recipients`, recipientData, { headers });
            toast.success('Notification recipient created successfully');
            setShowAddRecipient(false);
            setNewRecipient({
                name: '',
                email: '',
                phone: '',
                notification_type: 'admin',
                active: true
            });
            fetchNotificationRecipients();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create notification recipient');
        }
    };

    const updateNotificationRecipient = async (recipientId, recipientData) => {
        try {
            await axios.put(`${API_BASE_URL}/api/admin/notification-recipients/${recipientId}`, recipientData, { headers });
            toast.success('Notification recipient updated successfully');
            setEditingRecipient(null);
            fetchNotificationRecipients();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update notification recipient');
        }
    };

    const deleteNotificationRecipient = async (recipientId) => {
        if (!window.confirm('Are you sure you want to delete this notification recipient?')) return;

        try {
            await axios.delete(`${API_BASE_URL}/api/admin/notification-recipients/${recipientId}`, { headers });
            toast.success('Notification recipient deleted successfully');
            fetchNotificationRecipients();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete notification recipient');
        }
    };

    const updateNotificationSettings = async (settingsData) => {
        try {
            await axios.put(`${API_BASE_URL}/api/admin/notification-settings`, settingsData, { headers });
            toast.success('Notification settings updated successfully');
            fetchNotificationSettings();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update notification settings');
        }
    };

    // Category Management Functions
    const createCategory = async (categoryData) => {
        try {
            await axios.post(`${API_BASE_URL}/api/admin/categories`, categoryData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            toast.success('Category created successfully');
            setShowAddCategory(false);
            setNewCategory({ name: '', name_ar: '', icon: '🍽️' });
            fetchCategories();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create category');
        }
    };

    const updateCategory = async (categoryId, categoryData) => {
        try {
            await axios.put(`${API_BASE_URL}/api/admin/categories/${categoryId}`, categoryData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            toast.success('Category updated successfully');
            setEditingCategory(null);
            fetchCategories();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update category');
        }
    };

    const deleteCategory = async (categoryId) => {
        if (!window.confirm('Are you sure you want to delete this category? This will affect all items in this category.')) return;

        try {
            await axios.delete(`${API_BASE_URL}/api/admin/categories/${categoryId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            toast.success('Category deleted successfully');
            fetchCategories();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete category');
        }
    };

    const handleSettingChange = (settingKey, value) => {
        const newSettings = { ...notificationSettings };
        newSettings[settingKey] = value ? 'true' : 'false';
        setNotificationSettings(newSettings);
    };

    const calculateOrderStats = () => {
        const stats = {
            total: orders.length,
            pending: orders.filter(o => o.status === 'pending').length,
            accepted: orders.filter(o => o.status === 'accepted').length,
            rejected: orders.filter(o => o.status === 'rejected').length,
            totalRevenue: orders.reduce((sum, o) => sum + o.final_total, 0),
            avgOrderValue: orders.length > 0 ? (orders.reduce((sum, o) => sum + o.final_total, 0) / orders.length) : 0
        };
        setOrderStats(stats);
    };

    const updateOrderStatus = async (orderId, status) => {
        try {
            await axios.put(`${API_BASE_URL}/api/admin/orders/${orderId}/status`,
                { status }, { headers });
            toast.success('Order status updated');
            fetchOrders();
        } catch (error) {
            toast.error('Failed to update order status');
        }
    };

    const addMenuItem = async (itemData) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/admin/menu`, itemData, { headers });
            toast.success('Menu item added successfully');
            setShowAddItem(false);
            fetchMenuItems();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add menu item');
        }
    };

    const updateMenuItem = async (itemId, updatedData) => {
        try {
            await axios.put(`${API_BASE_URL}/api/admin/menu/${itemId}`, updatedData, { headers });
            toast.success('Menu item updated successfully');
            setEditingItem(null);
            fetchMenuItems();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update menu item');
        }
    };

    const updateMenuItemPrice = async (itemId, newPrice) => {
        try {
            if (isNaN(newPrice) || parseFloat(newPrice) <= 0) {
                toast.error('Price must be a positive number');
                return;
            }
            await axios.patch(`${API_BASE_URL}/api/admin/menu/${itemId}/price`,
                { price: parseFloat(newPrice) }, { headers });
            toast.success('Price updated');
            fetchMenuItems();
            setEditingItem(null);
        } catch (error) {
            toast.error('Failed to update price');
        }
    };

    const toggleItemAvailability = async (itemId, available) => {
        try {
            await axios.patch(`${API_BASE_URL}/api/admin/menu/${itemId}/availability`,
                { available }, { headers });
            toast.success(available ? 'Item marked as available' : 'Item marked as sold out');
            fetchMenuItems();
        } catch (error) {
            toast.error('Failed to update availability');
        }
    };

    const deleteMenuItem = async (itemId) => {
        if (!window.confirm('Are you sure you want to delete this item?')) return;

        try {
            await axios.delete(`${API_BASE_URL}/api/admin/menu/${itemId}`, { headers });
            toast.success('Menu item deleted');
            fetchMenuItems();
        } catch (error) {
            const errorMsg = error.response?.data?.detail || 'Failed to delete menu item';
            toast.error(errorMsg);
        }
    };

    const logout = () => {
        localStorage.removeItem('admin_token');
        window.location.reload();
    };

    const handleEditItem = (item) => {
        setEditingItem({
            ...item,
            // Convert sizes to the format expected by the edit form
            sizes: item.sizes || [
                { size: 'small', price: '', available: true },
                { size: 'medium', price: '', available: true },
                { size: 'large', price: '', available: true }
            ],
            // Use category ID for proper editing
            category_id: item.category?.id || item.category_id || ''
        });
    };

    const handleSaveEdit = async () => {
        if (!editingItem.name || !editingItem.description || !editingItem.image || !editingItem.category_id) {
            toast.error('Please fill in all required fields');
            return;
        }

        // Validate sizes
        const validSizes = editingItem.sizes.filter(size => size.price && !isNaN(size.price) && parseFloat(size.price) > 0);
        if (validSizes.length === 0) {
            toast.error('Please enter at least one valid price for sizes');
            return;
        }

        const updatedData = {
            name: editingItem.name,
            name_ar: editingItem.name_ar || null,
            description: editingItem.description,
            description_ar: editingItem.description_ar || null,
            image: editingItem.image,
            category_id: editingItem.category_id,
            available: editingItem.available,
            sizes: editingItem.sizes.map(size => ({
                size: size.size,
                price: parseFloat(size.price) || 0,
                available: size.available
            }))
        };

        await updateMenuItem(editingItem.id, updatedData);
    };

    const bulkUpdateItems = async () => {
        if (selectedItems.length === 0) {
            toast.error('Please select items to update');
            return;
        }

        if (!bulkAction) {
            toast.error('Please select an action');
            return;
        }

        try {
            const updates = selectedItems.map(itemId => ({
                id: itemId,
                available: bulkAction === 'enable' ? true : false
            }));

            await axios.post(`${API_BASE_URL}/api/admin/menu/bulk-update`, updates, { headers });
            toast.success(`Successfully ${bulkAction === 'enable' ? 'enabled' : 'disabled'} ${selectedItems.length} items`);
            setSelectedItems([]);
            setBulkAction('');
            fetchMenuItems();
        } catch (error) {
            toast.error('Failed to bulk update items');
        }
    };

    const toggleItemSelection = (itemId) => {
        setSelectedItems(prev =>
            prev.includes(itemId)
                ? prev.filter(id => id !== itemId)
                : [...prev, itemId]
        );
    };

    const selectAllItems = () => {
        if (selectedItems.length === filteredMenuItems.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems(filteredMenuItems.map(item => item.id));
        }
    };

    // Add the filteredMenuItems calculation
    const filteredMenuItems = menuItems.filter(item => {
        const matchesFilter = menuFilter === 'all' ||
            (menuFilter === 'available' && item.available) ||
            (menuFilter === 'unavailable' && !item.available) ||
            (item.category && item.category.name && item.category.name === menuFilter);

        const matchesSearch = menuSearch === '' ||
            item.name.toLowerCase().includes(menuSearch.toLowerCase()) ||
            item.description.toLowerCase().includes(menuSearch.toLowerCase()) ||
            (item.category && item.category.name && item.category.name.toLowerCase().includes(menuSearch.toLowerCase()));

        return matchesFilter && matchesSearch;
    });

    // Keep only this filteredOrders calculation (remove the duplicate)
    const filteredOrders = orders.filter(order => {
        const matchesFilter = orderFilter === 'all' || order.status === orderFilter;
        const matchesSearch = orderSearch === '' ||
            order.customer_info.name.toLowerCase().includes(orderSearch.toLowerCase()) ||
            order.customer_info.phone.includes(orderSearch) ||
            order.id.toLowerCase().includes(orderSearch.toLowerCase());

        // Date range filtering
        const orderDate = new Date(order.created_at);
        const matchesDateRange = !dateRange.startDate && !dateRange.endDate ||
            (dateRange.startDate && dateRange.endDate &&
                orderDate >= dateRange.startDate && orderDate <= dateRange.endDate);

        return matchesFilter && matchesSearch && matchesDateRange;
    });

    // Create CSV content with better formatting and null safety
    const exportOrders = () => {
        // Create CSV content with better formatting and null safety
        const csvContent = [
            ['Created Date', 'Order ID', 'Customer Name', 'Phone', 'Email', 'Delivery Type', 'Address', 'Items', 'Subtotal (OMR)', 'Delivery Charge (OMR)', 'Total (OMR)', 'Status'],
            ...filteredOrders.map(order => [
                order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A',
                order.id || 'N/A',
                order.customer_info?.name || 'N/A',
                order.customer_info?.phone || 'N/A',
                order.customer_info?.email || 'N/A',
                order.delivery_type || 'N/A',
                order.delivery_address || 'N/A',
                order.items?.map(item => `${item.name}${item.size ? ` [${item.size}]` : ''} (${item.quantity}x)`).join('; ') || 'N/A',
                (order.subtotal || 0).toFixed(3),
                (order.delivery_charge || 0).toFixed(3),
                (order.total || 0).toFixed(3),
                order.status || 'N/A'
            ])
        ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `wow-pizza-orders-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        setShowExportModal(false);
        toast.success('Orders exported as CSV successfully!');
    };

    const duplicateMenuItem = async (item) => {
        try {
            const duplicatedItem = {
                name: `${item.name} (Copy)`,
                description: item.description,
                price: item.price,
                image: item.image,
                category: item.category,
                available: false // Start as unavailable for review
            };

            const response = await axios.post(`${API_BASE_URL}/api/admin/menu`, duplicatedItem, { headers });
            toast.success('Menu item duplicated successfully');
            fetchMenuItems();
            setDuplicatingItem(null);
        } catch (error) {
            toast.error('Failed to duplicate menu item');
        }
    };

    // Update the exportOrdersPDF function in AdminDashboard
    const exportOrdersPDF = () => {
        // Create a new PDF document
        const doc = new jsPDF();

        // Set title
        doc.setFontSize(20);
        doc.text('WOW! PIZZA - Orders Report', 20, 20);

        // Set subtitle
        doc.setFontSize(12);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 20, 30);

        // Add some spacing
        let yPosition = 45;

        // Add orders
        filteredOrders.forEach((order, index) => {
            // Check if we need a new page
            if (yPosition > 250) {
                doc.addPage();
                yPosition = 20;
            }

            // Order header
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text(`Order #${index + 1}`, 20, yPosition);
            yPosition += 8;

            // Order details
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.text(`Order ID: ${order.id}`, 20, yPosition);
            yPosition += 5;
            doc.text(`Customer: ${order.customer_info.name}`, 20, yPosition);
            yPosition += 5;
            doc.text(`Phone: ${order.customer_info.phone}`, 20, yPosition);
            yPosition += 5;
            doc.text(`Email: ${order.customer_info.email}`, 20, yPosition);
            yPosition += 5;
            doc.text(`Delivery Type: ${order.delivery_type}`, 20, yPosition);
            yPosition += 5;
            doc.text(`Address: ${order.delivery_address || 'N/A'}`, 20, yPosition);
            yPosition += 5;

            // Items
            doc.text(`Items: ${order.items.map(item => `${item.name}${item.size ? ` [${item.size}]` : ''} (${item.quantity}x)`).join(', ')}`, 20, yPosition);
            yPosition += 5;

            // Financial details
            doc.text(`Subtotal: ${order.subtotal} OMR`, 20, yPosition);
            yPosition += 5;
            doc.text(`Delivery Charge: ${order.delivery_charge} OMR`, 20, yPosition);
            yPosition += 5;
            doc.text(`Total: ${order.total} OMR`, 20, yPosition);
            yPosition += 5;
            doc.text(`Status: ${order.status}`, 20, yPosition);
            yPosition += 5;
            doc.text(`Created: ${new Date(order.created_at).toLocaleString()}`, 20, yPosition);

            // Add spacing between orders
            yPosition += 15;
        });

        // Save the PDF
        doc.save(`wow-pizza-orders-${new Date().toISOString().split('T')[0]}.pdf`);

        setShowExportModal(false);
        toast.success('Orders exported as PDF successfully!');
    };

    // Add date range picker component
    const DateRangePicker = ({ dateRange, setDateRange, onClose }) => {
        const [tempStartDate, setTempStartDate] = useState(dateRange.startDate);
        const [tempEndDate, setTempEndDate] = useState(dateRange.endDate);

        const applyDateRange = () => {
            setDateRange({
                startDate: tempStartDate,
                endDate: tempEndDate
            });
            onClose();
        };

        const clearDateRange = () => {
            setDateRange({ startDate: null, endDate: null });
            setTempStartDate(null);
            setTempEndDate(null);
            onClose();
        };

        return (
            <div className="absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg z-50 p-4 min-w-80">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-800">Select Date Range</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="space-y-4">
                    <div>
                        <Label className="text-sm font-medium text-gray-700">Start Date</Label>
                        <input
                            type="date"
                            value={tempStartDate ? tempStartDate.toISOString().split('T')[0] : ''}
                            onChange={(e) => setTempStartDate(e.target.value ? new Date(e.target.value) : null)}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    <div>
                        <Label className="text-sm font-medium text-gray-700">End Date</Label>
                        <input
                            type="date"
                            value={tempEndDate ? tempEndDate.toISOString().split('T')[0] : ''}
                            onChange={(e) => setTempEndDate(e.target.value ? new Date(e.target.value) : null)}
                            min={tempStartDate ? tempStartDate.toISOString().split('T')[0] : undefined}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    <div className="flex space-x-2 pt-2">
                        <Button onClick={applyDateRange} className="bg-blue-600 hover:bg-blue-700 flex-1">
                            Apply Filter
                        </Button>
                        <Button onClick={clearDateRange} variant="outline" className="flex-1">
                            Clear
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="bg-red-600 text-white p-4">
                <div className="container mx-auto flex items-center justify-between">
                    <h1 className="text-2xl font-bold">Wow Pizza Admin</h1>
                    <Button onClick={logout} variant="outline" className="text-white border-white hover:bg-white hover:text-red-600">
                        Logout
                    </Button>
                </div>
            </div>

            <div className="container mx-auto px-4 py-8">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid w-full grid-cols-5">
                        <TabsTrigger value="orders" className="flex items-center">
                            <Users className="h-4 w-4 mr-2" />
                            Orders
                        </TabsTrigger>
                        <TabsTrigger value="menu" className="flex items-center">
                            <Pizza className="h-4 w-4 mr-2" />
                            Menu
                        </TabsTrigger>
                        <TabsTrigger value="game" className="flex items-center">
                            <Trophy className="h-4 w-4 mr-2" />
                            Game
                        </TabsTrigger>
                        <TabsTrigger value="notifications" className="flex items-center">
                            <Bell className="h-4 w-4 mr-2" />
                            Notifications
                        </TabsTrigger>
                        <TabsTrigger value="stats" className="flex items-center">
                            <BarChart3 className="h-4 w-4 mr-2" />
                            Stats
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="orders" className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Order Management</CardTitle>
                                <CardDescription>View and manage customer orders</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {/* Order Filters */}
                                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                                    <div className="flex flex-col md:flex-row gap-4">
                                        <div className="flex-1">
                                            <Label htmlFor="order-search">Search Orders</Label>
                                            <Input
                                                id="order-search"
                                                placeholder="Search by customer name, phone, or order ID..."
                                                value={orderSearch}
                                                onChange={(e) => setOrderSearch(e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="order-filter">Filter by Status</Label>
                                            <select
                                                id="order-filter"
                                                value={orderFilter}
                                                onChange={(e) => setOrderFilter(e.target.value)}
                                                className="w-full p-2 border rounded-md"
                                            >
                                                <option value="all">All Orders</option>
                                                <option value="pending">Pending</option>
                                                <option value="accepted">Accepted</option>
                                                <option value="rejected">Rejected</option>
                                            </select>
                                        </div>
                                        <div className="relative">
                                            <Label>Date Range</Label>
                                            <Button
                                                onClick={() => setShowDatePicker(!showDatePicker)}
                                                variant="outline"
                                                className="w-full justify-between"
                                            >
                                                <span>
                                                    {dateRange.startDate && dateRange.endDate
                                                        ? `${dateRange.startDate.toLocaleDateString()} - ${dateRange.endDate.toLocaleDateString()}`
                                                        : 'Select date range'
                                                    }
                                                </span>
                                                <Calendar className="h-4 w-4" />
                                            </Button>
                                            {showDatePicker && (
                                                <DateRangePicker
                                                    dateRange={dateRange}
                                                    setDateRange={setDateRange}
                                                    onClose={() => setShowDatePicker(false)}
                                                />
                                            )}
                                        </div>
                                        <div className="flex items-end">
                                            <Button
                                                onClick={() => setShowExportModal(true)}
                                                variant="outline"
                                                className="bg-green-600 text-white hover:bg-green-700 border-green-600"
                                            >
                                                <Download className="h-4 w-4 mr-2" />
                                                Export
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="mt-2 text-sm text-gray-600">
                                        Showing {filteredOrders.length} of {orders.length} orders
                                        {dateRange.startDate && dateRange.endDate && (
                                            <span className="ml-2 text-blue-600">
                                                • Filtered by date range: {dateRange.startDate.toLocaleDateString()} - {dateRange.endDate.toLocaleDateString()}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {loading && orders.length === 0 ? (
                                    <div className="flex justify-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {filteredOrders.map(order => (
                                            <div key={order.id} className="border rounded-lg p-4">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div>
                                                        <h3 className="font-semibold">Order #{order.id.slice(-8)}</h3>
                                                        <p className="text-sm text-gray-600">{order.customer_info.name} - {order.customer_info.phone}</p>
                                                    </div>
                                                    <Badge className={`${order.status === 'pending' ? 'bg-yellow-500' :
                                                        order.status === 'accepted' ? 'bg-green-500' :
                                                            order.status === 'rejected' ? 'bg-red-500' : 'bg-gray-500'
                                                        }`}>
                                                        {order.status}
                                                    </Badge>
                                                </div>

                                                <div className="text-sm text-gray-600 mb-3">
                                                    <p>📧 {order.customer_info.email}</p>
                                                    <p>📍 {order.customer_info.address}</p>
                                                    <p>🚚 Distance: {order.delivery_distance}km | Delivery: OMR {order.delivery_charge.toFixed(3)}</p>
                                                    <p>💰 Subtotal: OMR {order.subtotal.toFixed(3)} | Total: OMR {order.final_total.toFixed(3)}</p>
                                                    <p>💳 Payment: {order.payment_method === 'cash' ? 'Cash on Delivery' :
                                                        order.payment_method === 'debit' ? 'Debit Card' :
                                                            order.payment_method === 'credit' ? 'Credit Card' :
                                                                order.payment_method}</p>
                                                </div>

                                                {/* Order Items Details */}
                                                <div className="bg-gray-50 rounded p-3 mb-3">
                                                    <h4 className="font-semibold text-sm mb-2">Order Items:</h4>
                                                    <div className="space-y-2">
                                                        {order.items && order.items.map((item, index) => (
                                                            <div key={index} className="flex items-center justify-between text-sm">
                                                                <div className="flex items-center space-x-2">
                                                                    <img src={item.image} alt={item.name} className="w-8 h-8 object-cover rounded" />
                                                                    <span>{item.name}</span>
                                                                    {item.size && (
                                                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${item.size === 'heart' && !item.item_total ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
                                                                            {item.size === 'heart' && !item.item_total ? 'FREE' : item.size}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <span>Qty: {item.quantity} × OMR {(item.price || 0).toFixed(3)} = OMR {item.item_total.toFixed(3)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="flex space-x-2">
                                                    <Button
                                                        onClick={() => updateOrderStatus(order.id, 'accepted')}
                                                        size="sm"
                                                        className="bg-green-600 hover:bg-green-700"
                                                        disabled={order.status !== 'pending'}
                                                    >
                                                        <Check className="h-4 w-4 mr-1" />
                                                        Accept
                                                    </Button>
                                                    <Button
                                                        onClick={() => updateOrderStatus(order.id, 'rejected')}
                                                        size="sm"
                                                        variant="destructive"
                                                        disabled={order.status !== 'pending'}
                                                    >
                                                        <X className="h-4 w-4 mr-1" />
                                                        Reject
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                        {filteredOrders.length === 0 && (
                                            <div className="text-center py-8 text-gray-500">
                                                {orders.length === 0 ? 'No orders found' : 'No orders match your current filters'}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="menu" className="mt-6">
                        <Card>
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div>
                                        <CardTitle>Menu Management</CardTitle>
                                        <CardDescription>Manage menu items, prices, and availability</CardDescription>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button onClick={() => setShowCategoryManagement(true)} variant="outline">
                                            <Settings className="h-4 w-4 mr-2" />
                                            Manage Categories
                                        </Button>
                                        <Button onClick={() => setShowAddItem(true)} className="bg-green-600 hover:bg-green-700">
                                            <Plus className="h-4 w-4 mr-2" />
                                            Add New Item
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {/* Category Statistics */}
                                {categoryStats.length > 0 && (
                                    <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                                        <h3 className="font-semibold text-blue-800 mb-3">Category Overview</h3>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            {categoryStats.map((cat, index) => (
                                                <div key={index} className="bg-white p-3 rounded-lg border">
                                                    <h4 className="font-semibold text-blue-900 capitalize">{cat.name}</h4>
                                                    <div className="text-sm text-blue-700">
                                                        <p>Total: {cat.total_items}</p>
                                                        <p className="text-green-600">Available: {cat.available_items}</p>
                                                        <p className="text-red-600">Unavailable: {cat.unavailable_items}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Menu Filters */}
                                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                                    <div className="flex flex-col md:flex-row gap-4">
                                        <div className="flex-1">
                                            <Label htmlFor="menu-search">Search Menu Items</Label>
                                            <Input
                                                id="menu-search"
                                                placeholder="Search by name, description, or category..."
                                                value={menuSearch}
                                                onChange={(e) => setMenuSearch(e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="menu-filter">Filter by Status/Category</Label>
                                            <select
                                                id="menu-filter"
                                                value={menuFilter}
                                                onChange={(e) => setMenuFilter(e.target.value)}
                                                className="w-full p-2 border rounded-md"
                                            >
                                                <option value="all">All Items</option>
                                                <option value="available">Available Only</option>
                                                <option value="unavailable">Unavailable Only</option>
                                                {adminCategories.map(cat => (
                                                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="mt-2 text-sm text-gray-600">
                                        Showing {filteredMenuItems.length} of {menuItems.length} menu items
                                    </div>
                                </div>

                                {/* Bulk Operations */}
                                {filteredMenuItems.length > 0 && (
                                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="font-semibold">Bulk Operations</h3>
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedItems.length === filteredMenuItems.length}
                                                    onChange={selectAllItems}
                                                    className="rounded"
                                                />
                                                <span className="text-sm text-gray-600">
                                                    Select All ({selectedItems.length}/{filteredMenuItems.length})
                                                </span>
                                            </div>
                                        </div>

                                        {selectedItems.length > 0 && (
                                            <div className="flex items-center space-x-4">
                                                <select
                                                    value={bulkAction}
                                                    onChange={(e) => setBulkAction(e.target.value)}
                                                    className="p-2 border rounded-md"
                                                >
                                                    <option value="">Select Action</option>
                                                    <option value="enable">Enable Selected Items</option>
                                                    <option value="disable">Disable Selected Items</option>
                                                </select>
                                                <Button
                                                    onClick={bulkUpdateItems}
                                                    disabled={!bulkAction}
                                                    size="sm"
                                                    className="bg-blue-600 hover:bg-blue-700"
                                                >
                                                    Apply to {selectedItems.length} Items
                                                </Button>
                                                <Button
                                                    onClick={() => setSelectedItems([])}
                                                    variant="outline"
                                                    size="sm"
                                                >
                                                    Clear Selection
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {loading && menuItems.length === 0 ? (
                                    <div className="flex justify-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {filteredMenuItems.map(item => (
                                            <div key={item.id} className="border rounded-lg p-4 overflow-hidden">
                                                {/* Item Selection Checkbox */}
                                                <div className="flex items-center space-x-2 mb-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedItems.includes(item.id)}
                                                        onChange={() => toggleItemSelection(item.id)}
                                                        className="rounded"
                                                    />
                                                    <span className="text-sm text-gray-500">Select for bulk operations</span>
                                                </div>

                                                {editingItem && editingItem.id === item.id ? (
                                                    // Edit Mode
                                                    <div className="space-y-4">
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <div>
                                                                <Label>Name</Label>
                                                                <Input
                                                                    value={editingItem.name}
                                                                    onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                                                                    placeholder="Item name"
                                                                />
                                                            </div>
                                                            <div>
                                                                <Label>Category</Label>
                                                                <select
                                                                    value={editingItem.category_id}
                                                                    onChange={(e) => setEditingItem({ ...editingItem, category_id: e.target.value })}
                                                                    className="w-full p-2 border rounded-md"
                                                                >
                                                                    <option value="">Select Category</option>
                                                                    {adminCategories.map(cat => (
                                                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <Label>Description</Label>
                                                            <Textarea
                                                                value={editingItem.description}
                                                                onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                                                                placeholder="Item description"
                                                                rows={3}
                                                            />
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <div>
                                                                <Label>Arabic Name (اسم عربي)</Label>
                                                                <Input
                                                                    dir="rtl"
                                                                    value={editingItem.name_ar || ''}
                                                                    onChange={(e) => setEditingItem({ ...editingItem, name_ar: e.target.value })}
                                                                    placeholder="مثال: بيتزا مارجريتا"
                                                                />
                                                            </div>
                                                            <div>
                                                                <Label>Arabic Description (وصف عربي)</Label>
                                                                <Textarea
                                                                    dir="rtl"
                                                                    value={editingItem.description_ar || ''}
                                                                    onChange={(e) => setEditingItem({ ...editingItem, description_ar: e.target.value })}
                                                                    placeholder="وصف العنصر بالعربية..."
                                                                    rows={3}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <Label>Image URL</Label>
                                                            <Input
                                                                value={editingItem.image}
                                                                onChange={(e) => setEditingItem({ ...editingItem, image: e.target.value })}
                                                                placeholder="https://example.com/image.jpg"
                                                            />
                                                        </div>

                                                        {/* Size Management in Edit Mode */}
                                                        <div>
                                                            <Label className="text-base font-medium">Item Sizes and Prices</Label>
                                                            <p className="text-sm text-gray-600 mb-3">Configure sizes and prices for this item</p>
                                                            <div className="space-y-3">
                                                                {editingItem.sizes.map((size, index) => (
                                                                    <div key={index} className="flex items-center space-x-3 p-3 border rounded-lg">
                                                                        <div className="flex items-center space-x-2">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={size.available}
                                                                                onChange={(e) => {
                                                                                    const updatedSizes = [...editingItem.sizes];
                                                                                    updatedSizes[index] = { ...updatedSizes[index], available: e.target.checked };
                                                                                    setEditingItem({ ...editingItem, sizes: updatedSizes });
                                                                                }}
                                                                                className="rounded"
                                                                            />
                                                                            <Label className="font-medium capitalize">{size.size}</Label>
                                                                        </div>
                                                                        <div className="flex-1">
                                                                            <Input
                                                                                type="number"
                                                                                step="0.001"
                                                                                min="0"
                                                                                value={size.price}
                                                                                onChange={(e) => {
                                                                                    const updatedSizes = [...editingItem.sizes];
                                                                                    updatedSizes[index] = { ...updatedSizes[index], price: e.target.value };
                                                                                    setEditingItem({ ...editingItem, sizes: updatedSizes });
                                                                                }}
                                                                                placeholder="0.000"
                                                                                className="w-full"
                                                                            />
                                                                        </div>
                                                                        <span className="text-sm text-gray-500">OMR</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center space-x-2">
                                                            <input
                                                                type="checkbox"
                                                                id={`available-${item.id}`}
                                                                checked={editingItem.available}
                                                                onChange={(e) => setEditingItem({ ...editingItem, available: e.target.checked })}
                                                                className="rounded"
                                                            />
                                                            <Label htmlFor={`available-${item.id}`}>Available for ordering</Label>
                                                        </div>
                                                        <div className="flex space-x-2">
                                                            <Button onClick={handleSaveEdit} className="bg-green-600 hover:bg-green-700">
                                                                <Check className="h-4 w-4 mr-1" />
                                                                Save Changes
                                                            </Button>
                                                            <Button onClick={() => setEditingItem(null)} variant="outline">
                                                                Cancel
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    // View Mode
                                                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                                        <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded flex-shrink-0" />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center space-x-2 mb-1">
                                                                <h3 className="font-semibold">{item.name}</h3>
                                                                {item.name_ar && (
                                                                    <span className="text-sm text-gray-400 font-normal" dir="rtl">{item.name_ar}</span>
                                                                )}
                                                                <Badge
                                                                    className={item.available ? 'bg-green-500' : 'bg-red-500'}
                                                                >
                                                                    {item.available ? 'Available' : 'Sold Out'}
                                                                </Badge>
                                                            </div>
                                                            <p className="text-sm text-gray-600 mb-1 break-words">{item.description.split('|||COMBO_CONFIG|||')[0] || item.description}</p>
                                                            {item.description_ar && (
                                                                <p className="text-sm text-gray-400 mb-2" dir="rtl">{item.description_ar}</p>
                                                            )}
                                                            <div className="flex items-center space-x-2">
                                                                <span className="text-lg font-bold text-red-600">
                                                                    {item.sizes && item.sizes.length > 0
                                                                        ? `OMR ${Math.min(...item.sizes.map(s => s.price)).toFixed(3)} - ${Math.max(...item.sizes.map(s => s.price)).toFixed(3)}`
                                                                        : 'Price not available'
                                                                    }
                                                                </span>
                                                                <Badge variant="outline">{item.category && item.category.name ? item.category.name : (item.category || 'Unknown')}</Badge>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col space-y-2">
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => handleEditItem(item)}
                                                            >
                                                                <Edit className="h-4 w-4 mr-1" />
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant={item.available ? "destructive" : "default"}
                                                                onClick={() => toggleItemAvailability(item.id, !item.available)}
                                                                className={item.available ? "" : "bg-green-600 hover:bg-green-700"}
                                                            >
                                                                <Package className="h-4 w-4 mr-1" />
                                                                {item.available ? 'Mark Sold Out' : 'Mark Available'}
                                                            </Button>
                                                            <Button
                                                                onClick={() => deleteMenuItem(item.id)}
                                                                size="sm"
                                                                variant="destructive"
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                onClick={() => setDuplicatingItem(item)}
                                                                size="sm"
                                                                variant="outline"
                                                                className="bg-blue-50 text-blue-700 hover:bg-blue-100"
                                                            >
                                                                <Copy className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="game" className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Game Management</CardTitle>
                                <CardDescription>Manage game questions and statistics</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <h3 className="text-lg font-semibold mb-4">Game Statistics</h3>
                                        <div className="space-y-3">
                                            <div className="flex justify-between p-3 bg-blue-50 rounded">
                                                <span>Total Questions:</span>
                                                <span className="font-bold">{gameStats.total_questions || 0}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                                        <div className="space-y-3">
                                            <Button
                                                onClick={() => setShowAddQuestion(true)}
                                                className="w-full bg-red-600 hover:bg-red-700"
                                            >
                                                <Plus className="h-4 w-4 mr-2" />
                                                Add New Question
                                            </Button>
                                            <Button
                                                onClick={() => setShowQuestionsList(true)}
                                                variant="outline"
                                                className="w-full"
                                            >
                                                <Eye className="h-4 w-4 mr-2" />
                                                View All Questions ({gameQuestions.length})
                                            </Button>
                                            <Button
                                                onClick={() => setShowCouponsList(true)}
                                                variant="outline"
                                                className="w-full"
                                            >
                                                <Gift className="h-4 w-4 mr-2" />
                                                Manage Coupons ({coupons.length})
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="notifications" className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Notification Management</CardTitle>
                                <CardDescription>Manage notification recipients and settings</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <h3 className="text-lg font-semibold mb-4">Notification Recipients</h3>
                                        <div className="space-y-3">
                                            <Button
                                                onClick={() => setShowNotificationRecipients(true)}
                                                className="w-full bg-red-600 hover:bg-red-700"
                                            >
                                                <Plus className="h-4 w-4 mr-2" />
                                                Manage Recipients ({notificationRecipients.length})
                                            </Button>
                                            <Button
                                                onClick={() => setShowNotificationSettings(true)}
                                                variant="outline"
                                                className="w-full"
                                            >
                                                <Settings className="h-4 w-4 mr-2" />
                                                Notification Settings
                                            </Button>
                                        </div>

                                        <div className="mt-6">
                                            <h4 className="font-semibold mb-3">Quick Overview</h4>
                                            <div className="space-y-2">
                                                <div className="flex justify-between p-2 bg-blue-50 rounded">
                                                    <span>Total Recipients:</span>
                                                    <span className="font-bold">{notificationRecipients.length}</span>
                                                </div>
                                                <div className="flex justify-between p-2 bg-green-50 rounded">
                                                    <span>Active Recipients:</span>
                                                    <span className="font-bold">{notificationRecipients.filter(r => r.active).length}</span>
                                                </div>
                                                <div className="flex justify-between p-2 bg-yellow-50 rounded">
                                                    <span>Email Enabled:</span>
                                                    <span className="font-bold">{(notificationSettings.email_enabled || 'true') === 'true' ? 'Yes' : 'No'}</span>
                                                </div>
                                                <div className="flex justify-between p-2 bg-purple-50 rounded">
                                                    <span>SMS Enabled:</span>
                                                    <span className="font-bold">{(notificationSettings.sms_enabled || 'true') === 'true' ? 'Yes' : 'No'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-semibold mb-4">Notification Types</h3>
                                        <div className="space-y-3">
                                            <div className="p-3 border rounded-lg">
                                                <h4 className="font-semibold text-blue-600">📧 Email Notifications</h4>
                                                <p className="text-sm text-gray-600">Send order confirmations and alerts via email</p>
                                                <p className="text-xs text-gray-500 mt-1">Status: {(notificationSettings.email_enabled || 'true') === 'true' ? '✅ Enabled' : '❌ Disabled'}</p>
                                            </div>
                                            <div className="p-3 border rounded-lg">
                                                <h4 className="font-semibold text-green-600">📱 SMS Notifications</h4>
                                                <p className="text-sm text-gray-600">Send quick alerts via SMS</p>
                                                <p className="text-xs text-gray-500 mt-1">Status: {(notificationSettings.sms_enabled || 'true') === 'true' ? '✅ Enabled' : '❌ Disabled'}</p>
                                            </div>
                                            <div className="p-3 border rounded-lg">
                                                <h4 className="font-semibold text-purple-600">👥 Customer Notifications</h4>
                                                <p className="text-sm text-gray-600">Automatic order confirmations to customers</p>
                                                <p className="text-xs text-gray-500 mt-1">Status: ✅ Always Enabled</p>
                                            </div>
                                            <div className="p-3 border rounded-lg">
                                                <h4 className="font-semibold text-orange-600">🏪 Admin Notifications</h4>
                                                <p className="text-sm text-gray-600">Order alerts to all configured recipients</p>
                                                <p className="text-xs text-gray-500 mt-1">Status: ✅ Always Enabled</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="stats" className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Business Statistics</CardTitle>
                                <CardDescription>Overview of orders, revenue, and performance</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                    <div className="bg-blue-50 p-4 rounded-lg">
                                        <h3 className="text-lg font-bold text-blue-900">{orderStats.total || 0}</h3>
                                        <p className="text-blue-700">Total Orders</p>
                                    </div>
                                    <div className="bg-yellow-50 p-4 rounded-lg">
                                        <h3 className="text-lg font-bold text-yellow-900">{orderStats.pending || 0}</h3>
                                        <p className="text-yellow-700">Pending Orders</p>
                                    </div>
                                    <div className="bg-green-50 p-4 rounded-lg">
                                        <h3 className="text-lg font-bold text-green-900">{orderStats.accepted || 0}</h3>
                                        <p className="text-green-700">Accepted Orders</p>
                                    </div>
                                    <div className="bg-red-50 p-4 rounded-lg">
                                        <h3 className="text-lg font-bold text-red-900">{orderStats.rejected || 0}</h3>
                                        <p className="text-red-700">Rejected Orders</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="bg-green-50 p-4 rounded-lg">
                                        <h3 className="text-lg font-bold text-green-900">OMR {(orderStats.totalRevenue || 0).toFixed(3)}</h3>
                                        <p className="text-green-700">Total Revenue</p>
                                    </div>
                                    <div className="bg-purple-50 p-4 rounded-lg">
                                        <h3 className="text-lg font-bold text-purple-900">OMR {(orderStats.avgOrderValue || 0).toFixed(3)}</h3>
                                        <p className="text-purple-700">Average Order Value</p>
                                    </div>
                                </div>

                                {menuItems.length > 0 && (
                                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                                        <h3 className="font-semibold mb-3">Menu Overview</h3>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <div className="bg-white p-3 rounded-lg border">
                                                <h4 className="font-semibold text-gray-900">{menuItems.length}</h4>
                                                <p className="text-gray-600">Total Items</p>
                                            </div>
                                            <div className="bg-white p-3 rounded-lg border">
                                                <h4 className="font-semibold text-green-900">{menuItems.filter(i => i.available).length}</h4>
                                                <p className="text-green-600">Available Items</p>
                                            </div>
                                            <div className="bg-white p-3 rounded-lg border">
                                                <h4 className="font-semibold text-red-900">{menuItems.filter(i => !i.available).length}</h4>
                                                <p className="text-red-600">Unavailable Items</p>
                                            </div>
                                            <div className="bg-white p-3 rounded-lg border">
                                                <h4 className="font-semibold text-blue-900">{adminCategories.length}</h4>
                                                <p className="text-blue-600">Categories</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Add New Item Modal */}
            <AddMenuItemModal
                isOpen={showAddItem}
                onClose={() => setShowAddItem(false)}
                onAdd={addMenuItem}
                categories={adminCategories}
                menuItems={menuItems}
            />

            {/* Export Orders Modal */}
            {showExportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-md w-full p-6">
                        <h2 className="text-xl font-bold mb-4">Export Orders</h2>
                        <p className="text-gray-600 mb-4">
                            Export {filteredOrders.length} orders to your preferred format.
                        </p>
                        <div className="flex space-x-2">
                            <Button onClick={exportOrders} className="bg-green-600 hover:bg-green-700 flex-1">
                                <Download className="h-4 w-4 mr-2" />
                                Export CSV
                            </Button>
                            <Button onClick={exportOrdersPDF} className="bg-red-600 hover:bg-red-700 flex-1">
                                <FileText className="h-4 w-4 mr-2" />
                                Export PDF
                            </Button>
                        </div>
                        <div className="mt-4">
                            <Button onClick={() => setShowExportModal(false)} variant="outline" className="w-full">
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Duplicate Item Confirmation Modal */}
            {duplicatingItem && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-md w-full p-6">
                        <h2 className="text-xl font-bold mb-4">Duplicate Menu Item</h2>
                        <p className="text-gray-600 mb-4">
                            Create a copy of "{duplicatingItem.name}"? The duplicated item will start as unavailable for your review.
                        </p>
                        <div className="flex space-x-2">
                            <Button onClick={() => duplicateMenuItem(duplicatingItem)} className="bg-blue-600 hover:bg-blue-700 flex-1">
                                <Copy className="h-4 w-4 mr-2" />
                                Duplicate
                            </Button>
                            <Button onClick={() => setDuplicatingItem(null)} variant="outline" className="flex-1">
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Game Questions List Modal */}
            {showQuestionsList && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-800">Game Questions</h2>
                                <Button onClick={() => setShowQuestionsList(false)} variant="outline" size="sm">×</Button>
                            </div>

                            <div className="space-y-4">
                                {console.log('Rendering game questions:', gameQuestions.length, 'questions')}
                                {gameQuestions.map((question, index) => (
                                    <div key={index} className="border rounded-lg p-4">
                                        <div className="flex items-start space-x-4">
                                            <img src={question.image_url} alt="Question" className="w-24 h-24 object-cover rounded" />
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h3 className="font-semibold">{question.question}</h3>
                                                    <div className="flex space-x-2">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => setEditingQuestion(question)}
                                                        >
                                                            <Edit className="h-4 w-4 mr-1" />
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="destructive"
                                                            onClick={() => deleteGameQuestion(question.id)}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2 text-sm">
                                                    {question.options.map((option, optIndex) => (
                                                        <div key={optIndex} className={`p-2 rounded ${optIndex === question.correct_answer ? 'bg-green-100 text-green-800' : 'bg-gray-100'
                                                            }`}>
                                                            {optIndex + 1}. {option}
                                                            {optIndex === question.correct_answer && ' ✓'}
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="mt-2">
                                                    <Badge variant="outline" className="mr-2">Difficulty: {question.difficulty}</Badge>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {gameQuestions.length === 0 && (
                                    <div className="text-center py-8 text-gray-500">
                                        No game questions found
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Coupons List Modal */}
            {showCouponsList && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-800">Coupons Management</h2>
                                <div className="flex space-x-2">
                                    <Button
                                        onClick={() => setShowAddCoupon(true)}
                                        className="bg-green-600 hover:bg-green-700"
                                    >
                                        <Plus className="h-4 w-4 mr-2" />
                                        Add Coupon
                                    </Button>
                                    <Button onClick={() => setShowCouponsList(false)} variant="outline" size="sm">×</Button>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {coupons.map((coupon, index) => (
                                    <div key={index} className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h3 className="font-semibold text-lg">{coupon.code}</h3>
                                                <p className="text-gray-600">{coupon.discount_percentage}% discount</p>
                                                <p className="text-sm text-gray-500">
                                                    Used: {coupon.used_count}/{coupon.max_uses} |
                                                    Expires: {new Date(coupon.expires_at).toLocaleDateString()}
                                                </p>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Badge className={coupon.active ? 'bg-green-500' : 'bg-red-500'}>
                                                    {coupon.active ? 'Active' : 'Inactive'}
                                                </Badge>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setEditingCoupon(coupon)}
                                                >
                                                    <Edit className="h-4 w-4 mr-1" />
                                                    Edit
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="destructive"
                                                    onClick={() => deleteCoupon(coupon.id)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {coupons.length === 0 && (
                                    <div className="text-center py-8 text-gray-500">
                                        No coupons found
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Game Question Modal */}
            {(showAddQuestion || editingQuestion) && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-800">
                                    {editingQuestion ? 'Edit Game Question' : 'Add New Game Question'}
                                </h2>
                                <Button
                                    onClick={() => {
                                        setShowAddQuestion(false);
                                        setEditingQuestion(null);
                                    }}
                                    variant="outline"
                                    size="sm"
                                >×</Button>
                            </div>

                            <form onSubmit={(e) => {
                                e.preventDefault();
                                const questionData = editingQuestion || newQuestion;
                                if (editingQuestion) {
                                    updateGameQuestion(editingQuestion.id, questionData);
                                } else {
                                    createGameQuestion(questionData);
                                }
                            }} className="space-y-4">
                                <div>
                                    <Label>Question Text * (English)</Label>
                                    <Textarea
                                        value={editingQuestion ? editingQuestion.question : newQuestion.question}
                                        onChange={(e) => {
                                            if (editingQuestion) {
                                                setEditingQuestion({ ...editingQuestion, question: e.target.value });
                                            } else {
                                                setNewQuestion({ ...newQuestion, question: e.target.value });
                                            }
                                        }}
                                        placeholder="Enter the question text..."
                                        rows={3}
                                        required
                                    />
                                </div>

                                <div>
                                    <Label>Question Text (Arabic / عربي)</Label>
                                    <Textarea
                                        dir="rtl"
                                        value={editingQuestion ? (editingQuestion.question_ar || '') : newQuestion.question_ar}
                                        onChange={(e) => {
                                            if (editingQuestion) {
                                                setEditingQuestion({ ...editingQuestion, question_ar: e.target.value });
                                            } else {
                                                setNewQuestion({ ...newQuestion, question_ar: e.target.value });
                                            }
                                        }}
                                        placeholder="أدخل نص السؤال بالعربية..."
                                        rows={3}
                                        style={{ fontFamily: 'Cairo, system-ui, sans-serif' }}
                                    />
                                </div>

                                <div>
                                    <Label>Image URL *</Label>
                                    <Input
                                        value={editingQuestion ? editingQuestion.image_url : newQuestion.image_url}
                                        onChange={(e) => {
                                            if (editingQuestion) {
                                                setEditingQuestion({ ...editingQuestion, image_url: e.target.value });
                                            } else {
                                                setNewQuestion({ ...newQuestion, image_url: e.target.value });
                                            }
                                        }}
                                        placeholder="https://example.com/image.jpg"
                                        required
                                    />
                                </div>

                                <div>
                                    <Label>Options * (English)</Label>
                                    <div className="space-y-2">
                                        {(editingQuestion ? editingQuestion.options : newQuestion.options).map((option, index) => (
                                            <div key={index} className="flex items-center space-x-2">
                                                <Input
                                                    value={option}
                                                    onChange={(e) => {
                                                        const newOptions = [...(editingQuestion ? editingQuestion.options : newQuestion.options)];
                                                        newOptions[index] = e.target.value;
                                                        if (editingQuestion) {
                                                            setEditingQuestion({ ...editingQuestion, options: newOptions });
                                                        } else {
                                                            setNewQuestion({ ...newQuestion, options: newOptions });
                                                        }
                                                    }}
                                                    placeholder={`Option ${index + 1}`}
                                                    required
                                                />
                                                <input
                                                    type="radio"
                                                    name="correct_answer"
                                                    checked={(editingQuestion ? editingQuestion.correct_answer : newQuestion.correct_answer) === index}
                                                    onChange={() => {
                                                        if (editingQuestion) {
                                                            setEditingQuestion({ ...editingQuestion, correct_answer: index });
                                                        } else {
                                                            setNewQuestion({ ...newQuestion, correct_answer: index });
                                                        }
                                                    }}
                                                    className="rounded"
                                                />
                                                <Label className="text-sm">Correct</Label>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <Label>Options (Arabic / عربي)</Label>
                                    <div className="space-y-2">
                                        {(editingQuestion ? (editingQuestion.options_ar || ['', '', '', '']) : newQuestion.options_ar).map((option, index) => (
                                            <div key={index} className="flex items-center space-x-2">
                                                <Input
                                                    dir="rtl"
                                                    value={option}
                                                    onChange={(e) => {
                                                        const base = editingQuestion ? (editingQuestion.options_ar || ['', '', '', '']) : newQuestion.options_ar;
                                                        const newOptions = [...base];
                                                        newOptions[index] = e.target.value;
                                                        if (editingQuestion) {
                                                            setEditingQuestion({ ...editingQuestion, options_ar: newOptions });
                                                        } else {
                                                            setNewQuestion({ ...newQuestion, options_ar: newOptions });
                                                        }
                                                    }}
                                                    placeholder={`الخيار ${index + 1}`}
                                                    style={{ fontFamily: 'Cairo, system-ui, sans-serif' }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <Label>Difficulty *</Label>
                                    <select
                                        value={editingQuestion ? editingQuestion.difficulty : newQuestion.difficulty}
                                        onChange={(e) => {
                                            if (editingQuestion) {
                                                setEditingQuestion({ ...editingQuestion, difficulty: e.target.value });
                                            } else {
                                                setNewQuestion({ ...newQuestion, difficulty: e.target.value });
                                            }
                                        }}
                                        className="w-full p-2 border rounded-md"
                                        required
                                    >
                                        <option value="easy">Easy</option>
                                        <option value="medium">Medium</option>
                                        <option value="hard">Hard</option>
                                    </select>
                                </div>

                                <div className="flex space-x-2 pt-4">
                                    <Button type="submit" className="bg-green-600 hover:bg-green-700 flex-1">
                                        {editingQuestion ? 'Update Question' : 'Add Question'}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setShowAddQuestion(false);
                                            setEditingQuestion(null);
                                        }}
                                        variant="outline"
                                        className="flex-1"
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Coupon Modal */}
            {editingCoupon && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-800">Edit Coupon</h2>
                            <Button onClick={() => setEditingCoupon(null)} variant="outline" size="sm">×</Button>
                        </div>

                        <form onSubmit={(e) => {
                            e.preventDefault();
                            updateCoupon(editingCoupon.id, {
                                discount_percentage: editingCoupon.discount_percentage,
                                max_uses: editingCoupon.max_uses,
                                active: editingCoupon.active
                            });
                        }} className="space-y-4">
                            <div>
                                <Label>Coupon Code</Label>
                                <Input
                                    value={editingCoupon.code}
                                    disabled
                                    className="bg-gray-100"
                                />
                                <p className="text-sm text-gray-500 mt-1">Code cannot be changed</p>
                            </div>

                            <div>
                                <Label>Discount Percentage (%)</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={editingCoupon.discount_percentage}
                                    onChange={(e) => setEditingCoupon({ ...editingCoupon, discount_percentage: parseInt(e.target.value) })}
                                    required
                                />
                            </div>

                            <div>
                                <Label>Maximum Uses</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={editingCoupon.max_uses}
                                    onChange={(e) => setEditingCoupon({ ...editingCoupon, max_uses: parseInt(e.target.value) })}
                                    required
                                />
                            </div>

                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="coupon-active"
                                    checked={editingCoupon.active}
                                    onChange={(e) => setEditingCoupon({ ...editingCoupon, active: e.target.checked })}
                                    className="rounded"
                                />
                                <Label htmlFor="coupon-active">Active</Label>
                            </div>

                            <div className="flex space-x-2 pt-4">
                                <Button type="submit" className="bg-green-600 hover:bg-green-700 flex-1">
                                    Update Coupon
                                </Button>
                                <Button onClick={() => setEditingCoupon(null)} variant="outline" className="flex-1">
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add New Coupon Modal */}
            {showAddCoupon && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-800">Add New Coupon</h2>
                            <Button onClick={() => setShowAddCoupon(false)} variant="outline" size="sm">×</Button>
                        </div>

                        <form onSubmit={(e) => {
                            e.preventDefault();
                            createCoupon(newCoupon);
                        }} className="space-y-4">
                            <div>
                                <Label>Coupon Code *</Label>
                                <Input
                                    value={newCoupon.code}
                                    onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                                    placeholder="e.g., SAVE20"
                                    required
                                />
                            </div>

                            <div>
                                <Label>Discount Percentage (%) *</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={newCoupon.discount_percentage}
                                    onChange={(e) => setNewCoupon({ ...newCoupon, discount_percentage: parseInt(e.target.value) })}
                                    required
                                />
                            </div>

                            <div>
                                <Label>Maximum Uses *</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={newCoupon.max_uses}
                                    onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: parseInt(e.target.value) })}
                                    required
                                />
                            </div>

                            <div>
                                <Label>Expiry Days *</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={newCoupon.expiry_days}
                                    onChange={(e) => setNewCoupon({ ...newCoupon, expiry_days: parseInt(e.target.value) })}
                                    required
                                />
                            </div>

                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="new-coupon-active"
                                    checked={newCoupon.active}
                                    onChange={(e) => setNewCoupon({ ...newCoupon, active: e.target.checked })}
                                    className="rounded"
                                />
                                <Label htmlFor="new-coupon-active">Active</Label>
                            </div>

                            <div className="flex space-x-2 pt-4">
                                <Button type="submit" className="bg-green-600 hover:bg-green-700 flex-1">
                                    Create Coupon
                                </Button>
                                <Button onClick={() => setShowAddCoupon(false)} variant="outline" className="flex-1">
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Spin Coupons List Modal - REMOVED (using fixed arrays) */}

            {/* Notification Recipients List Modal */}
            {showNotificationRecipients && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-800">Notification Recipients Management</h2>
                                <div className="flex space-x-2">
                                    <Button
                                        onClick={() => setShowAddRecipient(true)}
                                        className="bg-green-600 hover:bg-green-700"
                                    >
                                        <Plus className="h-4 w-4 mr-2" />
                                        Add Recipient
                                    </Button>
                                    <Button onClick={() => setShowNotificationRecipients(false)} variant="outline" size="sm">×</Button>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {notificationRecipients.map((recipient, index) => (
                                    <div key={index} className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h3 className="font-semibold text-lg">{recipient.name}</h3>
                                                <p className="text-gray-600">
                                                    Type: {recipient.notification_type} |
                                                    {recipient.email && ` Email: ${recipient.email}`} |
                                                    {recipient.phone && ` Phone: ${recipient.phone}`}
                                                </p>
                                                <p className="text-sm text-gray-500">
                                                    Created: {new Date(recipient.created_at).toLocaleDateString()}
                                                </p>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Badge className={recipient.active ? 'bg-green-500' : 'bg-red-500'}>
                                                    {recipient.active ? 'Active' : 'Inactive'}
                                                </Badge>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setEditingRecipient(recipient)}
                                                >
                                                    <Edit className="h-4 w-4 mr-1" />
                                                    Edit
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="destructive"
                                                    onClick={() => deleteNotificationRecipient(recipient.id)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {notificationRecipients.length === 0 && (
                                    <div className="text-center py-8 text-gray-500">
                                        No notification recipients found
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Notification Recipient Modal */}
            {(showAddRecipient || editingRecipient) && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-800">
                                    {editingRecipient ? 'Edit Notification Recipient' : 'Add New Notification Recipient'}
                                </h2>
                                <Button
                                    onClick={() => {
                                        setShowAddRecipient(false);
                                        setEditingRecipient(null);
                                    }}
                                    variant="outline"
                                    size="sm"
                                >×</Button>
                            </div>

                            <form onSubmit={(e) => {
                                e.preventDefault();
                                const recipientData = editingRecipient || newRecipient;
                                if (editingRecipient) {
                                    updateNotificationRecipient(editingRecipient.id, recipientData);
                                } else {
                                    createNotificationRecipient(recipientData);
                                }
                            }} className="space-y-4">
                                <div>
                                    <Label>Name *</Label>
                                    <Input
                                        value={editingRecipient ? editingRecipient.name : newRecipient.name}
                                        onChange={(e) => {
                                            if (editingRecipient) {
                                                setEditingRecipient({ ...editingRecipient, name: e.target.value });
                                            } else {
                                                setNewRecipient({ ...newRecipient, name: e.target.value });
                                            }
                                        }}
                                        placeholder="e.g., Kitchen Manager"
                                        required
                                    />
                                </div>

                                <div>
                                    <Label>Email (Optional)</Label>
                                    <Input
                                        type="email"
                                        value={editingRecipient ? editingRecipient.email : newRecipient.email}
                                        onChange={(e) => {
                                            if (editingRecipient) {
                                                setEditingRecipient({ ...editingRecipient, email: e.target.value });
                                            } else {
                                                setNewRecipient({ ...newRecipient, email: e.target.value });
                                            }
                                        }}
                                        placeholder="e.g., manager@wowpizza.com"
                                    />
                                </div>

                                <div>
                                    <Label>Phone (Optional)</Label>
                                    <Input
                                        value={editingRecipient ? editingRecipient.phone : newRecipient.phone}
                                        onChange={(e) => {
                                            if (editingRecipient) {
                                                setEditingRecipient({ ...editingRecipient, phone: e.target.value });
                                            } else {
                                                setNewRecipient({ ...newRecipient, phone: e.target.value });
                                            }
                                        }}
                                        placeholder="e.g., +96812345678"
                                    />
                                </div>

                                <div>
                                    <Label>Notification Type *</Label>
                                    <select
                                        value={editingRecipient ? editingRecipient.notification_type : newRecipient.notification_type}
                                        onChange={(e) => {
                                            if (editingRecipient) {
                                                setEditingRecipient({ ...editingRecipient, notification_type: e.target.value });
                                            } else {
                                                setNewRecipient({ ...newRecipient, notification_type: e.target.value });
                                            }
                                        }}
                                        className="w-full p-2 border rounded-md"
                                        required
                                    >
                                        <option value="admin">Admin</option>
                                        <option value="manager">Manager</option>
                                        <option value="kitchen">Kitchen</option>
                                        <option value="delivery">Delivery</option>
                                        <option value="customer_service">Customer Service</option>
                                    </select>
                                </div>

                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="recipient-active"
                                        checked={editingRecipient ? editingRecipient.active : newRecipient.active}
                                        onChange={(e) => {
                                            if (editingRecipient) {
                                                setEditingRecipient({ ...editingRecipient, active: e.target.checked });
                                            } else {
                                                setNewRecipient({ ...newRecipient, active: e.target.checked });
                                            }
                                        }}
                                        className="rounded"
                                    />
                                    <Label htmlFor="recipient-active">Active</Label>
                                </div>

                                <div className="flex space-x-2 pt-4">
                                    <Button type="submit" className="bg-green-600 hover:bg-green-700 flex-1">
                                        {editingRecipient ? 'Update Recipient' : 'Create Recipient'}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setShowAddRecipient(false);
                                            setEditingRecipient(null);
                                        }}
                                        variant="outline"
                                        className="flex-1"
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Notification Settings Modal */}
            {showNotificationSettings && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-800">Notification Settings</h2>
                                <Button onClick={() => setShowNotificationSettings(false)} variant="outline" size="sm">×</Button>
                            </div>

                            <form onSubmit={(e) => {
                                e.preventDefault();
                                updateNotificationSettings(notificationSettings);
                            }} className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold mb-4">General Settings</h3>
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between p-3 border rounded-lg">
                                            <div>
                                                <Label className="text-base font-medium">Email Notifications</Label>
                                                <p className="text-sm text-gray-600">Enable email notifications for orders</p>
                                            </div>
                                            <input
                                                type="checkbox"
                                                name="email_enabled"
                                                checked={(notificationSettings.email_enabled || 'true') === 'true'}
                                                onChange={(e) => handleSettingChange('email_enabled', e.target.checked)}
                                                className="rounded"
                                            />
                                        </div>

                                        <div className="flex items-center justify-between p-3 border rounded-lg">
                                            <div>
                                                <Label className="text-base font-medium">SMS Notifications</Label>
                                                <p className="text-sm text-gray-600">Enable SMS notifications for orders</p>
                                            </div>
                                            <input
                                                type="checkbox"
                                                name="sms_enabled"
                                                checked={(notificationSettings.sms_enabled || 'true') === 'true'}
                                                onChange={(e) => handleSettingChange('sms_enabled', e.target.checked)}
                                                className="rounded"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold mb-4">Recipient Type Settings</h3>
                                    <div className="space-y-4">
                                        {(() => {
                                            // Get unique notification types from actual recipients
                                            const recipientTypes = [...new Set(notificationRecipients.map(r => r.notification_type))];
                                            return recipientTypes.map(type => (
                                                <div key={type} className="flex items-center justify-between p-3 border rounded-lg">
                                                    <div>
                                                        <Label className="text-base font-medium capitalize">{type} Notifications</Label>
                                                        <p className="text-sm text-gray-600">
                                                            Send notifications to {type} recipients
                                                            ({notificationRecipients.filter(r => r.notification_type === type).length} recipient{notificationRecipients.filter(r => r.notification_type === type).length !== 1 ? 's' : ''})
                                                        </p>
                                                    </div>
                                                    <input
                                                        type="checkbox"
                                                        name={`notify_${type}`}
                                                        checked={(notificationSettings[`notify_${type}`] || 'true') === 'true'}
                                                        onChange={(e) => handleSettingChange(`notify_${type}`, e.target.checked)}
                                                        className="rounded"
                                                    />
                                                </div>
                                            ));
                                        })()}

                                        {notificationRecipients.length === 0 && (
                                            <div className="text-center py-4 text-gray-500">
                                                No recipients found. Add recipients first to configure type-specific settings.
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex space-x-2 pt-4">
                                    <Button type="submit" className="bg-green-600 hover:bg-green-700 flex-1">
                                        Save Settings
                                    </Button>
                                    <Button
                                        onClick={() => setShowNotificationSettings(false)}
                                        variant="outline"
                                        className="flex-1"
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Category Management Modal */}
            {showCategoryManagement && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-800">Category Management</h2>
                                <div className="flex space-x-2">
                                    <Button
                                        onClick={() => setShowAddCategory(true)}
                                        className="bg-green-600 hover:bg-green-700"
                                    >
                                        <Plus className="h-4 w-4 mr-2" />
                                        Add Category
                                    </Button>
                                    <Button onClick={() => setShowCategoryManagement(false)} variant="outline" size="sm">×</Button>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {adminCategories.map((category, index) => (
                                    <div key={index} className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h3 className="font-semibold text-lg">{category.name}</h3>
                                                <p className="text-sm text-gray-500">Category ID: {category.id}</p>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setEditingCategory({ id: category.id, name: category.name, icon: category.icon })}
                                                >
                                                    <Edit className="h-4 w-4 mr-1" />
                                                    Edit
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="destructive"
                                                    onClick={() => deleteCategory(category.id)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {adminCategories.length === 0 && (
                                    <div className="text-center py-8 text-gray-500">
                                        No categories found
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Category Modal */}
            {(showAddCategory || editingCategory) && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-800">
                                {editingCategory ? 'Edit Category' : 'Add New Category'}
                            </h2>
                            <Button
                                onClick={() => {
                                    setShowAddCategory(false);
                                    setEditingCategory(null);
                                }}
                                variant="outline"
                                size="sm"
                            >×</Button>
                        </div>

                        <form onSubmit={(e) => {
                            e.preventDefault();
                            const categoryData = editingCategory || newCategory;
                            if (editingCategory) {
                                updateCategory(editingCategory.id, categoryData);
                            } else {
                                createCategory(categoryData);
                            }
                        }} className="space-y-4">
                            <div>
                                <Label>Category Name * (English)</Label>
                                <Input
                                    value={editingCategory ? editingCategory.name : newCategory.name}
                                    onChange={(e) => {
                                        if (editingCategory) {
                                            setEditingCategory({ ...editingCategory, name: e.target.value });
                                        } else {
                                            setNewCategory({ ...newCategory, name: e.target.value });
                                        }
                                    }}
                                    placeholder="e.g., Pizza, Sides, Drinks"
                                    required
                                />
                            </div>

                            <div>
                                <Label>Arabic Name (اسم عربي)</Label>
                                <Input
                                    dir="rtl"
                                    value={editingCategory ? (editingCategory.name_ar || '') : newCategory.name_ar}
                                    onChange={(e) => {
                                        if (editingCategory) {
                                            setEditingCategory({ ...editingCategory, name_ar: e.target.value });
                                        } else {
                                            setNewCategory({ ...newCategory, name_ar: e.target.value });
                                        }
                                    }}
                                    placeholder="مثال: بيتزا، مشروبات"
                                    style={{ fontFamily: 'Cairo, system-ui, sans-serif' }}
                                />
                            </div>

                            <div className="text-sm text-gray-500 italic bg-gray-50 p-3 rounded-lg border">
                                The standard food icon will be assigned automatically.
                            </div>

                            <div className="flex space-x-2 pt-4">
                                <Button type="submit" className="bg-green-600 hover:bg-green-700 flex-1">
                                    {editingCategory ? 'Update Category' : 'Create Category'}
                                </Button>
                                <Button
                                    onClick={() => {
                                        setShowAddCategory(false);
                                        setEditingCategory(null);
                                    }}
                                    variant="outline"
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};



// Admin Component (wrapper)
const Admin = () => {
    const [token, setToken] = useState(localStorage.getItem('admin_token'));

    if (!token) {
        return <AdminLogin onLogin={setToken} />;
    }

    return <AdminDashboard token={token} />;
};
// const About = () => {
//   return (
//     <div className="container mx-auto px-4 py-8">
//       <div className="max-w-4xl mx-auto">
//         <div className="text-center mb-12">
//           <h2 className="text-4xl font-bold text-red-600 mb-4">About Wow Pizza</h2>
//           <p className="text-2xl font-bold text-white mb-4">An Amazing taste Experience.</p>
//         </div>

//         <div className="grid md:grid-cols-2 gap-8 mb-12">
//           <Card className="shadow-lg">
//             <CardHeader>
//               <CardTitle className="text-2xl text-red-600">Our Vision</CardTitle>
//             </CardHeader>
//             <CardContent>
//               <p className="text-gray-700 leading-relaxed">
//               To become a leading pizza brand in Oman and beyond, expanding across multiple locations while consistently maintaining our uniform quality and taste.
//               </p>
//             </CardContent>
//           </Card>

//           <Card className="shadow-lg">
//             <CardHeader>
//               <CardTitle className="text-2xl text-red-600">Our Mission</CardTitle>
//             </CardHeader>
//             <CardContent>
//               <p className="text-gray-700 leading-relaxed">
//               To provide An Amazing taste Experience, to all our consumers on every occasion, through a combination of taste, love and smile, by using locally sourced, high quality ingredients.

//               </p>
//             </CardContent>
//           </Card>
//         </div>

//         <Card className="shadow-xl mb-8">
//           <CardHeader>
//             <CardTitle className="text-3xl text-center text-red-600 mb-6">Our Story</CardTitle>
//           </CardHeader>
//           <CardContent className="space-y-6">
//             <div className="grid md:grid-cols-3 gap-6">
//               <div className="text-center">
//                 <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
//                   <Pizza className="h-12 w-12 text-red-600" />
//                 </div>
//                 <h3 className="text-xl font-semibold mb-3">Akeel Ahmad Jalil</h3>
//                 <p className="text-gray-600">Co-Founder & Head Chef</p>
//                 <p className="text-sm text-gray-700 mt-2">
//                   With 15 years of culinary experience across India and the Middle East, 
//                   Akeel brings authentic pizza-making techniques to Oman.
//                 </p>
//               </div>

//               <div className="text-center">
//                 <div className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
//                   <Star className="h-12 w-12 text-orange-600" />
//                 </div>
//                 <h3 className="text-xl font-semibold mb-3">Irfan Ali Syed</h3>
//                 <p className="text-gray-600">Co-Founder & Investor</p>
//                 <p className="text-sm text-gray-700 mt-2">
//                   A passionate entrepreneur from Muscat, Irfan ensures every customer 
//                   receives exceptional service and quality.
//                 </p>
//               </div>

//               <div className="text-center">
//                 <div className="w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
//                   <MapPin className="h-12 w-12 text-yellow-600" />
//                 </div>
//                 <h3 className="text-xl font-semibold mb-3">Our Journey</h3>
//                 <p className="text-gray-600">Est. 2024</p>
//                 <p className="text-sm text-gray-700 mt-2">
//                   Starting from a shared dream to bring the best pizza experience 
//                   to Oman, we opened our doors in 2024 with 4 dedicated Partners.
//                 </p>
//               </div>
//             </div>

//             <div className="mt-8 p-6 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl">
//               <h4 className="text-xl font-semibold text-gray-800 mb-3">Our Commitment to Quality</h4>
//               <p className="text-gray-700 leading-relaxed">
//                 At Wow Pizza, we believe great pizza starts with great ingredients. We source the freshest 
//                 vegetables from local Omani farms, with high quality cheese, and prepare our signature 
//                 dough fresh daily. Every pizza is hand-stretched and baked to perfection in our oven, 
//                 ensuring that crispy crust and melted cheese combination that makes every bite a "wow" moment.
//               </p>
//             </div>

//             <div className="mt-6 p-6 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl">
//               <h4 className="text-xl font-semibold text-gray-800 mb-3">Community First</h4>
//               <p className="text-gray-700 leading-relaxed">
//                 We're more than just a pizza place - we're part of the Amerat community. From celebrating birthdays to hosting family game nights, Wow Pizza is committed to bringing people 
//                 together. Our interactive game challenges and coupon rewards are our way of showing 
//                 appreciation to our loyal customers while celebrating Oman's rich culture and landmarks.
//               </p>
//             </div>
//           </CardContent>
//         </Card>

//         <Card className="shadow-lg">
//           <CardHeader>
//             <CardTitle className="text-2xl text-center text-red-600">Visit Us Today!</CardTitle>
//           </CardHeader>
//           <CardContent>
//             <div className="grid md:grid-cols-3 gap-6 text-center">
//               <div>
//                 <MapPin className="h-8 w-8 text-red-600 mx-auto mb-2" />
//                 <h4 className="font-semibold">Location</h4>
//                 <p className="text-gray-600">Amerat, Muscat, Oman</p>
//               </div>
//               <div>
//                 <Phone className="h-8 w-8 text-red-600 mx-auto mb-2" />
//                 <h4 className="font-semibold">Phone</h4>
//                 <p className="text-gray-600">+968 9923 6352 / 24870085</p>
//               </div>
//               <div>
//                 <Mail className="h-8 w-8 text-red-600 mx-auto mb-2" />
//                 <h4 className="font-semibold">Email</h4>
//                 <p className="text-gray-600">wowpizza.muscat@gmail.com</p>
//               </div>
//             </div>
//           </CardContent>
//         </Card>
//       </div>
//     </div>
//   );
// };

const About = () => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const [activeTab, setActiveTab] = useState('vision');
    const contentRef = React.useRef(null);

    const handleTabClick = (tabId) => {
        setActiveTab(tabId);
        if (window.innerWidth < 1024) {
            setTimeout(() => {
                contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 50);
        }
    };

    const tabs = [
        { id: 'vision', label: t('about.tabs.vision'), color: 'from-red-500 to-red-600', bgColor: 'bg-red-500' },
        { id: 'mission', label: t('about.tabs.mission'), color: 'from-orange-500 to-orange-600', bgColor: 'bg-orange-500' },
        { id: 'story', label: t('about.tabs.story'), color: 'from-yellow-500 to-yellow-600', bgColor: 'bg-yellow-500' },
        { id: 'team', label: t('about.tabs.team'), color: 'from-green-500 to-green-600', bgColor: 'bg-green-500' },
        { id: 'quality', label: t('about.tabs.quality'), color: 'from-blue-500 to-blue-600', bgColor: 'bg-blue-500' },
        { id: 'community', label: t('about.tabs.community'), color: 'from-purple-500 to-purple-600', bgColor: 'bg-purple-500' },
        { id: 'contact', label: t('about.tabs.contact'), color: 'from-pink-500 to-pink-600', bgColor: 'bg-pink-500' }
    ];

    const tabContent = {
        vision: {
            title: t('about.vision.title'),
            subtitle: t('about.vision.subtitle'),
            content: t('about.vision.content'),
            icon: '🎯',
            gradient: 'from-red-400 to-red-600'
        },
        mission: {
            title: t('about.mission.title'),
            subtitle: t('about.mission.subtitle'),
            content: t('about.mission.content'),
            icon: '⭐',
            gradient: 'from-orange-400 to-orange-600'
        },
        story: {
            title: t('about.story.title'),
            subtitle: t('about.story.subtitle'),
            content: t('about.story.content'),
            icon: '📖',
            gradient: 'from-yellow-400 to-yellow-600'
        },
        team: {
            title: t('about.team.title'),
            subtitle: t('about.team.subtitle'),
            content: (
                <div className="space-y-6">
                    <div className="group relative overflow-hidden rounded-xl bg-gradient-to-r from-red-50 to-orange-50 p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
                        <div className="absolute inset-0 bg-gradient-to-r from-red-400/10 to-orange-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="relative flex items-center space-x-4">
                            <div className="relative">
                                <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-red-500 shadow-lg">
                                    <img
                                        src="/assets/logo/akeel.png"
                                        alt="Akeel Ahmad Jalil"
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                    <div className="w-full h-full bg-gradient-to-r from-red-400 to-red-600 flex items-center justify-center" style={{ display: 'none' }}>
                                        <Pizza className="h-8 w-8 text-white" />
                                    </div>
                                </div>
                                <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                    <span className="text-white text-xs">✓</span>
                                </div>
                            </div>
                            <div>
                                <h4 className="text-xl font-bold text-gray-800">Akeel Ahmad Jalil</h4>
                                <p className="text-red-600 font-semibold">{t('about.team.chefRole')}</p>
                                <p className="text-sm text-gray-600 mt-1">{t('about.team.chefBio')}</p>
                            </div>
                        </div>
                    </div>

                    <div className="group relative overflow-hidden rounded-xl bg-gradient-to-r from-orange-50 to-yellow-50 p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
                        <div className="absolute inset-0 bg-gradient-to-r from-orange-400/10 to-yellow-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="relative flex items-center space-x-4">
                            <div className="relative">
                                <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-orange-500 shadow-lg">
                                    <img
                                        src="/assets/logo/irfan.png"
                                        alt="Irfan Ali Syed"
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                    <div className="w-full h-full bg-gradient-to-r from-orange-400 to-orange-600 flex items-center justify-center" style={{ display: 'none' }}>
                                        <Star className="h-8 w-8 text-white" />
                                    </div>
                                </div>
                                <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                    <span className="text-white text-xs">✓</span>
                                </div>
                            </div>
                            <div>
                                <h4 className="text-xl font-bold text-gray-800">Irfan Ali Syed</h4>
                                <p className="text-orange-600 font-semibold">{t('about.team.investorRole')}</p>
                                <p className="text-sm text-gray-600 mt-1">{t('about.team.investorBio')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            ),
            icon: '👥',
            gradient: 'from-green-400 to-green-600'
        },
        quality: {
            title: t('about.quality.title'),
            subtitle: t('about.quality.subtitle'),
            content: t('about.quality.content'),
            icon: '🍕',
            gradient: 'from-blue-400 to-blue-600'
        },
        community: {
            title: t('about.community.title'),
            subtitle: t('about.community.subtitle'),
            content: t('about.community.content'),
            icon: '🤝',
            gradient: 'from-purple-400 to-purple-600'
        },
        contact: {
            title: t('about.contact.title'),
            subtitle: t('about.contact.subtitle'),
            content: (
                <div className="space-y-4">
                    <div className="group flex items-start space-x-4 p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl hover:bg-gradient-to-r hover:from-red-100 hover:to-orange-100 transition-all duration-300">
                        <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-r from-red-400 to-red-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                            <MapPin className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <h4 className="font-bold text-gray-800">{t('about.contact.location')}</h4>
                            <p className="text-gray-600"><span className="font-medium text-gray-700">{t('home.ameratName')}:</span> {t('about.contact.ameratAddress')}</p>
                            <p className="text-gray-600 mt-1"><span className="font-medium text-gray-700">{t('home.qurumName')}:</span> {t('about.contact.qurumAddress')}</p>
                            <p className="text-gray-600 mt-1"><span className="font-medium text-gray-700">{t('home.khuwairName')}:</span> {t('about.contact.khuwairAddress')}</p>
                        </div>
                    </div>

                    <div className="group flex items-start space-x-4 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl hover:bg-gradient-to-r hover:from-orange-100 hover:to-yellow-100 transition-all duration-300">
                        <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                            <Phone className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <h4 className="font-bold text-gray-800">{t('about.contact.phone')}</h4>
                            <p className="text-gray-600"><span className="font-medium text-gray-700">{t('home.ameratName')}:</span> <span dir="ltr">+968 9923 6352 / 24870085</span></p>
                            <p className="text-gray-600 mt-1"><span className="font-medium text-gray-700">{t('home.qurumName')}:</span> <span dir="ltr">+968 92111965</span></p>
                            <p className="text-gray-600 mt-1"><span className="font-medium text-gray-700">{t('home.khuwairName')}:</span> <span dir="ltr">+968 92111946</span></p>
                        </div>
                    </div>

                    <div className="group flex items-center space-x-4 p-4 bg-gradient-to-r from-yellow-50 to-green-50 rounded-xl hover:bg-gradient-to-r hover:from-yellow-100 hover:to-green-100 transition-all duration-300">
                        <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                            <Mail className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <h4 className="font-bold text-gray-800">{t('about.contact.email')}</h4>
                            <p className="text-gray-600">wowpizza.muscat@gmail.com</p>
                        </div>
                    </div>

                    <div className="group flex items-center space-x-4 p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl hover:bg-gradient-to-r hover:from-pink-100 hover:to-purple-100 transition-all duration-300">
                        <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-r from-pink-400 to-purple-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                            <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6 text-white">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                            </svg>
                        </div>
                        <div>
                            <h4 className="font-bold text-gray-800">{t('about.contact.instagram')}</h4>
                            <a
                                href="https://www.instagram.com/wowpizza.oman/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-600 hover:text-pink-500 transition-colors"
                            >
                                @wowpizza.oman
                            </a>
                        </div>
                    </div>

                    <div className="group flex items-center space-x-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl hover:bg-gradient-to-r hover:from-blue-100 hover:to-indigo-100 transition-all duration-300">
                        <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-r from-blue-400 to-indigo-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                            <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6 text-white">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                            </svg>
                        </div>
                        <div>
                            <h4 className="font-bold text-gray-800">{t('about.contact.facebook')}</h4>
                            <a
                                href="https://www.facebook.com/people/Wow-pizza/61573539833825/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-600 hover:text-blue-500 transition-colors"
                            >
                                Wow Pizza
                            </a>
                        </div>
                    </div>
                </div>
            ),
            icon: '📞',
            gradient: 'from-pink-400 to-pink-600'
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-20 left-20 w-72 h-72 bg-red-500/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-yellow-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
            </div>

            <div className="container mx-auto px-4 py-8 relative z-10">
                <div className="max-w-7xl mx-auto">
                    {/* Hero Section */}
                    <div className="text-center mb-12">
                        <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-red-400 via-red-600 to-red-500 bg-clip-text text-transparent mb-4 pb-2 px-4">
                            {t('about.title')}
                        </h1>
                        <p className="text-2xl text-white/90 font-light">{t('about.tagline')}</p>
                    </div>

                    <div className="grid lg:grid-cols-4 gap-8">
                        {/* Enhanced Sidebar */}
                        <div className="lg:col-span-1">
                            <div className="space-y-3">
                                {tabs.map((tab, index) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => handleTabClick(tab.id)}
                                        className={`w-full p-4 transition-all duration-500 transform hover:scale-105 ${lang === 'ar' ? 'text-right rounded-l-2xl' : 'text-left rounded-r-2xl'} ${activeTab === tab.id
                                            ? `bg-gradient-to-r ${tab.color} text-white shadow-2xl scale-105 ${lang === 'ar' ? 'border-r-4' : 'border-l-4'} border-white`
                                            : `${tab.bgColor} text-white hover:shadow-lg ${lang === 'ar' ? 'hover:border-r-4' : 'hover:border-l-4'} hover:border-white/50`
                                            }`}
                                        style={{
                                            clipPath: lang === 'ar'
                                                ? 'polygon(100% 0%, 10% 0%, 0% 50%, 10% 100%, 100% 100%)'
                                                : 'polygon(0% 0%, 90% 0%, 100% 50%, 90% 100%, 0% 100%)',
                                            animationDelay: `${index * 0.1}s`
                                        }}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <span className="text-2xl">{tab.icon}</span>
                                            <span className="font-bold text-lg">{tab.label}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Enhanced Content Area */}
                        <div className="lg:col-span-3" ref={contentRef}>
                            <div className="group">
                                <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm min-h-96 overflow-hidden">
                                    <div className={`bg-gradient-to-r ${tabContent[activeTab]?.gradient} p-6 text-white`}>
                                        <div className="flex items-center space-x-4">
                                            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                                                <span className="text-3xl">{tabContent[activeTab]?.icon}</span>
                                            </div>
                                            <div>
                                                <CardTitle className="text-3xl font-bold">{tabContent[activeTab]?.title}</CardTitle>
                                                <p className="text-white/90 text-lg">{tabContent[activeTab]?.subtitle}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <CardContent className="p-8">
                                        <div className="text-gray-700 leading-relaxed text-lg">
                                            {tabContent[activeTab]?.content}
                                        </div>
                                        {activeTab === 'quality' && (
                                            <div className="mt-6">
                                                <FoodSafetyCertificate />
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};



// Checkout Component
const CheckoutModal = ({ isOpen, onClose, cartItems, couponCode, total, deliveryDistance, onOrderComplete }) => {
    const [customerInfo, setCustomerInfo] = useState({
        name: '',
        email: '',
        phone: '',
        address: ''
    });
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [deliveryType, setDeliveryType] = useState('delivery');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const orderData = {
                customer_info: customerInfo,
                items: cartItems.map(item => ({
                    menu_item_id: item.id,
                    quantity: item.quantity
                })),
                coupon_code: couponCode,
                delivery_distance: deliveryType === 'delivery' ? deliveryDistance : 0,
                delivery_type: deliveryType,
                payment_method: paymentMethod
            };

            const response = await axios.post(`${API_BASE_URL}/api/orders`, orderData);
            const displayOrderNumber = response.data.order_number ?? response.data.order_id;
            toast.success(`Order placed successfully! Your order number is ${displayOrderNumber}.`);
            onOrderComplete();
        } catch (error) {
            toast.error('Failed to place order. Please try again.');
            console.error('Order error:', error);
        }
        setLoading(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-800">Checkout</h2>
                        <Button onClick={onClose} variant="outline" size="sm">×</Button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <Label htmlFor="name">Full Name</Label>
                            <Input
                                id="name"
                                value={customerInfo.name}
                                onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                                required
                            />
                        </div>

                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={customerInfo.email}
                                onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                                required
                            />
                        </div>

                        <div>
                            <Label htmlFor="phone">Phone Number</Label>
                            <Input
                                id="phone"
                                value={customerInfo.phone}
                                onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                                required
                            />
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-semibold mb-3">Delivery Type</h3>
                            <div className="space-y-3">
                                <div className="flex items-center space-x-3">
                                    <input
                                        type="radio"
                                        id="delivery"
                                        name="deliveryType"
                                        value="delivery"
                                        checked={deliveryType === 'delivery'}
                                        onChange={(e) => setDeliveryType(e.target.value)}
                                        className="text-red-600 focus:ring-red-500"
                                    />
                                    <Label htmlFor="delivery" className="flex items-center cursor-pointer">
                                        <Truck className="h-4 w-4 mr-2 text-blue-600" />
                                        Home Delivery
                                    </Label>
                                </div>

                                <div className="flex items-center space-x-3">
                                    <input
                                        type="radio"
                                        id="pickup"
                                        name="deliveryType"
                                        value="pickup"
                                        checked={deliveryType === 'pickup'}
                                        onChange={(e) => setDeliveryType(e.target.value)}
                                        className="text-red-600 focus:ring-red-500"
                                    />
                                    <Label htmlFor="pickup" className="flex items-center cursor-pointer">
                                        <Package className="h-4 w-4 mr-2 text-green-600" />
                                        Self Pickup (Free)
                                    </Label>
                                </div>
                            </div>
                        </div>

                        {deliveryType === 'delivery' && (
                            <div>
                                <Label htmlFor="address">Delivery Address</Label>
                                <Textarea
                                    id="address"
                                    value={customerInfo.address}
                                    onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
                                    required
                                />
                            </div>
                        )}

                        {deliveryType === 'pickup' && (
                            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                <div className="flex items-center mb-2">
                                    <Package className="h-5 w-5 mr-2 text-green-600" />
                                    <h4 className="font-semibold text-green-800">Pickup Location</h4>
                                </div>
                                <p className="text-green-700 text-sm">
                                    <strong>WOW! PIZZA - Al Amerat Branch</strong><br />
                                    Al Amarat - Phase 5, Muscat, Oman<br />
                                    <strong>Phone:</strong> +968 99236352 / 24870085<br />
                                    <br />
                                    <strong>WOW! PIZZA - Qurum Branch</strong><br />
                                    Qurum Commercial Centre, Opposite Lulu Hypermarket, Qurum, Muscat, Oman<br />
                                    <strong>Phone:</strong> +968 92111965<br />
                                    <br />
                                    <strong>WOW! PIZZA - Al Khuwair Branch</strong><br />
                                    Near Badr Al Samaa Hospital, Behind Indi Spice Restaurant, Al Khuwair, Muscat, Oman<br />
                                    <strong>Phone:</strong> +968 92111946<br />
                                    <br />
                                    <strong>Pickup Time:</strong> 15-20 minutes after order
                                </p>
                            </div>
                        )}

                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-semibold mb-3">Payment Method</h3>
                            <div className="space-y-3">
                                <div className="flex items-center space-x-3">
                                    <input
                                        type="radio"
                                        id="cash"
                                        name="paymentMethod"
                                        value="cash"
                                        checked={paymentMethod === 'cash'}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                        className="text-red-600 focus:ring-red-500"
                                    />
                                    <Label htmlFor="cash" className="flex items-center cursor-pointer">
                                        <DollarSign className="h-4 w-4 mr-2 text-green-600" />
                                        Cash
                                    </Label>
                                </div>

                                <div className="flex items-center space-x-3">
                                    <input
                                        type="radio"
                                        id="debit"
                                        name="paymentMethod"
                                        value="debit"
                                        checked={paymentMethod === 'debit'}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                        className="text-red-600 focus:ring-red-500"
                                    />
                                    <Label htmlFor="debit" className="flex items-center cursor-pointer">
                                        <CreditCard className="h-4 w-4 mr-2 text-blue-600" />
                                        Debit Card
                                    </Label>
                                </div>

                                <div className="flex items-center space-x-3">
                                    <input
                                        type="radio"
                                        id="credit"
                                        name="paymentMethod"
                                        value="credit"
                                        checked={paymentMethod === 'credit'}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                        className="text-red-600 focus:ring-red-500"
                                    />
                                    <Label htmlFor="credit" className="flex items-center cursor-pointer">
                                        <CreditCard className="h-4 w-4 mr-2 text-purple-600" />
                                        Credit Card
                                    </Label>
                                </div>
                            </div>
                            <p className="text-sm text-gray-600 mt-2">
                                💡 <strong>Note:</strong> All transactions are processed at {deliveryType === 'pickup' ? 'pickup' : 'delivery'}, no online payment.
                                {paymentMethod !== 'cash' && deliveryType === 'delivery' && ' Our delivery team will bring the card machine.'}
                                {paymentMethod !== 'cash' && deliveryType === 'pickup' && ' Please bring your card for payment at pickup.'}
                            </p>
                        </div>

                        <div className="bg-red-50 p-4 rounded-lg">
                            <div className="flex justify-between text-xl font-bold">
                                <span>Total {deliveryType === 'pickup' ? '(pickup - no delivery fee)' : '(including delivery)'}:</span>
                                <span>OMR {total.toFixed(3)}</span>
                            </div>
                            {deliveryType === 'delivery' && (
                                <p className="text-sm text-gray-600 mt-1">Distance: {deliveryDistance}km from Amerat</p>
                            )}
                            {deliveryType === 'pickup' && (
                                <p className="text-sm text-green-600 mt-1">✓ Free pickup - no delivery charges</p>
                            )}
                        </div>

                        <Button
                            type="submit"
                            className="w-full bg-red-600 hover:bg-red-700 text-white"
                            disabled={loading}
                        >
                            {loading ? 'Placing Order...' : 'Place Order'}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
};

// Privacy Policy Component
const Privacy = () => {
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-12 px-4">
            <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-xl p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-red-600 mb-2">🍕 Wow Pizza – Privacy Policy</h1>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                    <p><strong>Effective date:</strong> <span className="text-gray-700">05 Oct 2024</span></p>
                    <p><strong>Website:</strong> <a href="https://wowpizza.world" className="text-red-600 hover:underline">https://wowpizza.world</a></p>
                    <p><strong>Contact:</strong> <a href="mailto:wowpizza.muscat@gmail.com" className="text-red-600 hover:underline">wowpizza.muscat@gmail.com</a></p>
                </div>

                <div className="space-y-6 text-gray-700">
                    <section>
                        <h2 className="text-2xl font-bold text-red-600 mb-3">1) Who we are</h2>
                        <p>Wow Pizza ("we", "us", "our") operates the website and ordering experience at wowpizza.world.</p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-red-600 mb-3">2) What we collect</h2>
                        <ul className="list-disc list-inside space-y-2 ml-4">
                            <li><strong>Order details:</strong> items, prices, delivery method.</li>
                            <li><strong>Customer info:</strong> name, phone number, email, address / map location.</li>
                            <li><strong>Device info:</strong> device fingerprint or ID used to prevent coupon fraud and limit spins/games to once per day.</li>
                            <li><strong>Account/admin info:</strong> staff phone numbers/emails to receive order notifications.</li>
                            <li><strong>Technical data:</strong> IP address, browser, cookies, and similar identifiers for security, session management, and analytics.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-red-600 mb-3">3) How we use your data</h2>
                        <ul className="list-disc list-inside space-y-2 ml-4">
                            <li>Provide and deliver orders, process payments, and send confirmations/receipts.</li>
                            <li>Show your chosen map location to the restaurant for delivery.</li>
                            <li>Send admin-only order alerts (e.g., via email and WhatsApp) so staff can prepare orders.</li>
                            <li>Prevent abuse (e.g., one spin/game per day, single-use coupons) and detect fraud.</li>
                            <li>Improve our menu, promos, and site experience, and comply with legal obligations.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-red-600 mb-3">4) Legal bases</h2>
                        <p>We process data to perform a contract (fulfilling your order), based on legitimate interests (fraud prevention and service operations), and to meet legal requirements (tax/records).</p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-red-600 mb-3">5) Sharing</h2>
                        <p className="mb-2">We share data only with service providers necessary to run our service, such as:</p>
                        <ul className="list-disc list-inside space-y-2 ml-4">
                            <li>Hosting & infrastructure (website/DB, image storage/CDN).</li>
                            <li>Email/SMS/WhatsApp providers (e.g., Meta's WhatsApp Business Platform) for admin notifications about orders.</li>
                            <li>Payment processors (when you pay by card).</li>
                        </ul>
                        <p className="mt-2">These partners process data on our behalf under contracts and appropriate safeguards. We don't sell personal data.</p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-red-600 mb-3">6) WhatsApp usage</h2>
                        <p>We may send order alerts to our admins using the WhatsApp Business Platform. These alerts can include customer name, phone, address/map link, order items, and totals to prepare and deliver the order. Customers are not marketed on WhatsApp through this flow.</p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-red-600 mb-3">7) Retention</h2>
                        <ul className="list-disc list-inside space-y-2 ml-4">
                            <li><strong>Order records:</strong> retained as required for accounting/compliance.</li>
                            <li><strong>Coupon/game/device data:</strong> retained only as long as needed to prevent abuse and for audit, then deleted or anonymized.</li>
                            <li><strong>Logs and backups:</strong> short, rolling periods unless needed for security/legal reasons.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-red-600 mb-3">8) Your rights</h2>
                        <p>Subject to local laws, you can request access, correction, deletion, or restriction of your personal data. Contact <a href="mailto:wowpizza.muscat@gmail.com" className="text-red-600 hover:underline">wowpizza.muscat@gmail.com</a>.</p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-red-600 mb-3">9) Cookies & similar tech</h2>
                        <p>We use cookies/local storage for sessions, cart, device limits (spins/coupons), and analytics. You can control cookies in your browser; some features may not work without them.</p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-red-600 mb-3">10) Security</h2>
                        <p>We use technical and organizational measures to protect data. No method is 100% secure, but we strive to safeguard your information.</p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-red-600 mb-3">11) International transfers</h2>
                        <p>If data is processed outside your country, we use appropriate safeguards (e.g., contractual clauses).</p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-red-600 mb-3">12) Changes</h2>
                        <p>We may update this policy and will post the new version with a revised effective date.</p>
                    </section>

                    <div className="bg-green-50 p-6 rounded-lg mt-8">
                        <h2 className="text-2xl font-bold text-red-600 mb-3">Contact</h2>
                        <p>For questions or requests, email <a href="mailto:wowpizza.muscat@gmail.com" className="text-red-600 hover:underline font-semibold">wowpizza.muscat@gmail.com</a>.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Main App Component
function App() {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const [cartItems, setCartItems] = useState(() => {
        try {
            const saved = localStorage.getItem('wow_pizza_cart');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });
    const [showCart, setShowCart] = useState(false);
    const [recentlyAddedItems, setRecentlyAddedItems] = useState(new Set());
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [showOfferPopup, setShowOfferPopup] = useState(false);
    const [isClosingOffer, setIsClosingOffer] = useState(false);
    const [showHappyHourPopup, setShowHappyHourPopup] = useState(false);
    const [isClosingHappyHour, setIsClosingHappyHour] = useState(false);
    const [showWomensDayPopup, setShowWomensDayPopup] = useState(false);
    const [isClosingWomensDay, setIsClosingWomensDay] = useState(false);

    console.log('App component rendered with cart items:', cartItems.length);

    // Show BOGO popup on Tuesday & Friday, once per session.
    // The Omani Women's Day popup takes priority while that offer runs.
    useEffect(() => {
        if (isTuesday() && !isWomensDayOffer() && !sessionStorage.getItem('bogoPopupShown')) {
            const timer = setTimeout(() => {
                setShowOfferPopup(true);
                sessionStorage.setItem('bogoPopupShown', 'true');
            }, 800);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleCloseOfferPopup = () => {
        setIsClosingOffer(true);
        setTimeout(() => {
            setShowOfferPopup(false);
            setIsClosingOffer(false);
        }, 500);
    };

    // Show Omani Women's Day popup on 17-19 Oct (Oman time), once per session.
    // It takes priority over the Happy Hour popup on those days.
    useEffect(() => {
        if (isWomensDayOffer() && !sessionStorage.getItem('womensDayPopupShown')) {
            const timer = setTimeout(() => {
                setShowWomensDayPopup(true);
                sessionStorage.setItem('womensDayPopupShown', 'true');
            }, 800);
            return () => clearTimeout(timer);
        }
    }, []);

    // Tapping the popup poster closes it and takes the customer to the pizza menu
    const handleWomensDayPosterClick = () => {
        handleCloseWomensDayPopup();
        if (document.getElementById('filter-section')) {
            setTimeout(() => window.dispatchEvent(new Event('womensday:order')), 500);
        } else {
            // Menu isn't on this page: open the home page and let the menu finish the jump
            try { sessionStorage.setItem('womensDayGoToPizza', '1'); } catch (e) {}
            window.location.assign('/');
        }
    };

    const handleCloseWomensDayPopup = () => {
        setIsClosingWomensDay(true);
        setTimeout(() => {
            setShowWomensDayPopup(false);
            setIsClosingWomensDay(false);
        }, 500);
    };

    // Show Happy Hour popup during 11 AM - 4 PM (Oman time), once per session.
    // On Tuesday & Friday the BOGO popup takes priority, so we skip it there.
    useEffect(() => {
        if (!isTuesday() && !isWomensDayOffer() && isHappyHour() && !sessionStorage.getItem('happyHourPopupShown')) {
            const timer = setTimeout(() => {
                setShowHappyHourPopup(true);
                sessionStorage.setItem('happyHourPopupShown', 'true');
            }, 800);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleCloseHappyHourPopup = () => {
        setIsClosingHappyHour(true);
        setTimeout(() => {
            setShowHappyHourPopup(false);
            setIsClosingHappyHour(false);
        }, 500);
    };

    useEffect(() => {
        try {
            if (cartItems.length > 0) {
                localStorage.setItem('wow_pizza_cart', JSON.stringify(cartItems));
            } else {
                localStorage.removeItem('wow_pizza_cart');
            }
        } catch (e) {}
    }, [cartItems]);

    const addToCart = (menuItem, selectedSize = 'medium', selections = null) => {
        // Check if ordering is allowed at this time
        if (!isOrderingAllowed()) {
            const currentOmanTime = getCurrentOmanTime();

            toast.error(
                `Outside Ordering Hours!`,
                {
                    description: (
                        <div style={{ whiteSpace: 'pre-line' }}>
                            {`Current time: ${currentOmanTime} (Oman).\nOrdering is available ${getOrderingHoursMessage()}.\nPlease come back during our ordering hours!`}
                        </div>
                    ),
                    duration: 5000,
                }
            );
            return;
        }

        if (!menuItem.available) {
            toast.error('This item is currently sold out');
            return;
        }

        // Happy Hour deals can only be ordered between 11 AM and 4 PM (Oman time)
        const isHappyHourDeal = (menuItem.name || '').toLowerCase().includes('happy hour')
            || (menuItem.name_ar || '').includes('ساعة السعادة');
        if (isHappyHourDeal && !isHappyHour()) {
            toast.error(t('happyHour.title'), {
                description: t('happyHour.outsideWindow'),
                duration: 5000,
            });
            return;
        }

        // Find the selected size
        const sizeItem = menuItem.sizes?.find(size => size.size === selectedSize && size.available);
        if (!sizeItem) {
            toast.error('Selected size is not available');
            return;
        }

        // Create cart item with size information
        const cartItem = {
            ...menuItem,
            selectedSize: selectedSize,
            price: sizeItem.price,
            quantity: 1,
            selections: selections,
            cartId: Date.now().toString() + Math.random().toString(36).substr(2, 9)
        };

        setCartItems(prev => {
            // Check for existing item with same ID, size AND selections
            const existing = prev.find(item =>
                item.id === menuItem.id &&
                item.selectedSize === selectedSize &&
                JSON.stringify(item.selections) === JSON.stringify(selections)
            );

            if (existing) {
                return prev.map(item =>
                    item.id === menuItem.id &&
                        item.selectedSize === selectedSize &&
                        JSON.stringify(item.selections) === JSON.stringify(selections)
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            }
            return [...prev, cartItem];
        });

        // Add visual feedback
        setRecentlyAddedItems(prev => new Set([...prev, menuItem.id]));
        setTimeout(() => {
            setRecentlyAddedItems(prev => {
                const newSet = new Set(prev);
                newSet.delete(menuItem.id);
                return newSet;
            });
        }, 2000);

        const displayName = lang === 'ar' && menuItem.name_ar ? menuItem.name_ar : menuItem.name;
        toast.success(lang === 'ar' ? `${displayName} (${selectedSize}) أضيف إلى السلة!` : `${displayName} (${selectedSize}) added to cart!`);
    };

    const updateQuantity = (cartId, newQuantity) => {
        if (newQuantity <= 0) {
            removeFromCart(cartId);
            return;
        }

        // Check if we're increasing quantity (adding more items)
        const currentItem = cartItems.find(item => item.cartId === cartId);
        if (currentItem && newQuantity > currentItem.quantity) {
            // Check if ordering is allowed at this time
            if (!isOrderingAllowed()) {
                const currentOmanTime = getCurrentOmanTime();

                toast.error(
                    `Outside Ordering Hours!`,
                    {
                        description: (
                            <div style={{ whiteSpace: 'pre-line' }}>
                                {`Current time: ${currentOmanTime} (Oman).\nOrdering is available ${getOrderingHoursMessage()}.\nPlease come back during our ordering hours!`}
                            </div>
                        ),
                        duration: 5000,
                    }
                );
                return;
            }
        }

        setCartItems(prev =>
            prev.map(item =>
                item.cartId === cartId
                    ? { ...item, quantity: newQuantity }
                    : item
            )
        );
    };

    const removeFromCart = (cartId) => {
        setCartItems(prev => prev.filter(item => item.cartId !== cartId));
    };

    const handlePlaceOrder = async (orderData) => {
        try {
            // Check if ordering is allowed at this time before placing order
            if (!isOrderingAllowed()) {
                const currentOmanTime = getCurrentOmanTime();

                toast.error(
                    `Outside Ordering Hours!`,
                    {
                        description: (
                            <div style={{ whiteSpace: 'pre-line' }}>
                                {`Current time: ${currentOmanTime} (Oman).\nOrders can only be placed ${getOrderingHoursMessage()}.\nPlease come back during our ordering hours!`}
                            </div>
                        ),
                        duration: 6000,
                    }
                );
                throw new Error('Ordering is not available at this time');
            }

            console.log('Placing order:', orderData);

            // Prepare the order data for the backend
            const orderPayload = {
                customer_info: {
                    name: orderData.customerInfo.name,
                    email: orderData.customerInfo.email,
                    phone: orderData.customerInfo.phone,
                    address: orderData.deliveryType === 'delivery'
                        ? `Way ${orderData.addressDetails.wayNumber}, Building ${orderData.addressDetails.buildingNumber}, Flat ${orderData.addressDetails.flatNumber}${orderData.addressDetails.floor ? `, Floor ${orderData.addressDetails.floor}` : ''}${orderData.addressDetails.additionalInfo ? `, ${orderData.addressDetails.additionalInfo}` : ''}`
                        : `Pickup: ${orderData.pickupBranch ? `${orderData.pickupBranch.name}, ${orderData.pickupBranch.address}` : WOW_PIZZA_ADDRESS}`
                },
                items: orderData.items.map(item => ({
                    menu_item_id: item.id,
                    size: item.selectedSize || 'medium',
                    quantity: item.quantity,
                    selections: item.selections || []
                })),
                delivery_distance: orderData.distance || 0,
                nearest_branch: orderData.nearestBranch?.name || null,
                delivery_lat: orderData.mapLocation?.lat ?? null,
                delivery_lng: orderData.mapLocation?.lng ?? null,
                map_address: orderData.mapLocation?.address || null,
                coupon_code: orderData.appliedCoupon || null,
                payment_method: orderData.paymentMethod,
                delivery_type: orderData.deliveryType,
                subtotal: orderData.subtotal,
                discount_amount: (orderData.couponDiscount || 0) + (orderData.bogoDiscount || 0),
                delivery_charge: orderData.deliveryCharge,
                final_total: orderData.total,
                free_heart_pizzas: orderData.freeHeartPizzas ?? 0,
                free_heart_flavours: orderData.freeHeartFlavours || []
            };

            // Send order to backend
            const response = await fetch(`${API_BASE_URL}/api/orders`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(orderPayload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to place order');
            }

            const result = await response.json();

            // Show success message with simple sequential order number
            const displayOrderNumber = result.order_number ?? result.order_id;
            toast.success(`Order placed successfully! Your order number is ${displayOrderNumber}.`);

            // Clear cart and return to menu
            setCartItems([]);
            setShowCart(false);
        } catch (error) {
            console.error('Error placing order:', error);
            toast.error(error.message || 'Failed to place order. Please try again.');
            throw error;
        }
    };

    return (
        <Router>
            <ScrollToTop />
            <div className="min-h-screen bg-black overflow-y-auto">
                <style>
                    {`
            @keyframes fall {
              to {
                transform: translateY(100vh) rotate(360deg);
                opacity: 0;
              }
            }
            @keyframes fadeInScale {
              from { opacity: 0; transform: scale(0.95); }
              to { opacity: 1; transform: scale(1); }
            }
            @keyframes shrinkToCorner {
              to { 
                opacity: 0;
                transform: translate(40vw, 40vh) scale(0.4);
              }
            }
            @keyframes floatTeaser {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-6px); }
            }
            
            /* Custom scrollbar styling */
            ::-webkit-scrollbar {
              width: 12px;
            }
            
            ::-webkit-scrollbar-track {
              background: #1a1a1a;
            }
            
            ::-webkit-scrollbar-thumb {
              background: #dc2626;
              border-radius: 6px;
            }
            
            ::-webkit-scrollbar-thumb:hover {
              background: #b91c1c;
            }
            
            /* Firefox scrollbar */
            html {
              scrollbar-width: thin;
              scrollbar-color: #dc2626 #1a1a1a;
            }
          `}
                </style>
                <Header cartItems={cartItems} setShowCart={setShowCart} setSelectedCategory={setSelectedCategory} />
                {/* Pass selectedCategory and handler to Menu */}
                <main className="pt-24">
                    <Routes>
                        <Route path="/" element={
                            <>
                                <Menu
                                    onAddToCart={addToCart}
                                    updateQuantity={updateQuantity}
                                    recentlyAddedItems={recentlyAddedItems}
                                    cartItems={cartItems}
                                    selectedCategory={selectedCategory}
                                    onSelectCategory={setSelectedCategory}
                                />
                            </>
                        } />
                        <Route path="/amerat" element={<AmeratPage />} />
                        <Route path="/qurum" element={<QurumPage />} />
                        <Route path="/al-khuwair" element={<KhuwairPage />} />
                        <Route path="/game" element={<Game />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/admin" element={<Admin />} />
                        <Route path="/privacy" element={<Privacy />} />
                    </Routes>
                </main>

                <footer className="bg-black text-white py-12 mt-12 border-t-4 border-red-600">
                    <div className="container mx-auto px-4">
                        <div className="flex flex-col items-center justify-center mb-8">
                            <div className="flex items-center mb-4">
                                <Pizza className="h-10 w-10 mr-4 text-red-500 animate-pulse" />
                                <h3 className="text-3xl font-black tracking-tighter">{lang === 'ar' ? 'واو! بيتزا' : 'WOW! PIZZA'}</h3>
                            </div>
                            <p className="text-gray-400 text-center max-w-md">{t('footer.description')}</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 border-b border-gray-800 pb-12">
                            <div className="flex flex-col items-center text-center space-y-4">
                                <div className="bg-gray-900/50 p-4 rounded-full border border-red-500/20">
                                    <MapPin className="h-6 w-6 text-red-500" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-white mb-1 uppercase tracking-widest text-xs">{t('footer.findUs')}</h4>
                                    <p className="text-gray-400 text-sm">{t('footer.ameratBranch')}</p>
                                    <p className="text-gray-400 text-sm mt-1">{t('footer.qurumBranch')}</p>
                                    <p className="text-gray-400 text-sm mt-1">{t('footer.khuwairBranch')}</p>
                                </div>
                            </div>

                            <div className="flex flex-col items-center text-center space-y-4">
                                <div className="bg-gray-900/50 p-4 rounded-full border border-red-500/20">
                                    <Phone className="h-6 w-6 text-red-500" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-white mb-1 uppercase tracking-widest text-xs">{t('footer.callUs')}</h4>
                                    <p className="text-gray-400 text-sm font-medium"><span dir="ltr">{t('footer.ameratPhone')}</span></p>
                                    <p className="text-gray-400 text-sm font-medium"><span dir="ltr">{t('footer.qurumPhone')}</span></p>
                                    <p className="text-gray-400 text-sm font-medium"><span dir="ltr">{t('footer.khuwairPhone')}</span></p>
                                </div>
                            </div>

                            <div className="flex flex-col items-center text-center space-y-4">
                                <div className="bg-gray-900/50 p-4 rounded-full border border-red-500/20">
                                    <Mail className="h-6 w-6 text-red-500" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-white mb-1 uppercase tracking-widest text-xs">{t('footer.emailUs')}</h4>
                                    <p className="text-gray-400 text-sm">wowpizza.muscat@gmail.com</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col items-center space-y-6">
                            <div className="flex items-center space-x-8">
                                <a
                                    href="https://www.instagram.com/wowpizza.oman/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group flex items-center space-x-2 grayscale hover:grayscale-0 transition-all duration-300"
                                >
                            <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6 text-white">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                            </svg>
                                    <span className="text-gray-400 group-hover:text-pink-400 text-sm font-bold">@wowpizza.oman</span>
                                </a>
                                <a
                                    href="https://www.facebook.com/people/Wow-pizza/61573539833825/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group flex items-center space-x-2 grayscale hover:grayscale-0 transition-all duration-300"
                                >
                            <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6 text-white">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                            </svg>
                                    <span className="text-gray-400 group-hover:text-blue-400 text-sm font-bold">Wow Pizza</span>
                                </a>
                            </div>
                        </div>
                        <div className="mt-6 pt-6 border-t border-gray-700 space-y-2">
                            <p className="text-gray-400">{t('footer.rights')}</p>
                            <div>
                                <Link to="/privacy" className="text-gray-400 hover:text-red-500 transition-colors text-sm">
                                    {t('footer.privacyPolicy')}
                                </Link>
                                <span className="mx-2 text-gray-700">|</span>
                                <a
                                    href="https://wowpizza.world/admin"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-gray-500 hover:text-red-500 transition-colors text-xs"
                                >
                                    {t('footer.adminLogin')}
                                </a>
                            </div>
                        </div>
                    </div>
                </footer>

                <CartModalWithCheckout
                    isOpen={showCart}
                    onClose={() => setShowCart(false)}
                    cartItems={cartItems}
                    onUpdateQuantity={updateQuantity}
                    onRemoveItem={removeFromCart}
                    onPlaceOrder={handlePlaceOrder}
                />

                {/* Khanjar Badge - Made in Oman */}
                <KhanjarBadge />

                {/* Sonner Toaster for toast notifications */}
                <Toaster position="top-center" richColors />

                {/* Happy Hour Lunch Treat Popup - daily 11 AM to 4 PM */}
                <Dialog open={showHappyHourPopup} onOpenChange={(open) => {
                    if (!open && !isClosingHappyHour) handleCloseHappyHourPopup();
                }}>
                    <DialogContent className="max-w-[95vw] w-full p-0 bg-transparent border-none sm:max-w-[80vw] md:max-w-[70vw] lg:max-w-[60vw] xl:max-w-[50vw] [&>button]:hidden">
                        <DialogTitle className="sr-only">{t('happyHour.title')}</DialogTitle>
                        <div className={`relative bg-white rounded-xl overflow-hidden shadow-2xl flex justify-center items-center p-2 sm:p-3 md:p-4 ${isClosingHappyHour ? 'animate-[shrinkToCorner_0.5s_forwards]' : 'animate-[fadeInScale_0.3s_ease-out]'}`}>
                            <img
                                src={lang === 'ar' ? '/assets/logo/happy_hour_arabic.jpeg' : '/assets/logo/happy_hour_eng.jpeg'}
                                alt={t('happyHour.posterAlt')}
                                className="w-auto max-w-full h-auto max-h-[85vh] sm:max-h-[80vh] md:max-h-[75vh] lg:max-h-[70vh] object-contain"
                            />
                            <Button
                                onClick={handleCloseHappyHourPopup}
                                className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-black/70 hover:bg-black/90 text-white rounded-full h-8 w-8 sm:h-10 sm:w-10 p-0 z-10"
                                variant="ghost"
                                size="icon"
                            >
                                <X className="h-4 w-4 sm:h-6 sm:w-6" />
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Omani Women's Day Popup - 17 to 19 October */}
                <Dialog open={showWomensDayPopup} onOpenChange={(open) => {
                    if (!open && !isClosingWomensDay) handleCloseWomensDayPopup();
                }}>
                    <DialogContent className="max-w-[95vw] w-full p-0 bg-transparent border-none sm:max-w-[80vw] md:max-w-[70vw] lg:max-w-[60vw] xl:max-w-[50vw] [&>button]:hidden">
                        <DialogTitle className="sr-only">{t('womensDay.title')}</DialogTitle>
                        <div className={`relative bg-white rounded-xl overflow-hidden shadow-2xl flex justify-center items-center p-2 sm:p-3 md:p-4 ${isClosingWomensDay ? 'animate-[shrinkToCorner_0.5s_forwards]' : 'animate-[fadeInScale_0.3s_ease-out]'}`}>
                            <button
                                type="button"
                                onClick={handleWomensDayPosterClick}
                                aria-label={t('womensDay.cta')}
                                className="relative block cursor-pointer focus:outline-none"
                            >
                                <img
                                    src="/assets/logo/womens_day_offer.jpeg"
                                    alt={t('womensDay.posterAlt')}
                                    className="w-auto max-w-full h-auto max-h-[75vh] md:max-h-[70vh] lg:max-h-[65vh] object-contain"
                                />
                                    <span className="pointer-events-none mt-2 sm:mt-3 mx-auto flex w-fit items-center gap-1.5 whitespace-nowrap rounded-full bg-red-600 px-5 py-2.5 text-sm sm:text-base font-black text-white shadow-[0_6px_20px_rgba(220,38,38,0.5)] animate-pulse">
                                        {t('womensDay.tapToOrder')}
                                    </span>
                            </button>
                            <Button
                                onClick={handleCloseWomensDayPopup}
                                className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-black/70 hover:bg-black/90 text-white rounded-full h-8 w-8 sm:h-10 sm:w-10 p-0 z-10"
                                variant="ghost"
                                size="icon"
                            >
                                <X className="h-4 w-4 sm:h-6 sm:w-6" />
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Tuesday & Friday BOGO Offer Popup */}
                <Dialog open={showOfferPopup} onOpenChange={(open) => {
                    if (!open && !isClosingOffer) handleCloseOfferPopup();
                }}>
                    <DialogContent className="max-w-[95vw] w-full p-0 bg-transparent border-none sm:max-w-[80vw] md:max-w-[70vw] lg:max-w-[60vw] xl:max-w-[50vw] [&>button]:hidden">
                        <div className={`relative bg-white rounded-xl overflow-hidden shadow-2xl flex justify-center items-center p-2 sm:p-3 md:p-4 ${isClosingOffer ? 'animate-[shrinkToCorner_0.5s_forwards]' : 'animate-[fadeInScale_0.3s_ease-out]'}`}>
                            <img
                                src="/assets/logo/BOGO.jpeg"
                                alt="Buy 1 Get 1 Free - Tuesday & Friday Offer"
                                className="w-auto max-w-full h-auto max-h-[85vh] sm:max-h-[80vh] md:max-h-[75vh] lg:max-h-[70vh] object-contain"
                            />
                            <Button
                                onClick={handleCloseOfferPopup}
                                className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-black/70 hover:bg-black/90 text-white rounded-full h-8 w-8 sm:h-10 sm:w-10 p-0 z-10"
                                variant="ghost"
                                size="icon"
                            >
                                <X className="h-4 w-4 sm:h-6 sm:w-6" />
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>


            </div>
        </Router>
    );
}

export default App;